/**
 * MicManager — centralized microphone management for Timbrica tools.
 *
 * Covers ~30 tools that use getUserMedia with similar patterns.
 * NOT for WebRTC tools (walkie-talkie, video-call) — they need custom track negotiation.
 *
 * Usage:
 *   var mic = new MicManager({ locale: window.__LOCALE });
 *
 *   // Simple: get stream. On denied/not_found/busy a recovery modal is shown
 *   // automatically with browser-specific instructions + "try again" button.
 *   var stream = await mic.start();
 *
 *   // Disable auto-recovery UI (tool wants full control of error flow):
 *   var mic = new MicManager({ locale: window.__LOCALE, autoHelp: false });
 *
 *   // Custom retry callback (default is no-op; tool controls how to retry):
 *   var mic = new MicManager({ locale, onRetry: function(){ startBtn.click(); } });
 *
 *   // With device selection:
 *   var devices = await mic.enumerate();
 *   var stream = await mic.start({ deviceId: 'abc123' });
 *
 *   // With analyser for level metering:
 *   mic.onLevel(function(rms) { updateMeter(rms); });
 *
 *   // Stop + cleanup:
 *   mic.stop();
 *
 * Features:
 *   - Proper error handling with localized messages (all error types)
 *   - Browser-specific recovery modal (Chrome/Firefox/Safari) — shown once per
 *     session per error code (dedup), so retry spam does not flood telemetry
 *   - Automatic stream cleanup on page navigation
 *   - Device enumeration with change detection
 *   - Audio level monitoring (RMS)
 *   - Telemetry for mic errors — deduplicated per session
 */
(function() {
'use strict';

var ERRORS = {
    en: {
        denied: 'Microphone access denied. Please allow access in browser settings and try again.',
        dismissed: 'Click the mic button again and choose “Allow” in the browser prompt.',
        not_found: 'No microphone found. Please connect a microphone and reload the page.',
        busy: 'Microphone is busy. Close other tabs or apps using the mic and try again.',
        not_supported: 'Your browser does not support microphone access.',
        blocked_env: 'This browser blocks microphone access. Open the page in Chrome, Safari, or Firefox to record.',
        os_blocked: 'Your system is blocking microphone access. Enable it in your operating system privacy settings, then try again.',
        unresponsive: 'The microphone is not responding. Another app may be using it — close apps like Zoom, Discord or Teams, then reload the page.',
        session_mode: 'The iPhone did not turn the microphone on: the page was still in sound playback mode. Press “Try again”. If it is refused again, close this tab and open the page in a new one.',
        generic: 'Microphone error. Try closing other tabs or restarting the browser.'
    },
    ru: {
        denied: 'Доступ к микрофону запрещён. Разрешите доступ в настройках браузера и попробуйте снова.',
        dismissed: 'Нажмите на кнопку микрофона ещё раз и выберите «Разрешить» в браузерном окне.',
        not_found: 'Микрофон не найден. Подключите микрофон и обновите страницу.',
        busy: 'Микрофон занят. Закройте другие вкладки или приложения, использующие микрофон.',
        not_supported: 'Ваш браузер не поддерживает доступ к микрофону.',
        blocked_env: 'Этот браузер блокирует доступ к микрофону. Откройте страницу в Chrome, Safari или Firefox, чтобы записать.',
        os_blocked: 'Доступ к микрофону блокирует операционная система. Включите его в настройках конфиденциальности системы и попробуйте снова.',
        unresponsive: 'Микрофон не отвечает. Возможно, его занял другой звонок или приложение — закройте Zoom, Discord, Teams и обновите страницу.',
        session_mode: 'iPhone не включил микрофон: страница ещё была в режиме воспроизведения звука. Нажмите «Попробовать снова». Если не получится — закройте вкладку и откройте страницу в новой.',
        generic: 'Ошибка микрофона. Попробуйте закрыть другие вкладки или перезапустить браузер.'
    },
    ko: {
        denied: '마이크 접근이 거부되었습니다. 브라우저 설정에서 접근을 허용하고 다시 시도하세요.',
        dismissed: '마이크 버튼을 다시 누르고 브라우저 창에서 “허용”을 선택하세요.',
        not_found: '마이크를 찾을 수 없습니다. 마이크를 연결하고 페이지를 새로고침하세요.',
        busy: '마이크가 사용 중입니다. 마이크를 사용하는 다른 탭이나 앱을 닫고 다시 시도하세요.',
        not_supported: '브라우저가 마이크 접근을 지원하지 않습니다.',
        blocked_env: '이 브라우저는 마이크 접근을 차단합니다. 녹음하려면 Chrome, Safari 또는 Firefox에서 페이지를 여세요.',
        os_blocked: '운영체제가 마이크 접근을 차단하고 있습니다. 시스템 개인정보 설정에서 허용한 후 다시 시도하세요.',
        unresponsive: '마이크가 응답하지 않습니다. 다른 앱이 사용 중일 수 있습니다 — Zoom, Discord, Teams 같은 앱을 닫고 페이지를 새로고침하세요.',
        session_mode: 'iPhone이 마이크를 켜지 않았습니다. 페이지가 아직 소리 재생 모드였습니다. ‘다시 시도’를 누르세요. 다시 거부되면 이 탭을 닫고 새 탭에서 페이지를 여세요.',
        generic: '마이크 오류. 다른 탭을 닫거나 브라우저를 재시작해 보세요.'
    },
    ar: {
        denied: 'تم رفض الوصول إلى الميكروفون. يرجى السماح بالوصول في إعدادات المتصفح والمحاولة مرة أخرى.',
        dismissed: 'اضغط زر الميكروفون مرة أخرى واختر "السماح" في نافذة المتصفح.',
        not_found: 'لم يتم العثور على ميكروفون. يرجى توصيل ميكروفون وإعادة تحميل الصفحة.',
        busy: 'الميكروفون مشغول. أغلق التبويبات أو التطبيقات الأخرى التي تستخدم الميكروفون.',
        not_supported: 'متصفحك لا يدعم الوصول إلى الميكروفون.',
        blocked_env: 'هذا المتصفح يحظر الوصول إلى الميكروفون. افتح الصفحة في Chrome أو Safari أو Firefox للتسجيل.',
        os_blocked: 'نظام التشغيل يحظر الوصول إلى الميكروفون. فعّله من إعدادات الخصوصية في النظام ثم حاول مرة أخرى.',
        unresponsive: 'الميكروفون لا يستجيب. قد يستخدمه تطبيق آخر — أغلق تطبيقات مثل Zoom أو Discord أو Teams ثم أعد تحميل الصفحة.',
        session_mode: 'لم يشغّل iPhone الميكروفون: كانت الصفحة لا تزال في وضع تشغيل الصوت. اضغط «إعادة المحاولة». إذا رُفض مرة أخرى، أغلق هذا التبويب وافتح الصفحة في تبويب جديد.',
        generic: 'خطأ في الميكروفون. حاول إغلاق التبويبات الأخرى أو إعادة تشغيل المتصفح.'
    },
    id: {
        denied: 'Akses mikrofon ditolak. Izinkan akses di pengaturan browser dan coba lagi.',
        dismissed: 'Tekan tombol mikrofon lagi dan pilih “Izinkan” di prompt browser.',
        not_found: 'Mikrofon tidak ditemukan. Hubungkan mikrofon dan muat ulang halaman.',
        busy: 'Mikrofon sedang digunakan. Tutup tab atau aplikasi lain yang menggunakan mikrofon.',
        not_supported: 'Browser Anda tidak mendukung akses mikrofon.',
        blocked_env: 'Browser ini memblokir akses mikrofon. Buka halaman di Chrome, Safari, atau Firefox untuk merekam.',
        os_blocked: 'Sistem operasi memblokir akses mikrofon. Aktifkan di pengaturan privasi sistem, lalu coba lagi.',
        unresponsive: 'Mikrofon tidak merespons. Aplikasi lain mungkin menggunakannya — tutup aplikasi seperti Zoom, Discord, atau Teams, lalu muat ulang halaman.',
        session_mode: 'iPhone tidak menyalakan mikrofon: halaman masih dalam mode pemutaran suara. Tekan “Coba lagi”. Jika ditolak lagi, tutup tab ini dan buka halaman di tab baru.',
        generic: 'Kesalahan mikrofon. Coba tutup tab lain atau restart browser.'
    },
    pt: {
        denied: 'Acesso ao microfone negado. Permita o acesso nas configurações do navegador e tente novamente.',
        dismissed: 'Clique novamente no botão do microfone e escolha “Permitir” na janela do navegador.',
        not_found: 'Microfone não encontrado. Conecte um microfone e recarregue a página.',
        busy: 'Microfone está ocupado. Feche outras abas ou apps que usam o microfone e tente novamente.',
        not_supported: 'Seu navegador não suporta acesso ao microfone.',
        blocked_env: 'Este navegador bloqueia o acesso ao microfone. Abra a página no Chrome, Safari ou Firefox para gravar.',
        os_blocked: 'O sistema operacional está bloqueando o acesso ao microfone. Ative nas configurações de privacidade do sistema e tente novamente.',
        unresponsive: 'O microfone não está respondendo. Outro aplicativo pode estar usando-o — feche apps como Zoom, Discord ou Teams e recarregue a página.',
        session_mode: 'O iPhone não ligou o microfone: a página ainda estava no modo de reprodução de som. Toque em “Tentar novamente”. Se for recusado de novo, feche esta aba e abra a página em uma nova.',
        generic: 'Erro no microfone. Tente fechar outras abas ou reiniciar o navegador.'
    },
    de: {
        denied: 'Mikrofonzugriff verweigert. Bitte erlaube den Zugriff in den Browser-Einstellungen und versuche es erneut.',
        dismissed: 'Klicke erneut auf die Mikrofon-Taste und wähle „Zulassen“ im Browser-Dialog.',
        not_found: 'Kein Mikrofon gefunden. Bitte schließe ein Mikrofon an und lade die Seite neu.',
        busy: 'Mikrofon ist belegt. Schließe andere Tabs oder Apps, die das Mikrofon nutzen, und versuche es erneut.',
        not_supported: 'Dein Browser unterstützt keinen Mikrofonzugriff.',
        blocked_env: 'Dieser Browser blockiert den Mikrofonzugriff. Öffne die Seite in Chrome, Safari oder Firefox, um aufzunehmen.',
        os_blocked: 'Das Betriebssystem blockiert den Mikrofonzugriff. Aktiviere ihn in den Datenschutzeinstellungen des Systems und versuche es erneut.',
        unresponsive: 'Das Mikrofon reagiert nicht. Eine andere App nutzt es möglicherweise — schließe Apps wie Zoom, Discord oder Teams und lade die Seite neu.',
        session_mode: 'Das iPhone hat das Mikrofon nicht eingeschaltet: Die Seite war noch im Wiedergabemodus. Tippe auf „Erneut versuchen“. Klappt es wieder nicht, schließe diesen Tab und öffne die Seite in einem neuen.',
        generic: 'Mikrofon-Fehler. Versuche, andere Tabs zu schließen oder den Browser neu zu starten.'
    },
    es: {
        denied: 'Acceso al micrófono denegado. Permite el acceso en la configuración del navegador e inténtalo de nuevo.',
        dismissed: 'Pulsa de nuevo el botón del micrófono y elige «Permitir» en la ventana del navegador.',
        not_found: 'No se encontró ningún micrófono. Conecta un micrófono y recarga la página.',
        busy: 'El micrófono está ocupado. Cierra otras pestañas o aplicaciones que lo usen e inténtalo de nuevo.',
        not_supported: 'Tu navegador no admite el acceso al micrófono.',
        blocked_env: 'Este navegador bloquea el acceso al micrófono. Abre la página en Chrome, Safari o Firefox para grabar.',
        os_blocked: 'El sistema operativo está bloqueando el acceso al micrófono. Actívalo en la configuración de privacidad del sistema e inténtalo de nuevo.',
        unresponsive: 'El micrófono no responde. Es posible que otra aplicación lo esté usando — cierra apps como Zoom, Discord o Teams y recarga la página.',
        session_mode: 'El iPhone no encendió el micrófono: la página seguía en modo de reproducción de sonido. Toca «Reintentar». Si vuelve a rechazarlo, cierra esta pestaña y abre la página en una nueva.',
        generic: 'Error de micrófono. Prueba a cerrar otras pestañas o reiniciar el navegador.'
    },
    fr: {
        denied: 'Accès au microphone refusé. Autorisez l\'accès dans les paramètres du navigateur et réessayez.',
        dismissed: 'Cliquez à nouveau sur le bouton du microphone et choisissez « Autoriser » dans la fenêtre du navigateur.',
        not_found: 'Aucun microphone détecté. Branchez un microphone et rechargez la page.',
        busy: 'Le microphone est occupé. Fermez les autres onglets ou applications qui l\'utilisent et réessayez.',
        not_supported: 'Votre navigateur ne prend pas en charge l\'accès au microphone.',
        blocked_env: 'Ce navigateur bloque l\'accès au microphone. Ouvrez la page dans Chrome, Safari ou Firefox pour enregistrer.',
        os_blocked: 'Le système d\'exploitation bloque l\'accès au microphone. Activez-le dans les paramètres de confidentialité du système et réessayez.',
        unresponsive: 'Le microphone ne répond pas. Une autre application l\'utilise peut-être — fermez des apps comme Zoom, Discord ou Teams et rechargez la page.',
        session_mode: 'L’iPhone n’a pas activé le micro : la page était encore en mode lecture du son. Appuyez sur « Réessayer ». Si c’est encore refusé, fermez cet onglet et ouvrez la page dans un nouvel onglet.',
        generic: 'Erreur de microphone. Essayez de fermer d\'autres onglets ou de redémarrer le navigateur.'
    },
    uk: {
        denied: 'Доступ до мікрофона заборонено. Дозвольте доступ у налаштуваннях браузера і спробуйте знову.',
        dismissed: 'Натисніть кнопку мікрофона ще раз і виберіть «Дозволити» у вікні браузера.',
        not_found: 'Мікрофон не знайдено. Підключіть мікрофон і оновіть сторінку.',
        busy: 'Мікрофон зайнятий. Закрийте інші вкладки або застосунки, що використовують мікрофон.',
        not_supported: 'Ваш браузер не підтримує доступ до мікрофона.',
        blocked_env: 'Цей браузер блокує доступ до мікрофона. Відкрийте сторінку в Chrome, Safari або Firefox, щоб записати.',
        os_blocked: 'Доступ до мікрофона блокує операційна система. Увімкніть його в налаштуваннях конфіденційності системи і спробуйте знову.',
        unresponsive: 'Мікрофон не відповідає. Можливо, його зайняв інший дзвінок або застосунок — закрийте Zoom, Discord, Teams і оновіть сторінку.',
        session_mode: 'iPhone не ввімкнув мікрофон: сторінка ще була в режимі відтворення звуку. Натисніть «Спробувати знову». Якщо не вийде — закрийте вкладку й відкрийте сторінку в новій.',
        generic: 'Помилка мікрофона. Спробуйте закрити інші вкладки або перезапустити браузер.'
    },
    tr: {
        denied: 'Mikrofon erişimi reddedildi. Lütfen tarayıcı ayarlarından erişime izin verin ve tekrar deneyin.',
        dismissed: 'Mikrofon düğmesine tekrar tıklayın ve tarayıcı penceresinde “İzin Ver” seçeneğini seçin.',
        not_found: 'Mikrofon bulunamadı. Lütfen bir mikrofon bağlayın ve sayfayı yeniden yükleyin.',
        busy: 'Mikrofon meşgul. Mikrofonu kullanan diğer sekmeleri veya uygulamaları kapatıp tekrar deneyin.',
        not_supported: 'Tarayıcınız mikrofon erişimini desteklemiyor.',
        blocked_env: 'Bu tarayıcı mikrofon erişimini engelliyor. Kayıt yapmak için sayfayı Chrome, Safari veya Firefox’ta açın.',
        os_blocked: 'Sisteminiz mikrofon erişimini engelliyor. İşletim sisteminizin gizlilik ayarlarından etkinleştirip tekrar deneyin.',
        unresponsive: 'Mikrofon yanıt vermiyor. Başka bir uygulama onu kullanıyor olabilir — Zoom, Discord veya Teams gibi uygulamaları kapatıp sayfayı yeniden yükleyin.',
        session_mode: 'iPhone mikrofonu açmadı: sayfa hâlâ ses çalma modundaydı. “Tekrar dene”ye dokunun. Yine reddedilirse bu sekmeyi kapatıp sayfayı yeni bir sekmede açın.',
        generic: 'Mikrofon hatası. Diğer sekmeleri kapatmayı veya tarayıcıyı yeniden başlatmayı deneyin.'
    },
};

/**
 * Recovery modal copy per locale. Each entry:
 *   title        — modal heading
 *   intro        — one-line explanation before the instructions
 *   chrome       — Chrome/Edge/Opera/Brave step-by-step (HTML allowed)
 *   firefox      — Firefox instructions
 *   safari       — Safari (desktop and iOS)
 *   retry        — primary button label
 *   close        — secondary button label
 *   nf_title     — "no microphone detected" modal heading
 *   nf_body      — body text for not_found case
 *   nf_refresh   — primary button for refresh-and-retry
 *   busy_title   — heading for "busy"
 *   busy_body    — body for "busy"
 *   busy_retry   — primary button for busy
 */
var HELP = {
    en: {
        title: 'Allow microphone access',
        intro: 'We cannot record without your permission. Here is how to enable it in your browser:',
        chrome: '<strong>Chrome, Edge, Brave, Opera:</strong> click the lock or tune icon to the left of the address bar → find <em>Microphone</em> → switch to <em>Allow</em>. Then reload the page.',
        yandex: '<strong>Yandex Browser:</strong> click the coloured shield/Protect badge on the left of the address bar → <em>Details</em> → <em>Site settings</em> → <em>Microphone use</em> → <em>Allow</em>, then reload. <strong>Alternative:</strong> menu (≡) → Settings → Sites → Advanced site settings → Microphone → find this site → Allow.',
        yandex_mobile: '<strong>Yandex Browser on a phone:</strong> its mobile version has no per-site permission settings, so the block cannot be lifted from inside it. Two ways out: open this page in Chrome (button below) and allow the microphone there — or stay in Yandex Browser and wipe its site memory: ≡ → Settings → Privacy → Clear data → tick <em>Site data</em> → Clear. It will ask again after that (note: you will be signed out of websites).',
        open_chrome: 'Open in Chrome',
        firefox: '<strong>Firefox:</strong> click the microphone icon in the address bar → clear the block → reload the page, then click <em>Allow</em> when prompted again.',
        safari: '<strong>Safari:</strong> Safari → Settings → Websites → Microphone → set this site to <em>Allow</em>. On iPhone/iPad: Settings → Safari → Microphone.',
        samsung: '<strong>Samsung Internet:</strong> tap the lock icon in the address bar → <em>Permissions</em> → <em>Microphone</em> → <em>Allow</em>, then reload. <strong>Or:</strong> Menu (≡) → Settings → Sites and downloads → Site permissions → Microphone → find this site → Allow.',
        inapp: '<strong>This is an in-app browser.</strong> It has no address bar, so there is no microphone setting to turn on here. Open this page in <b>Chrome</b>, <b>Safari</b> or another normal browser — the link is below — and allow the microphone there. <b>Or</b> use the file mode below: record on your phone and upload the audio, no microphone permission needed.',
        stuck_title: 'Still blocked?',
        stuck_body: 'It looks like the permission is still off. The fastest path is the <strong>file mode</strong> below — record on your phone or upload an audio file, and Whisper transcribes it locally with no microphone needed.',
        fallback_label: 'Switch to file mode',
        retry: 'I allowed it — try again',
        close: 'Dismiss',
        nf_title: 'No microphone detected',
        nf_body: 'Plug in a microphone, USB headset, or Bluetooth device and refresh the list. On laptops, check that the built-in mic is not disabled in system settings.',
        nf_refresh: 'Refresh and retry',
        busy_title: 'Microphone is busy',
        sm_title: 'The iPhone did not turn the microphone on',
        busy_body: 'Another tab or app is using the microphone (Zoom, Discord, Teams, another browser tab…). Close it, then try again.',
        busy_retry: 'Try again',
        be_title: 'Microphone not available in this browser',
        be_body: 'This browser blocks microphone access — common on kids tablets, managed devices, and apps that open web pages inside themselves. To record, open <strong>timbrica.com</strong> in Chrome, Safari, Firefox, or Edge.',
        be_kids_body: 'This is the Amazon Kids browser — it blocks the microphone for every website, and there is no setting here (for kids or parents) that unlocks it. Ask a parent to open this page in the <strong>Silk</strong> browser from the parent profile — or continue here by uploading a file.',
        un_title: 'Microphone is not responding',
        un_body: 'Your browser started the microphone request but it never completed. This usually means another app — Zoom, Discord, Teams, a game, or another browser tab — is holding the microphone, or the system audio service has stalled.<br><br>Close other apps that use the mic and try again, or reload the page. If it keeps happening, restart your browser.',
        un_retry: 'Try again',
        os_title: 'Microphone blocked by your system',
        os_intro: 'The browser allowed the microphone, but your operating system is blocking it. Turn it on in system settings:',
        os_win: '<strong>Windows:</strong> Start → Settings → Privacy &amp; security → Microphone → turn on <em>Microphone access</em> and <em>Let apps access your microphone</em>. Then come back and click the button below.',
        os_mac: '<strong>macOS:</strong> System Settings → Privacy &amp; Security → Microphone → enable access for your browser. Then click the button below.',
        os_retry: 'I enabled it — try again',
        os_reload: 'Reload the page',
        os_reload_hint: 'Already enabled it but still blocked? The change usually only applies after the page reloads — use the button below. If you run antivirus with a microphone shield (e.g. Kaspersky), allow this site there too.',
        chrome_android: '<strong>Chrome on Android:</strong> tap the lock (or ⓘ) icon to the left of the address → <em>Permissions</em> → <em>Microphone</em> → <em>Allow</em>. Then come back to this tab — it continues by itself, no reload needed.',
        chrome_bounced: '<strong>Chrome did not open</strong> — it is probably not installed on this device. Copy the link below into any other browser and allow the microphone there, or use the file mode.',
        status_blocked: 'Microphone for this site: <strong>blocked</strong>.',
        status_waiting: 'Microphone: the browser will ask again — press the microphone button.',
        status_granted: 'Microphone allowed — starting…',
        status_still: 'Still blocked. The switch is in the browser, not on this page — follow the steps above; this will continue by itself once it is on.',
        check: 'I turned it on — check',
        wait_hint: 'The browser is asking for microphone permission — find its prompt (next to the address bar, or at the bottom of the screen on a phone) and choose Allow.',
        wait_title: 'Still waiting for your answer',
        wait_body: 'The browser\'s microphone prompt has been open for a while and nothing can continue until it is answered. Choose <em>Allow</em> in it. If you cannot see any prompt, reload the page and press the button again — or use the file mode below.'
    },
    ru: {
        title: 'Разрешите доступ к микрофону',
        intro: 'Без вашего разрешения мы не можем записывать. Как включить доступ в браузере:',
        chrome: '<strong>Chrome, Edge, Brave, Opera:</strong> нажмите на значок замка или настроек слева от адресной строки → найдите пункт <em>Микрофон</em> → переключите на <em>Разрешить</em>. Затем обновите страницу.',
        yandex: '<strong>Яндекс Браузер:</strong> нажмите на цветной значок щита (Protect) слева от адресной строки → <em>Подробнее</em> → <em>Настройки сайта</em> → <em>Использование микрофона</em> → <em>Разрешить</em>, затем обновите страницу. <strong>Альтернатива:</strong> ≡ меню → Настройки → Сайты → Расширенные настройки сайтов → Микрофон → найти этот сайт → Разрешено.',
        yandex_mobile: '<strong>Яндекс Браузер на телефоне:</strong> в мобильной версии нет настроек разрешений для сайтов — снять запрет изнутри нельзя. Два выхода: открыть эту страницу в Chrome (кнопка ниже) и разрешить микрофон там — либо остаться в Яндекс Браузере и стереть его память о сайтах: ≡ → Настройки → Конфиденциальность → Очистить данные → отметить <em>Данные сайтов</u> → Очистить. После этого он спросит заново (учтите: вы выйдете из аккаунтов на сайтах).',
        open_chrome: 'Открыть в Chrome',
        firefox: '<strong>Firefox:</strong> нажмите на значок микрофона в адресной строке → снимите блокировку → обновите страницу, затем нажмите <em>Разрешить</em> во всплывающем окне.',
        safari: '<strong>Safari:</strong> Safari → Настройки → Веб-сайты → Микрофон → поставьте <em>Разрешить</em> для этого сайта. На iPhone/iPad: Настройки → Safari → Микрофон.',
        samsung: '<strong>Samsung Internet:</strong> нажмите на значок замка в адресной строке → <em>Разрешения</em> → <em>Микрофон</em> → <em>Разрешить</em>, затем обновите страницу. <strong>Или:</strong> Меню (≡) → Настройки → Сайты и загрузки → Разрешения для сайтов → Микрофон → найдите этот сайт → Разрешить.',
        inapp: '<strong>Это встроенный браузер приложения.</strong> В нём нет адресной строки, поэтому включить микрофон здесь негде. Откройте страницу в <b>Chrome</b>, <b>Яндекс Браузере</b> или другом обычном браузере — ссылка ниже — и разрешите микрофон там. <b>Или</b> воспользуйтесь файловым режимом ниже: запишите на телефон и загрузите аудио, разрешение микрофона не нужно.',
        stuck_title: 'Всё ещё заблокировано?',
        stuck_body: 'Похоже, разрешение пока не снято. Быстрый путь — <strong>файловый режим</strong> ниже: запишите на телефоне или загрузите аудио, и Whisper расшифрует локально, без микрофона.',
        fallback_label: 'Перейти в файловый режим',
        retry: 'Я разрешил — попробовать снова',
        close: 'Закрыть',
        nf_title: 'Микрофон не найден',
        nf_body: 'Подключите микрофон, USB-гарнитуру или Bluetooth-устройство и обновите список. На ноутбуках проверьте, что встроенный микрофон не отключён в настройках системы.',
        nf_refresh: 'Обновить и повторить',
        be_title: 'Микрофон недоступен в этом браузере',
        be_body: 'Этот браузер блокирует доступ к микрофону — так бывает на детских планшетах, корпоративных устройствах и в приложениях, которые открывают веб-страницы внутри себя. Чтобы записать звук, откройте <strong>timbrica.com</strong> в Chrome, Safari, Firefox или Edge.',
        be_kids_body: 'Это детский браузер Amazon Kids — он блокирует микрофон для всех сайтов, и включить его здесь нельзя ни ребёнку, ни родителю. Попросите родителя открыть эту страницу в браузере <strong>Silk</strong> из родительского профиля — или продолжите здесь, загрузив файл.',
        busy_title: 'Микрофон занят',
        sm_title: 'iPhone не включил микрофон',
        busy_body: 'Другая вкладка или приложение использует микрофон (Zoom, Discord, Teams, другая вкладка браузера…). Закройте его и попробуйте снова.',
        busy_retry: 'Попробовать снова',
        un_title: 'Микрофон не отвечает',
        un_body: 'Браузер начал запрашивать доступ к микрофону, но запрос так и не завершился. Обычно это значит, что микрофон занят другим приложением — Zoom, Discord, Teams, игрой или другой вкладкой браузера — или зависла системная служба звука.<br><br>Закройте другие приложения, использующие микрофон, и попробуйте снова или обновите страницу. Если повторяется — перезапустите браузер.',
        un_retry: 'Попробовать снова',
        os_title: 'Микрофон заблокирован системой',
        os_intro: 'Браузер разрешил доступ к микрофону, но его блокирует операционная система. Включите доступ в настройках системы:',
        os_win: '<strong>Windows:</strong> Пуск → Параметры → Конфиденциальность и защита → Микрофон → включите <em>Доступ к микрофону</em> и <em>Разрешить приложениям доступ к микрофону</em>. Затем вернитесь и нажмите кнопку ниже.',
        os_mac: '<strong>macOS:</strong> Системные настройки → Конфиденциальность и безопасность → Микрофон → включите доступ для вашего браузера. Затем нажмите кнопку ниже.',
        os_retry: 'Я включил доступ — попробовать снова',
        os_reload: 'Перезагрузить страницу',
        os_reload_hint: 'Уже включили, но доступ всё ещё заблокирован? Изменение обычно срабатывает только после перезагрузки страницы — нажмите кнопку ниже. Если у вас антивирус с защитой микрофона (например, Kaspersky), разрешите доступ к этому сайту и в нём.',
        chrome_android: '<strong>Chrome на Android:</strong> нажмите значок замка (или ⓘ) слева от адреса → <em>Разрешения</em> → <em>Микрофон</em> → <em>Разрешить</em>. Потом вернитесь на эту вкладку — всё продолжится само, перезагружать не нужно.',
        chrome_bounced: '<strong>Chrome не открылся</strong> — скорее всего, его нет на этом устройстве. Скопируйте ссылку ниже в любой другой браузер и разрешите микрофон там — или воспользуйтесь файловым режимом.',
        status_blocked: 'Микрофон для этого сайта: <strong>запрещён</strong>.',
        status_waiting: 'Микрофон: браузер спросит снова — нажмите кнопку микрофона.',
        status_granted: 'Микрофон разрешён — запускаем…',
        status_still: 'Всё ещё запрещён. Переключатель — в браузере, а не на этой странице: пройдите шаги выше; как только он включён, всё продолжится само.',
        check: 'Я включил — проверить',
        wait_hint: 'Браузер спрашивает разрешение на микрофон — найдите его запрос (рядом с адресной строкой, а на телефоне — внизу экрана) и выберите «Разрешить».',
        wait_title: 'Всё ещё ждём вашего ответа',
        wait_body: 'Запрос браузера на микрофон открыт уже давно, и пока на него не ответить, ничего не продолжится. Выберите в нём <em>Разрешить</em>. Если никакого запроса не видно — перезагрузите страницу и нажмите кнопку ещё раз, или используйте файловый режим ниже.'
    },
    ko: {
        title: '마이크 접근 허용',
        intro: '허가 없이는 녹음할 수 없습니다. 브라우저에서 접근을 활성화하는 방법:',
        chrome: '<strong>Chrome, Edge, Brave, Opera:</strong> 주소 표시줄 왼쪽의 자물쇠 또는 설정 아이콘 클릭 → <em>마이크</em> 항목 찾기 → <em>허용</em>으로 전환. 그런 다음 페이지를 새로고침하세요.',
        yandex: '<strong>Yandex Browser:</strong> 주소 표시줄 왼쪽의 색상 보호 방패 아이콘 클릭 → <em>자세히</em> → <em>사이트 설정</em> → <em>마이크 사용</em> → <em>허용</em> 후 새로고침. <strong>대안:</strong> ≡ 메뉴 → 설정 → 사이트 → 고급 사이트 설정 → 마이크 → 이 사이트 찾기 → 허용.',
        yandex_mobile: '<strong>휴대폰의 Yandex Browser:</strong> 모바일 버전에는 사이트별 권한 설정이 없어 내부에서 차단을 해제할 수 없습니다. 두 가지 방법: 아래 버튼으로 이 페이지를 Chrome에서 열어 마이크를 허용하거나, Yandex Browser에 머물러 사이트 기록을 지우세요: ≡ → 설정 → 개인정보 보호 → 데이터 삭제 → <em>사이트 데이터</em> 선택 → 삭제. 그 다음 다시 묻습니다(사이트에서 로그아웃됩니다).',
        open_chrome: 'Chrome에서 열기',
        firefox: '<strong>Firefox:</strong> 주소 표시줄의 마이크 아이콘 클릭 → 차단 해제 → 페이지 새로고침 후 팝업에서 <em>허용</em> 클릭.',
        safari: '<strong>Safari:</strong> Safari → 설정 → 웹사이트 → 마이크 → 이 사이트를 <em>허용</em>으로 설정. iPhone/iPad: 설정 → Safari → 마이크.',
        samsung: '<strong>Samsung Internet:</strong> 주소창의 자물쇠 아이콘 탭 → <em>권한</em> → <em>마이크</em> → <em>허용</em> 후 새로 고침. <strong>또는:</strong> 메뉴 (≡) → 설정 → 사이트 및 다운로드 → 사이트 권한 → 마이크 → 이 사이트 → 허용.',
        inapp: '<strong>앱 내장 브라우저입니다.</strong> 주소 표시줄이 없어 여기서는 마이크를 켤 수 없습니다. 아래 링크를 <b>Chrome</b>이나 <b>Safari</b> 같은 일반 브라우저에서 열고 그곳에서 마이크를 허용하세요. <b>또는</b> 아래 파일 모드를 사용하세요 — 휴대폰으로 녹음해 올리면 마이크 권한이 필요 없습니다.',
        stuck_title: '아직 차단되어 있나요?',
        stuck_body: '권한이 아직 풀리지 않은 것 같습니다. 가장 빠른 방법은 아래 <strong>파일 모드</strong>입니다 — 휴대폰으로 녹음하거나 오디오 파일을 업로드하면 Whisper가 마이크 없이 로컬에서 변환합니다.',
        fallback_label: '파일 모드로 전환',
        retry: '허용했어요 — 다시 시도',
        close: '닫기',
        nf_title: '마이크를 찾을 수 없음',
        nf_body: '마이크, USB 헤드셋 또는 Bluetooth 장치를 연결하고 목록을 새로고침하세요. 노트북이라면 시스템 설정에서 내장 마이크가 비활성화되지 않았는지 확인하세요.',
        nf_refresh: '새로고침 후 다시 시도',
        be_title: '이 브라우저에서는 마이크를 사용할 수 없습니다',
        be_body: '이 브라우저는 마이크 접근을 차단합니다 — 어린이 태블릿, 관리 기기, 웹 페이지를 자체적으로 여는 앱에서 흔히 발생합니다. 녹음하려면 Chrome, Safari, Firefox 또는 Edge에서 <strong>timbrica.com</strong>을 여세요.',
        be_kids_body: 'Amazon Kids 브라우저는 모든 웹사이트의 마이크를 차단하며, 어린이도 부모도 여기서는 켤 수 없습니다. 부모 프로필의 <strong>Silk</strong> 브라우저로 이 페이지를 열어 달라고 부탁하거나, 파일을 업로드해 여기서 계속하세요.',
        busy_title: '마이크가 사용 중입니다',
        sm_title: 'iPhone이 마이크를 켜지 않았습니다',
        busy_body: '다른 탭이나 앱(Zoom, Discord, Teams, 다른 브라우저 탭 등)이 마이크를 사용 중입니다. 닫고 다시 시도하세요.',
        busy_retry: '다시 시도',
        un_title: '마이크가 응답하지 않습니다',
        un_body: '브라우저가 마이크 요청을 시작했지만 완료되지 않았습니다. 보통 다른 앱 — Zoom, Discord, Teams, 게임 또는 다른 브라우저 탭 — 이 마이크를 점유하고 있거나 시스템 오디오 서비스가 멈춘 경우입니다.<br><br>마이크를 사용하는 다른 앱을 닫고 다시 시도하거나 페이지를 새로고침하세요. 계속되면 브라우저를 재시작하세요.',
        un_retry: '다시 시도',
        os_title: '시스템이 마이크를 차단함',
        os_intro: '브라우저는 마이크를 허용했지만 운영체제가 차단하고 있습니다. 시스템 설정에서 켜세요:',
        os_win: '<strong>Windows:</strong> 시작 → 설정 → 개인 정보 및 보안 → 마이크 → <em>마이크 액세스</em>와 <em>앱이 마이크에 액세스하도록 허용</em>을 켭니다. 그런 다음 돌아와서 아래 버튼을 누르세요.',
        os_mac: '<strong>macOS:</strong> 시스템 설정 → 개인정보 보호 및 보안 → 마이크 → 브라우저의 접근을 허용합니다. 그런 다음 아래 버튼을 누르세요.',
        os_retry: '허용했어요 — 다시 시도',
        os_reload: '페이지 새로고침',
        os_reload_hint: '이미 켰는데도 계속 차단되나요? 변경 사항은 보통 페이지를 새로고침해야 적용됩니다 — 아래 버튼을 누르세요. 마이크 보호 기능이 있는 백신(예: Kaspersky)을 사용 중이라면 이 사이트도 허용하세요.',
        chrome_android: '<strong>Android의 Chrome:</strong> 주소 왼쪽의 자물쇠(또는 ⓘ) 아이콘 탭 → <em>권한</em> → <em>마이크</em> → <em>허용</em>. 그런 다음 이 탭으로 돌아오세요 — 새로고침 없이 자동으로 이어집니다.',
        chrome_bounced: '<strong>Chrome이 열리지 않았습니다</strong> — 이 기기에 설치되어 있지 않은 것 같습니다. 아래 링크를 다른 브라우저에 복사해 그곳에서 마이크를 허용하거나, 파일 모드를 사용하세요.',
        status_blocked: '이 사이트의 마이크: <strong>차단됨</strong>.',
        status_waiting: '마이크: 브라우저가 다시 묻습니다 — 마이크 버튼을 누르세요.',
        status_granted: '마이크 허용됨 — 시작하는 중…',
        status_still: '아직 차단되어 있습니다. 스위치는 이 페이지가 아니라 브라우저에 있습니다 — 위의 단계를 따르세요. 켜지는 즉시 자동으로 이어집니다.',
        check: '켰어요 — 확인',
        wait_hint: '브라우저가 마이크 권한을 묻고 있습니다 — 요청 창(주소 표시줄 옆, 휴대폰에서는 화면 하단)을 찾아 허용을 선택하세요.',
        wait_title: '아직 답변을 기다리고 있습니다',
        wait_body: '브라우저의 마이크 요청 창이 한참 열려 있으며, 답할 때까지 진행되지 않습니다. 그 창에서 <em>허용</em>을 선택하세요. 요청 창이 보이지 않으면 페이지를 새로고침하고 버튼을 다시 누르거나, 아래 파일 모드를 사용하세요.'
    },
    ar: {
        title: 'السماح بالوصول إلى الميكروفون',
        intro: 'لا يمكننا التسجيل بدون إذنك. إليك كيفية تمكين الوصول في المتصفح:',
        chrome: '<strong>Chrome, Edge, Brave, Opera:</strong> انقر على أيقونة القفل أو الإعدادات يسار شريط العناوين ← ابحث عن <em>الميكروفون</em> ← اضبطه على <em>السماح</em>. ثم أعد تحميل الصفحة.',
        yandex: '<strong>متصفح Yandex:</strong> انقر على أيقونة الدرع الملونة (Protect) يسار شريط العناوين ← <em>التفاصيل</em> ← <em>إعدادات الموقع</em> ← <em>استخدام الميكروفون</em> ← <em>السماح</em>، ثم أعد تحميل الصفحة. <strong>بديل:</strong> ≡ القائمة ← الإعدادات ← المواقع ← إعدادات المواقع المتقدمة ← الميكروفون ← اعثر على هذا الموقع ← السماح.',
        yandex_mobile: '<strong>متصفح Yandex على الهاتف:</strong> لا تحتوي النسخة المحمولة على إعدادات أذونات للمواقع، لذلك لا يمكن رفع الحظر من داخله. طريقتان: افتح هذه الصفحة في Chrome (الزر بالأسفل) واسمح بالميكروفون هناك، أو ابق في Yandex وامسح ذاكرة المواقع: ≡ ← الإعدادات ← الخصوصية ← مسح البيانات ← حدد <em>بيانات المواقع</em> ← مسح. سيسأل مرة أخرى (ستخرج من حسابات المواقع).',
        open_chrome: 'افتح في Chrome',
        firefox: '<strong>Firefox:</strong> انقر على أيقونة الميكروفون في شريط العناوين ← أزل الحظر ← أعد تحميل الصفحة، ثم اضغط <em>السماح</em> في النافذة المنبثقة.',
        safari: '<strong>Safari:</strong> Safari ← الإعدادات ← المواقع ← الميكروفون ← اضبط هذا الموقع على <em>السماح</em>. على iPhone/iPad: الإعدادات ← Safari ← الميكروفون.',
        samsung: '<strong>Samsung Internet:</strong> اضغط على أيقونة القفل في شريط العنوان ← <em>الأذونات</em> ← <em>الميكروفون</em> ← <em>السماح</em>، ثم أعد تحميل الصفحة. <strong>أو:</strong> القائمة (≡) ← الإعدادات ← المواقع والتنزيلات ← أذونات الموقع ← الميكروفون ← هذا الموقع ← السماح.',
        inapp: '<strong>هذا متصفح مدمج داخل التطبيق.</strong> لا يحتوي على شريط عناوين، لذا لا يمكن تفعيل الميكروفون هنا. افتح الصفحة في <b>Chrome</b> أو <b>Safari</b> أو أي متصفح عادي — الرابط أدناه — واسمح بالميكروفون هناك. <b>أو</b> استخدم وضع الملفات أدناه: سجّل على هاتفك وارفع الصوت، دون الحاجة إلى إذن الميكروفون.',
        stuck_title: 'لا يزال محظوراً؟',
        stuck_body: 'يبدو أن الإذن لم يُلغَ بعد. الطريقة الأسرع هي <strong>وضع الملف</strong> أدناه — سجّل بهاتفك أو ارفع ملفاً صوتياً، وسيقوم Whisper بالتفريغ محلياً دون الحاجة إلى ميكروفون.',
        fallback_label: 'التبديل إلى وضع الملف',
        retry: 'سمحت بالوصول — حاول مجدداً',
        close: 'إغلاق',
        nf_title: 'لم يتم العثور على ميكروفون',
        nf_body: 'صل ميكروفوناً أو سماعة USB أو جهاز Bluetooth وحدّث القائمة. على أجهزة اللابتوب تأكد أن الميكروفون المدمج غير معطل في إعدادات النظام.',
        nf_refresh: 'تحديث وإعادة المحاولة',
        be_title: 'الميكروفون غير متاح في هذا المتصفح',
        be_body: 'هذا المتصفح يحظر الوصول إلى الميكروفون — وهذا شائع في أجهزة الأطفال اللوحية والأجهزة المُدارة والتطبيقات التي تفتح صفحات الويب داخلها. للتسجيل، افتح <strong>timbrica.com</strong> في Chrome أو Safari أو Firefox أو Edge.',
        be_kids_body: 'هذا متصفح Amazon Kids — يحظر الميكروفون لجميع المواقع، ولا يمكن للطفل ولا للوالدين تفعيله هنا. اطلب من أحد الوالدين فتح هذه الصفحة في متصفح <strong>Silk</strong> من ملف الوالدين — أو تابع هنا برفع ملف.',
        busy_title: 'الميكروفون مشغول',
        sm_title: 'لم يشغّل iPhone الميكروفون',
        busy_body: 'تبويب أو تطبيق آخر يستخدم الميكروفون (Zoom, Discord, Teams, تبويب متصفح آخر…). أغلقه وأعد المحاولة.',
        busy_retry: 'إعادة المحاولة',
        un_title: 'الميكروفون لا يستجيب',
        un_body: 'بدأ المتصفح طلب الوصول إلى الميكروفون لكنه لم يكتمل أبداً. عادةً يعني هذا أن تطبيقاً آخر — Zoom أو Discord أو Teams أو لعبة أو تبويب متصفح آخر — يحتجز الميكروفون، أو أن خدمة الصوت في النظام قد توقفت.<br><br>أغلق التطبيقات الأخرى التي تستخدم الميكروفون وحاول مجدداً، أو أعد تحميل الصفحة. إذا تكرر الأمر، أعد تشغيل المتصفح.',
        un_retry: 'حاول مجدداً',
        os_title: 'النظام يحظر الميكروفون',
        os_intro: 'سمح المتصفح بالوصول إلى الميكروفون، لكن نظام التشغيل يحظره. فعّله من إعدادات النظام:',
        os_win: '<strong>Windows:</strong> ابدأ ← الإعدادات ← الخصوصية والأمان ← الميكروفون ← فعّل <em>الوصول إلى الميكروفون</em> و<em>السماح للتطبيقات بالوصول إلى الميكروفون</em>. ثم عُد واضغط الزر أدناه.',
        os_mac: '<strong>macOS:</strong> إعدادات النظام ← الخصوصية والأمان ← الميكروفون ← فعّل الوصول لمتصفحك. ثم اضغط الزر أدناه.',
        os_retry: 'فعّلت الوصول — حاول مجدداً',
        os_reload: 'إعادة تحميل الصفحة',
        os_reload_hint: 'فعّلته لكنه لا يزال محظوراً؟ عادةً لا يُطبَّق التغيير إلا بعد إعادة تحميل الصفحة — استخدم الزر أدناه. إذا كنت تستخدم برنامج حماية فيه درع للميكروفون (مثل Kaspersky)، فاسمح لهذا الموقع فيه أيضاً.',
        chrome_android: '<strong>Chrome على Android:</strong> اضغط على أيقونة القفل (أو ⓘ) يسار العنوان ← <em>الأذونات</em> ← <em>الميكروفون</em> ← <em>السماح</em>. ثم عُد إلى هذا التبويب — سيتابع تلقائياً دون إعادة تحميل.',
        chrome_bounced: '<strong>لم يُفتح Chrome</strong> — على الأرجح غير مثبّت على هذا الجهاز. انسخ الرابط أدناه إلى أي متصفح آخر واسمح بالميكروفون هناك، أو استخدم وضع الملف.',
        status_blocked: 'الميكروفون لهذا الموقع: <strong>محظور</strong>.',
        status_waiting: 'الميكروفون: سيسأل المتصفح مجدداً — اضغط زر الميكروفون.',
        status_granted: 'تم السماح بالميكروفون — جارٍ البدء…',
        status_still: 'لا يزال محظوراً. المفتاح في المتصفح وليس في هذه الصفحة — اتبع الخطوات أعلاه؛ سيتابع تلقائياً بمجرد تفعيله.',
        check: 'فعّلته — تحقّق',
        wait_hint: 'المتصفح يطلب إذن الميكروفون — ابحث عن نافذة الطلب (بجوار شريط العناوين، أو أسفل الشاشة على الهاتف) واختر السماح.',
        wait_title: 'ما زلنا ننتظر إجابتك',
        wait_body: 'نافذة طلب الميكروفون في المتصفح مفتوحة منذ فترة، ولن يتابع شيء حتى تُجيب عليها. اختر <em>السماح</em> فيها. إن لم ترَ أي نافذة، أعد تحميل الصفحة واضغط الزر مجدداً — أو استخدم وضع الملف أدناه.'
    },
    id: {
        title: 'Izinkan akses mikrofon',
        intro: 'Kami tidak dapat merekam tanpa izin Anda. Begini cara mengaktifkannya di browser:',
        chrome: '<strong>Chrome, Edge, Brave, Opera:</strong> klik ikon gembok atau pengaturan di kiri bilah alamat → cari <em>Mikrofon</em> → ubah ke <em>Izinkan</em>. Lalu muat ulang halaman.',
        yandex: '<strong>Yandex Browser:</strong> klik ikon perisai berwarna (Protect) di kiri bilah alamat → <em>Detail</em> → <em>Pengaturan situs</em> → <em>Penggunaan mikrofon</em> → <em>Izinkan</em>, lalu muat ulang. <strong>Alternatif:</strong> menu ≡ → Pengaturan → Situs → Pengaturan situs lanjutan → Mikrofon → temukan situs ini → Izinkan.',
        yandex_mobile: '<strong>Yandex Browser di ponsel:</strong> versi selulernya tidak punya pengaturan izin per situs, jadi blokir tidak bisa dicabut dari dalam. Dua jalan keluar: buka halaman ini di Chrome (tombol di bawah) dan izinkan mikrofon di sana — atau tetap di Yandex Browser dan hapus memori situsnya: ≡ → Pengaturan → Privasi → Hapus data → centang <em>Data situs</em> → Hapus. Setelah itu izin akan ditanyakan lagi (Anda akan keluar dari akun di situs-situs).',
        open_chrome: 'Buka di Chrome',
        firefox: '<strong>Firefox:</strong> klik ikon mikrofon di bilah alamat → hapus pemblokiran → muat ulang halaman, lalu klik <em>Izinkan</em> saat diminta.',
        safari: '<strong>Safari:</strong> Safari → Pengaturan → Situs Web → Mikrofon → atur situs ini ke <em>Izinkan</em>. Di iPhone/iPad: Pengaturan → Safari → Mikrofon.',
        samsung: '<strong>Samsung Internet:</strong> ketuk ikon gembok di bilah alamat → <em>Izin</em> → <em>Mikrofon</em> → <em>Izinkan</em>, lalu muat ulang. <strong>Atau:</strong> Menu (≡) → Pengaturan → Situs dan unduhan → Izin situs → Mikrofon → cari situs ini → Izinkan.',
        inapp: '<strong>Ini browser di dalam aplikasi.</strong> Tidak ada bilah alamat, jadi mikrofon tidak bisa diaktifkan di sini. Buka halaman ini di <b>Chrome</b>, <b>Safari</b>, atau browser biasa lainnya — tautannya di bawah — dan izinkan mikrofon di sana. <b>Atau</b> gunakan mode file di bawah: rekam di ponsel lalu unggah audionya, tanpa izin mikrofon.',
        stuck_title: 'Masih diblokir?',
        stuck_body: 'Sepertinya izin masih belum aktif. Cara tercepat adalah <strong>mode file</strong> di bawah — rekam dengan ponsel atau unggah file audio, dan Whisper akan menyalinnya secara lokal tanpa mikrofon.',
        fallback_label: 'Beralih ke mode File',
        retry: 'Saya sudah mengizinkan — coba lagi',
        close: 'Tutup',
        nf_title: 'Mikrofon tidak terdeteksi',
        nf_body: 'Colokkan mikrofon, headset USB, atau perangkat Bluetooth dan segarkan daftarnya. Pada laptop, pastikan mikrofon bawaan tidak dinonaktifkan di pengaturan sistem.',
        nf_refresh: 'Segarkan dan coba lagi',
        be_title: 'Mikrofon tidak tersedia di browser ini',
        be_body: 'Browser ini memblokir akses mikrofon — umum terjadi pada tablet anak, perangkat terkelola, dan aplikasi yang membuka halaman web di dalamnya. Untuk merekam, buka <strong>timbrica.com</strong> di Chrome, Safari, Firefox, atau Edge.',
        be_kids_body: 'Ini browser Amazon Kids — mikrofon diblokir untuk semua situs, dan tidak ada pengaturan di sini (untuk anak maupun orang tua) yang bisa mengaktifkannya. Minta orang tua membuka halaman ini di browser <strong>Silk</strong> dari profil orang tua — atau lanjutkan di sini dengan mengunggah file.',
        busy_title: 'Mikrofon sedang digunakan',
        sm_title: 'iPhone tidak menyalakan mikrofon',
        busy_body: 'Tab atau aplikasi lain sedang menggunakan mikrofon (Zoom, Discord, Teams, tab browser lain…). Tutup dan coba lagi.',
        busy_retry: 'Coba lagi',
        un_title: 'Mikrofon tidak merespons',
        un_body: 'Browser memulai permintaan mikrofon tetapi tidak pernah selesai. Biasanya ini berarti aplikasi lain — Zoom, Discord, Teams, game, atau tab browser lain — sedang menahan mikrofon, atau layanan audio sistem macet.<br><br>Tutup aplikasi lain yang memakai mikrofon dan coba lagi, atau muat ulang halaman. Jika terus terjadi, mulai ulang browser.',
        un_retry: 'Coba lagi',
        os_title: 'Mikrofon diblokir oleh sistem',
        os_intro: 'Browser mengizinkan mikrofon, tetapi sistem operasi memblokirnya. Aktifkan di pengaturan sistem:',
        os_win: '<strong>Windows:</strong> Mulai → Pengaturan → Privasi &amp; keamanan → Mikrofon → aktifkan <em>Akses mikrofon</em> dan <em>Izinkan aplikasi mengakses mikrofon</em>. Lalu kembali dan klik tombol di bawah.',
        os_mac: '<strong>macOS:</strong> Pengaturan Sistem → Privasi &amp; Keamanan → Mikrofon → aktifkan akses untuk browser Anda. Lalu klik tombol di bawah.',
        os_retry: 'Sudah saya aktifkan — coba lagi',
        os_reload: 'Muat ulang halaman',
        os_reload_hint: 'Sudah diaktifkan tapi masih diblokir? Perubahan biasanya baru berlaku setelah halaman dimuat ulang — gunakan tombol di bawah. Jika Anda memakai antivirus dengan pelindung mikrofon (mis. Kaspersky), izinkan situs ini di sana juga.',
        chrome_android: '<strong>Chrome di Android:</strong> ketuk ikon gembok (atau ⓘ) di kiri alamat → <em>Izin</em> → <em>Mikrofon</em> → <em>Izinkan</em>. Lalu kembali ke tab ini — akan berlanjut sendiri, tanpa muat ulang.',
        chrome_bounced: '<strong>Chrome tidak terbuka</strong> — kemungkinan tidak terpasang di perangkat ini. Salin tautan di bawah ke browser lain dan izinkan mikrofon di sana, atau gunakan mode file.',
        status_blocked: 'Mikrofon untuk situs ini: <strong>diblokir</strong>.',
        status_waiting: 'Mikrofon: browser akan bertanya lagi — tekan tombol mikrofon.',
        status_granted: 'Mikrofon diizinkan — memulai…',
        status_still: 'Masih diblokir. Sakelarnya ada di browser, bukan di halaman ini — ikuti langkah di atas; begitu aktif, semuanya berlanjut sendiri.',
        check: 'Sudah saya aktifkan — periksa',
        wait_hint: 'Browser meminta izin mikrofon — cari kotak permintaannya (di samping bilah alamat, atau di bagian bawah layar pada ponsel) dan pilih Izinkan.',
        wait_title: 'Masih menunggu jawaban Anda',
        wait_body: 'Permintaan mikrofon dari browser sudah lama terbuka, dan tidak ada yang berlanjut sampai dijawab. Pilih <em>Izinkan</em> di sana. Jika tidak ada permintaan yang terlihat, muat ulang halaman dan tekan tombolnya lagi — atau gunakan mode file di bawah.'
    },
    pt: {
        title: 'Permitir acesso ao microfone',
        intro: 'Não podemos gravar sem a sua permissão. Veja como habilitar no seu navegador:',
        chrome: '<strong>Chrome, Edge, Brave, Opera:</strong> clique no ícone de cadeado ou de ajustes à esquerda da barra de endereços → encontre <em>Microfone</em> → mude para <em>Permitir</em>. Depois recarregue a página.',
        yandex: '<strong>Yandex Browser:</strong> clique no ícone de escudo colorido (Protect) à esquerda da barra de endereços → <em>Detalhes</em> → <em>Configurações do site</em> → <em>Uso do microfone</em> → <em>Permitir</em>, depois recarregue. <strong>Alternativa:</strong> menu ≡ → Configurações → Sites → Configurações avançadas de sites → Microfone → encontre este site → Permitir.',
        yandex_mobile: '<strong>Yandex Browser no telemóvel:</strong> a versão móvel não tem definições de permissões por site, por isso o bloqueio não pode ser removido por dentro. Duas saídas: abrir esta página no Chrome (botão abaixo) e permitir o microfone lá — ou ficar no Yandex Browser e apagar a memória de sites: ≡ → Configurações → Privacidade → Limpar dados → marcar <em>Dados de sites</em> → Limpar. Depois disso ele pergunta de novo (você sairá das contas nos sites).',
        open_chrome: 'Abrir no Chrome',
        firefox: '<strong>Firefox:</strong> clique no ícone do microfone na barra de endereços → remova o bloqueio → recarregue a página e clique em <em>Permitir</em> quando solicitado.',
        safari: '<strong>Safari:</strong> Safari → Preferências → Sites → Microfone → defina este site como <em>Permitir</em>. No iPhone/iPad: Ajustes → Safari → Microfone.',
        samsung: '<strong>Samsung Internet:</strong> toque no ícone de cadeado na barra de endereço → <em>Permissões</em> → <em>Microfone</em> → <em>Permitir</em>, depois recarregue. <strong>Ou:</strong> Menu (≡) → Configurações → Sites e downloads → Permissões de site → Microfone → encontre este site → Permitir.',
        inapp: '<strong>Este é um navegador dentro de um aplicativo.</strong> Ele não tem barra de endereços, então não há como ativar o microfone aqui. Abra esta página no <b>Chrome</b>, <b>Safari</b> ou outro navegador normal — o link está abaixo — e permita o microfone lá. <b>Ou</b> use o modo de arquivo abaixo: grave no celular e envie o áudio, sem precisar de permissão de microfone.',
        stuck_title: 'Ainda bloqueado?',
        stuck_body: 'Parece que a permissão ainda não foi liberada. O caminho mais rápido é o <strong>modo arquivo</strong> abaixo — grave no celular ou envie um arquivo de áudio, e o Whisper transcreve localmente sem precisar de microfone.',
        fallback_label: 'Mudar para modo Arquivo',
        retry: 'Permiti — tentar novamente',
        close: 'Fechar',
        nf_title: 'Nenhum microfone detectado',
        nf_body: 'Conecte um microfone, headset USB ou dispositivo Bluetooth e atualize a lista. Em notebooks, verifique se o microfone embutido não está desativado nas configurações do sistema.',
        nf_refresh: 'Atualizar e tentar novamente',
        busy_title: 'Microfone ocupado',
        sm_title: 'O iPhone não ligou o microfone',
        busy_body: 'Outra aba ou app está usando o microfone (Zoom, Discord, Teams, outra aba do navegador…). Feche e tente novamente.',
        busy_retry: 'Tentar novamente',
        be_title: 'Microfone indisponível neste navegador',
        be_body: 'Este navegador bloqueia o acesso ao microfone — comum em tablets infantis, dispositivos gerenciados e apps que abrem páginas web dentro deles. Para gravar, abra <strong>timbrica.com</strong> no Chrome, Safari, Firefox ou Edge.',
        be_kids_body: 'Este é o navegador Amazon Kids — ele bloqueia o microfone para todos os sites, e não há como ativá-lo aqui (nem a criança, nem o responsável). Peça a um responsável para abrir esta página no navegador <strong>Silk</strong> do perfil dos pais — ou continue aqui enviando um arquivo.',
        un_title: 'O microfone não está respondendo',
        un_body: 'O navegador iniciou a solicitação do microfone, mas ela nunca foi concluída. Normalmente isso significa que outro aplicativo — Zoom, Discord, Teams, um jogo ou outra aba do navegador — está usando o microfone, ou o serviço de áudio do sistema travou.<br><br>Feche outros aplicativos que usam o microfone e tente novamente, ou recarregue a página. Se continuar, reinicie o navegador.',
        un_retry: 'Tentar novamente',
        os_title: 'Microfone bloqueado pelo sistema',
        os_intro: 'O navegador permitiu o microfone, mas o sistema operacional está bloqueando. Ative nas configurações do sistema:',
        os_win: '<strong>Windows:</strong> Iniciar → Configurações → Privacidade e segurança → Microfone → ative <em>Acesso ao microfone</em> e <em>Permitir que os apps acessem o microfone</em>. Depois volte e clique no botão abaixo.',
        os_mac: '<strong>macOS:</strong> Ajustes do Sistema → Privacidade e Segurança → Microfone → ative o acesso para o seu navegador. Depois clique no botão abaixo.',
        os_retry: 'Ativei — tentar novamente',
        os_reload: 'Recarregar a página',
        os_reload_hint: 'Já ativou mas continua bloqueado? A mudança normalmente só vale depois de recarregar a página — use o botão abaixo. Se você usa antivírus com proteção de microfone (ex.: Kaspersky), permita este site nele também.',
        chrome_android: '<strong>Chrome no Android:</strong> toque no ícone de cadeado (ou ⓘ) à esquerda do endereço → <em>Permissões</em> → <em>Microfone</em> → <em>Permitir</em>. Depois volte a esta aba — ela continua sozinha, sem recarregar.',
        chrome_bounced: '<strong>O Chrome não abriu</strong> — provavelmente não está instalado neste aparelho. Copie o link abaixo em outro navegador e permita o microfone lá, ou use o modo arquivo.',
        status_blocked: 'Microfone para este site: <strong>bloqueado</strong>.',
        status_waiting: 'Microfone: o navegador vai perguntar de novo — toque no botão do microfone.',
        status_granted: 'Microfone permitido — iniciando…',
        status_still: 'Ainda bloqueado. O interruptor fica no navegador, não nesta página — siga os passos acima; assim que estiver ligado, tudo continua sozinho.',
        check: 'Já liguei — verificar',
        wait_hint: 'O navegador está pedindo permissão para o microfone — encontre o aviso (ao lado da barra de endereço, ou na parte de baixo da tela no celular) e escolha Permitir.',
        wait_title: 'Ainda esperando sua resposta',
        wait_body: 'O pedido de microfone do navegador está aberto há um tempo, e nada continua até que seja respondido. Escolha <em>Permitir</em> nele. Se não vê nenhum pedido, recarregue a página e toque no botão de novo — ou use o modo arquivo abaixo.'
    },
    de: {
        title: 'Mikrofonzugriff erlauben',
        intro: 'Ohne deine Erlaubnis können wir nicht aufnehmen. So aktivierst du den Zugriff im Browser:',
        chrome: '<strong>Chrome, Edge, Brave, Opera:</strong> Klicke auf das Schloss- oder Einstellungssymbol links neben der Adressleiste → finde <em>Mikrofon</em> → wechsle auf <em>Zulassen</em>. Lade dann die Seite neu.',
        yandex: '<strong>Yandex Browser:</strong> Klicke auf das farbige Schild-Symbol (Protect) links neben der Adressleiste → <em>Details</em> → <em>Site-Einstellungen</em> → <em>Mikrofonnutzung</em> → <em>Zulassen</em>, dann neu laden. <strong>Alternative:</strong> ≡ Menü → Einstellungen → Websites → Erweiterte Site-Einstellungen → Mikrofon → diese Site finden → Zulassen.',
        yandex_mobile: '<strong>Yandex Browser am Handy:</strong> die mobile Version hat keine Berechtigungseinstellungen pro Website, die Sperre lässt sich von innen nicht aufheben. Zwei Wege: diese Seite in Chrome öffnen (Schaltfläche unten) und das Mikrofon dort erlauben — oder im Yandex Browser bleiben und sein Website-Gedächtnis löschen: ≡ → Einstellungen → Datenschutz → Daten löschen → <em>Website-Daten</em> ankreuzen → Löschen. Danach fragt er erneut (du wirst von Websites abgemeldet).',
        open_chrome: 'In Chrome öffnen',
        firefox: '<strong>Firefox:</strong> Klicke auf das Mikrofon-Symbol in der Adressleiste → Sperre aufheben → Seite neu laden, dann <em>Zulassen</em> im Popup klicken.',
        safari: '<strong>Safari:</strong> Safari → Einstellungen → Websites → Mikrofon → diese Site auf <em>Zulassen</em> setzen. iPhone/iPad: Einstellungen → Safari → Mikrofon.',
        samsung: '<strong>Samsung Internet:</strong> Schloss-Symbol in der Adressleiste antippen → <em>Berechtigungen</em> → <em>Mikrofon</em> → <em>Zulassen</em>, dann Seite neu laden. <strong>Oder:</strong> Menü (≡) → Einstellungen → Websites und Downloads → Website-Berechtigungen → Mikrofon → diese Site → Zulassen.',
        inapp: '<strong>Das ist ein In-App-Browser.</strong> Er hat keine Adressleiste, hier lässt sich das Mikrofon also nicht freigeben. Öffnen Sie die Seite in <b>Chrome</b>, <b>Safari</b> oder einem normalen Browser — der Link steht unten — und erlauben Sie das Mikrofon dort. <b>Oder</b> nutzen Sie unten den Dateimodus: mit dem Handy aufnehmen und die Audiodatei hochladen, ganz ohne Mikrofonfreigabe.',
        stuck_title: 'Immer noch blockiert?',
        stuck_body: 'Die Berechtigung ist noch nicht aktiv. Der schnellste Weg ist der <strong>Datei-Modus</strong> unten — auf dem Handy aufnehmen oder eine Audiodatei hochladen, Whisper transkribiert lokal ohne Mikrofon.',
        fallback_label: 'Zum Datei-Modus wechseln',
        retry: 'Habe erlaubt — erneut versuchen',
        close: 'Schließen',
        nf_title: 'Kein Mikrofon erkannt',
        nf_body: 'Schließe ein Mikrofon, USB-Headset oder Bluetooth-Gerät an und aktualisiere die Liste. Bei Laptops prüfe, ob das eingebaute Mikrofon nicht in den Systemeinstellungen deaktiviert ist.',
        nf_refresh: 'Aktualisieren und erneut versuchen',
        busy_title: 'Mikrofon belegt',
        sm_title: 'Das iPhone hat das Mikrofon nicht eingeschaltet',
        busy_body: 'Ein anderer Tab oder eine App nutzt das Mikrofon (Zoom, Discord, Teams, ein anderer Browser-Tab…). Schließe es und versuche es erneut.',
        busy_retry: 'Erneut versuchen',
        be_title: 'Mikrofon in diesem Browser nicht verfügbar',
        be_body: 'Dieser Browser blockiert den Mikrofonzugriff — häufig bei Kinder-Tablets, verwalteten Geräten und Apps, die Webseiten in sich selbst öffnen. Zum Aufnehmen öffne <strong>timbrica.com</strong> in Chrome, Safari, Firefox oder Edge.',
        be_kids_body: 'Dies ist der Amazon-Kids-Browser — er blockiert das Mikrofon für alle Websites, und weder Kind noch Eltern können es hier aktivieren. Bitte deine Eltern, diese Seite im <strong>Silk</strong>-Browser des Elternprofils zu öffnen — oder mach hier mit einer hochgeladenen Datei weiter.',
        un_title: 'Das Mikrofon reagiert nicht',
        un_body: 'Der Browser hat die Mikrofon-Anfrage gestartet, sie wurde aber nie abgeschlossen. Meist hält eine andere App — Zoom, Discord, Teams, ein Spiel oder ein anderer Browser-Tab — das Mikrofon belegt, oder der System-Audiodienst hängt.<br><br>Schließe andere Apps, die das Mikrofon nutzen, und versuche es erneut oder lade die Seite neu. Wenn es weiterhin auftritt, starte den Browser neu.',
        un_retry: 'Erneut versuchen',
        os_title: 'Mikrofon vom System blockiert',
        os_intro: 'Der Browser hat das Mikrofon erlaubt, aber das Betriebssystem blockiert es. Aktiviere es in den Systemeinstellungen:',
        os_win: '<strong>Windows:</strong> Start → Einstellungen → Datenschutz &amp; Sicherheit → Mikrofon → aktiviere <em>Mikrofonzugriff</em> und <em>Apps Zugriff auf das Mikrofon erlauben</em>. Komm dann zurück und klicke auf die Schaltfläche unten.',
        os_mac: '<strong>macOS:</strong> Systemeinstellungen → Datenschutz &amp; Sicherheit → Mikrofon → aktiviere den Zugriff für deinen Browser. Klicke dann auf die Schaltfläche unten.',
        os_retry: 'Habe es aktiviert — erneut versuchen',
        os_reload: 'Seite neu laden',
        os_reload_hint: 'Schon aktiviert, aber immer noch blockiert? Die Änderung greift meist erst nach dem Neuladen der Seite — nutze den Button unten. Wenn du ein Antivirus mit Mikrofon-Schutz verwendest (z. B. Kaspersky), erlaube diese Site auch dort.',
        chrome_android: '<strong>Chrome auf Android:</strong> tippe auf das Schloss- (oder ⓘ-) Symbol links neben der Adresse → <em>Berechtigungen</em> → <em>Mikrofon</em> → <em>Zulassen</em>. Dann zurück zu diesem Tab — es geht von selbst weiter, kein Neuladen nötig.',
        chrome_bounced: '<strong>Chrome hat sich nicht geöffnet</strong> — wahrscheinlich ist es auf diesem Gerät nicht installiert. Kopiere den Link unten in einen anderen Browser und erlaube dort das Mikrofon, oder nutze den Datei-Modus.',
        status_blocked: 'Mikrofon für diese Seite: <strong>blockiert</strong>.',
        status_waiting: 'Mikrofon: der Browser fragt erneut — drücke die Mikrofon-Taste.',
        status_granted: 'Mikrofon erlaubt — es geht los…',
        status_still: 'Immer noch blockiert. Der Schalter sitzt im Browser, nicht auf dieser Seite — folge den Schritten oben; sobald er an ist, geht es von selbst weiter.',
        check: 'Habe es eingeschaltet — prüfen',
        wait_hint: 'Der Browser fragt nach der Mikrofon-Berechtigung — finde die Abfrage (neben der Adressleiste, auf dem Handy am unteren Bildschirmrand) und wähle Zulassen.',
        wait_title: 'Wir warten noch auf deine Antwort',
        wait_body: 'Die Mikrofon-Abfrage des Browsers ist schon eine Weile offen, und nichts geht weiter, bis sie beantwortet ist. Wähle dort <em>Zulassen</em>. Siehst du keine Abfrage, lade die Seite neu und drücke die Taste erneut — oder nutze den Datei-Modus unten.'
    },
    es: {
        title: 'Permitir el acceso al micrófono',
        intro: 'No podemos grabar sin tu permiso. Así se activa el acceso en tu navegador:',
        chrome: '<strong>Chrome, Edge, Brave, Opera:</strong> haz clic en el icono de candado o de ajustes a la izquierda de la barra de direcciones → busca <em>Micrófono</em> → cámbialo a <em>Permitir</em>. Luego recarga la página.',
        yandex: '<strong>Yandex Browser:</strong> haz clic en el icono de escudo de color (Protect) a la izquierda de la barra de direcciones → <em>Detalles</em> → <em>Configuración del sitio</em> → <em>Uso del micrófono</em> → <em>Permitir</em>, luego recarga. <strong>Alternativa:</strong> menú ≡ → Configuración → Sitios → Configuración avanzada de sitios → Micrófono → encuentra este sitio → Permitir.',
        yandex_mobile: '<strong>Yandex Browser en el móvil:</strong> la versión móvil no tiene ajustes de permisos por sitio, así que el bloqueo no se puede quitar desde dentro. Dos salidas: abrir esta página en Chrome (botón abajo) y permitir el micrófono allí, o quedarte en Yandex Browser y borrar su memoria de sitios: ≡ → Configuración → Privacidad → Borrar datos → marcar <em>Datos de sitios</em> → Borrar. Después volverá a preguntar (se cerrará tu sesión en los sitios).',
        open_chrome: 'Abrir en Chrome',
        firefox: '<strong>Firefox:</strong> haz clic en el icono del micrófono en la barra de direcciones → quita el bloqueo → recarga la página y haz clic en <em>Permitir</em> cuando se solicite.',
        safari: '<strong>Safari:</strong> Safari → Ajustes → Sitios web → Micrófono → establece este sitio en <em>Permitir</em>. En iPhone/iPad: Ajustes → Safari → Micrófono.',
        samsung: '<strong>Samsung Internet:</strong> toca el icono de candado en la barra de direcciones → <em>Permisos</em> → <em>Micrófono</em> → <em>Permitir</em>, luego recarga. <strong>O:</strong> Menú (≡) → Ajustes → Sitios y descargas → Permisos del sitio → Micrófono → busca este sitio → Permitir.',
        inapp: '<strong>Este es un navegador integrado en una app.</strong> No tiene barra de direcciones, así que aquí no hay dónde activar el micrófono. Abre esta página en <b>Chrome</b>, <b>Safari</b> u otro navegador normal — el enlace está abajo — y permite el micrófono allí. <b>O</b> usa el modo de archivo de abajo: graba con el móvil y sube el audio, sin permiso de micrófono.',
        stuck_title: '¿Sigue bloqueado?',
        stuck_body: 'Parece que el permiso aún no está activo. La vía más rápida es el <strong>modo archivo</strong> de abajo: graba con el móvil o sube un archivo de audio, y Whisper lo transcribe localmente sin micrófono.',
        fallback_label: 'Cambiar al modo archivo',
        retry: 'Ya lo permití — reintentar',
        close: 'Cerrar',
        nf_title: 'No se detectó ningún micrófono',
        nf_body: 'Conecta un micrófono, auriculares USB o un dispositivo Bluetooth y actualiza la lista. En portátiles, comprueba que el micrófono integrado no esté desactivado en la configuración del sistema.',
        nf_refresh: 'Actualizar y reintentar',
        busy_title: 'El micrófono está ocupado',
        sm_title: 'El iPhone no encendió el micrófono',
        busy_body: 'Otra pestaña o aplicación está usando el micrófono (Zoom, Discord, Teams, otra pestaña del navegador…). Ciérrala y reinténtalo.',
        busy_retry: 'Reintentar',
        be_title: 'Micrófono no disponible en este navegador',
        be_body: 'Este navegador bloquea el acceso al micrófono — es habitual en tabletas infantiles, dispositivos gestionados y apps que abren páginas web dentro de sí mismas. Para grabar, abre <strong>timbrica.com</strong> en Chrome, Safari, Firefox o Edge.',
        be_kids_body: 'Este es el navegador Amazon Kids: bloquea el micrófono para todos los sitios y aquí no hay forma de activarlo (ni para el niño ni para los padres). Pide a un adulto que abra esta página en el navegador <strong>Silk</strong> desde el perfil de los padres, o continúa aquí subiendo un archivo.',
        un_title: 'El micrófono no responde',
        un_body: 'El navegador inició la solicitud del micrófono pero nunca se completó. Normalmente esto significa que otra aplicación — Zoom, Discord, Teams, un juego u otra pestaña del navegador — está ocupando el micrófono, o el servicio de audio del sistema se ha bloqueado.<br><br>Cierra otras aplicaciones que usen el micrófono y reinténtalo, o recarga la página. Si continúa, reinicia el navegador.',
        un_retry: 'Reintentar',
        os_title: 'Micrófono bloqueado por el sistema',
        os_intro: 'El navegador permitió el micrófono, pero el sistema operativo lo está bloqueando. Actívalo en la configuración del sistema:',
        os_win: '<strong>Windows:</strong> Inicio → Configuración → Privacidad y seguridad → Micrófono → activa <em>Acceso al micrófono</em> y <em>Permitir que las aplicaciones accedan al micrófono</em>. Luego vuelve y haz clic en el botón de abajo.',
        os_mac: '<strong>macOS:</strong> Ajustes del Sistema → Privacidad y seguridad → Micrófono → activa el acceso para tu navegador. Luego haz clic en el botón de abajo.',
        os_retry: 'Ya lo activé — reintentar',
        os_reload: 'Recargar la página',
        os_reload_hint: '¿Ya lo activaste pero sigue bloqueado? El cambio normalmente solo surte efecto tras recargar la página — usa el botón de abajo. Si usas un antivirus con escudo de micrófono (p. ej. Kaspersky), permite este sitio también allí.',
        chrome_android: '<strong>Chrome en Android:</strong> toca el icono del candado (o ⓘ) a la izquierda de la dirección → <em>Permisos</em> → <em>Micrófono</em> → <em>Permitir</em>. Luego vuelve a esta pestaña: continúa sola, sin recargar.',
        chrome_bounced: '<strong>Chrome no se abrió</strong>: probablemente no está instalado en este dispositivo. Copia el enlace de abajo en otro navegador y permite el micrófono allí, o usa el modo archivo.',
        status_blocked: 'Micrófono para este sitio: <strong>bloqueado</strong>.',
        status_waiting: 'Micrófono: el navegador volverá a preguntar; pulsa el botón del micrófono.',
        status_granted: 'Micrófono permitido, iniciando…',
        status_still: 'Sigue bloqueado. El interruptor está en el navegador, no en esta página: sigue los pasos de arriba; en cuanto esté activado, todo continúa solo.',
        check: 'Ya lo activé, comprobar',
        wait_hint: 'El navegador está pidiendo permiso para el micrófono: busca su aviso (junto a la barra de direcciones o, en el móvil, en la parte inferior de la pantalla) y elige Permitir.',
        wait_title: 'Seguimos esperando tu respuesta',
        wait_body: 'La solicitud de micrófono del navegador lleva un rato abierta y nada continúa hasta que se responda. Elige <em>Permitir</em> en ella. Si no ves ninguna solicitud, recarga la página y pulsa el botón otra vez, o usa el modo archivo de abajo.'
    },
    fr: {
        title: 'Autoriser l\'accès au microphone',
        intro: 'Nous ne pouvons pas enregistrer sans votre autorisation. Voici comment l\'activer dans votre navigateur :',
        chrome: '<strong>Chrome, Edge, Brave, Opera :</strong> cliquez sur l\'icône de cadenas ou de réglages à gauche de la barre d\'adresse → trouvez <em>Microphone</em> → passez sur <em>Autoriser</em>. Puis rechargez la page.',
        yandex: '<strong>Yandex Browser :</strong> cliquez sur l\'icône de bouclier coloré (Protect) à gauche de la barre d\'adresse → <em>Détails</em> → <em>Paramètres du site</em> → <em>Utilisation du microphone</em> → <em>Autoriser</em>, puis rechargez. <strong>Alternative :</strong> menu ≡ → Paramètres → Sites → Paramètres avancés des sites → Microphone → trouvez ce site → Autoriser.',
        yandex_mobile: '<strong>Yandex Browser sur téléphone :</strong> la version mobile n’a pas de réglages d’autorisations par site, le blocage ne peut donc pas être levé depuis l’intérieur. Deux issues : ouvrir cette page dans Chrome (bouton ci-dessous) et y autoriser le microphone — ou rester dans Yandex Browser et effacer sa mémoire des sites : ≡ → Paramètres → Confidentialité → Effacer les données → cocher <em>Données des sites</em> → Effacer. Il redemandera ensuite (vous serez déconnectée des sites).',
        open_chrome: 'Ouvrir dans Chrome',
        firefox: '<strong>Firefox :</strong> cliquez sur l\'icône du microphone dans la barre d\'adresse → retirez le blocage → rechargez la page, puis cliquez sur <em>Autoriser</em> à l\'invite.',
        safari: '<strong>Safari :</strong> Safari → Réglages → Sites web → Microphone → réglez ce site sur <em>Autoriser</em>. Sur iPhone/iPad : Réglages → Safari → Microphone.',
        samsung: '<strong>Samsung Internet :</strong> touchez l\'icône de cadenas dans la barre d\'adresse → <em>Autorisations</em> → <em>Microphone</em> → <em>Autoriser</em>, puis rechargez. <strong>Ou :</strong> Menu (≡) → Paramètres → Sites et téléchargements → Autorisations du site → Microphone → ce site → Autoriser.',
        inapp: '<strong>Ceci est un navigateur intégré à une application.</strong> Il n\'a pas de barre d\'adresse, impossible donc d\'y activer le microphone. Ouvrez cette page dans <b>Chrome</b>, <b>Safari</b> ou un autre navigateur classique — le lien est ci-dessous — et autorisez le microphone là-bas. <b>Ou</b> utilisez le mode fichier ci-dessous : enregistrez sur votre téléphone et envoyez l\'audio, sans autorisation micro.',
        stuck_title: 'Toujours bloqué ?',
        stuck_body: 'Il semble que l\'autorisation ne soit pas encore activée. Le plus rapide est le <strong>mode fichier</strong> ci-dessous — enregistrez sur votre téléphone ou téléversez un fichier audio, et Whisper le transcrit localement sans microphone.',
        fallback_label: 'Passer en mode fichier',
        retry: 'Je l\'ai autorisé — réessayer',
        close: 'Fermer',
        nf_title: 'Aucun microphone détecté',
        nf_body: 'Branchez un microphone, un casque USB ou un appareil Bluetooth et actualisez la liste. Sur les ordinateurs portables, vérifiez que le microphone intégré n\'est pas désactivé dans les paramètres système.',
        nf_refresh: 'Actualiser et réessayer',
        busy_title: 'Le microphone est occupé',
        sm_title: 'L’iPhone n’a pas activé le micro',
        busy_body: 'Un autre onglet ou une autre application utilise le microphone (Zoom, Discord, Teams, un autre onglet du navigateur…). Fermez-le et réessayez.',
        busy_retry: 'Réessayer',
        be_title: 'Microphone indisponible dans ce navigateur',
        be_body: 'Ce navigateur bloque l\'accès au microphone — fréquent sur les tablettes pour enfants, les appareils gérés et les applications qui ouvrent des pages web en leur sein. Pour enregistrer, ouvrez <strong>timbrica.com</strong> dans Chrome, Safari, Firefox ou Edge.',
        be_kids_body: 'Ceci est le navigateur Amazon Kids : il bloque le micro pour tous les sites, et rien ici (ni enfant ni parent) ne permet de l\'activer. Demandez à un parent d\'ouvrir cette page dans le navigateur <strong>Silk</strong> depuis le profil parent — ou continuez ici en important un fichier.',
        un_title: 'Le microphone ne répond pas',
        un_body: 'Le navigateur a lancé la demande de microphone mais elle ne s\'est jamais terminée. Cela signifie généralement qu\'une autre application — Zoom, Discord, Teams, un jeu ou un autre onglet du navigateur — occupe le microphone, ou que le service audio du système est bloqué.<br><br>Fermez les autres applications qui utilisent le microphone et réessayez, ou rechargez la page. Si cela persiste, redémarrez le navigateur.',
        un_retry: 'Réessayer',
        os_title: 'Microphone bloqué par le système',
        os_intro: 'Le navigateur a autorisé le microphone, mais le système d\'exploitation le bloque. Activez-le dans les paramètres système :',
        os_win: '<strong>Windows :</strong> Démarrer → Paramètres → Confidentialité et sécurité → Microphone → activez <em>Accès au microphone</em> et <em>Autoriser les applications à accéder au microphone</em>. Revenez ensuite et cliquez sur le bouton ci-dessous.',
        os_mac: '<strong>macOS :</strong> Réglages Système → Confidentialité et sécurité → Microphone → activez l\'accès pour votre navigateur. Cliquez ensuite sur le bouton ci-dessous.',
        os_retry: 'Je l\'ai activé — réessayer',
        os_reload: 'Recharger la page',
        os_reload_hint: 'Déjà activé mais toujours bloqué ? Le changement ne prend généralement effet qu\'après le rechargement de la page — utilisez le bouton ci-dessous. Si vous utilisez un antivirus avec bouclier micro (p. ex. Kaspersky), autorisez aussi ce site dedans.',
        chrome_android: '<strong>Chrome sur Android :</strong> touchez l’icône du cadenas (ou ⓘ) à gauche de l’adresse → <em>Autorisations</em> → <em>Micro</em> → <em>Autoriser</em>. Revenez ensuite sur cet onglet : ça reprend tout seul, sans recharger.',
        chrome_bounced: '<strong>Chrome ne s’est pas ouvert</strong> : il n’est probablement pas installé sur cet appareil. Copiez le lien ci-dessous dans un autre navigateur et autorisez-y le micro, ou utilisez le mode fichier.',
        status_blocked: 'Micro pour ce site : <strong>bloqué</strong>.',
        status_waiting: 'Micro : le navigateur redemandera — appuyez sur le bouton du micro.',
        status_granted: 'Micro autorisé — démarrage…',
        status_still: 'Toujours bloqué. L’interrupteur est dans le navigateur, pas sur cette page : suivez les étapes ci-dessus ; dès qu’il est activé, tout reprend tout seul.',
        check: 'Je l’ai activé — vérifier',
        wait_hint: 'Le navigateur demande l’autorisation du micro : repérez sa fenêtre (à côté de la barre d’adresse, ou en bas de l’écran sur téléphone) et choisissez Autoriser.',
        wait_title: 'Nous attendons toujours votre réponse',
        wait_body: 'La demande de micro du navigateur est ouverte depuis un moment, et rien ne continue tant qu’on n’y répond pas. Choisissez-y <em>Autoriser</em>. Si vous ne voyez aucune demande, rechargez la page et appuyez à nouveau sur le bouton — ou utilisez le mode fichier ci-dessous.'
    },
    uk: {
        title: 'Дозвольте доступ до мікрофона',
        intro: 'Без вашого дозволу ми не можемо записувати. Як увімкнути доступ у браузері:',
        chrome: '<strong>Chrome, Edge, Brave, Opera:</strong> натисніть на значок замка або налаштувань ліворуч від адресного рядка → знайдіть пункт <em>Мікрофон</em> → переключіть на <em>Дозволити</em>. Потім оновіть сторінку.',
        yandex: '<strong>Яндекс Браузер:</strong> натисніть на кольоровий значок щита (Protect) ліворуч від адресного рядка → <em>Детальніше</em> → <em>Налаштування сайту</em> → <em>Використання мікрофона</em> → <em>Дозволити</em>, потім оновіть сторінку. <strong>Альтернатива:</strong> ≡ меню → Налаштування → Сайти → Розширені налаштування сайтів → Мікрофон → знайти цей сайт → Дозволено.',
        yandex_mobile: '<strong>Яндекс Браузер на телефоні:</strong> у мобільній версії немає налаштувань дозволів для сайтів — зняти заборону зсередини неможливо. Два виходи: відкрити цю сторінку в Chrome (кнопка нижче) і дозволити мікрофон там — або залишитися в Яндекс Браузері й очистити його пам\'ять про сайти: ≡ → Налаштування → Конфіденційність → Очистити дані → відмітити <em>Дані сайтів</u> → Очистити. Після цього він запитає знову (майте на увазі: ви вийдете з акаунтів на сайтах).',
        open_chrome: 'Відкрити в Chrome',
        firefox: '<strong>Firefox:</strong> натисніть на значок мікрофона в адресному рядку → зніміть блокування → оновіть сторінку, потім натисніть <em>Дозволити</em> у спливаючому вікні.',
        safari: '<strong>Safari:</strong> Safari → Налаштування → Веб-сайти → Мікрофон → встановіть <em>Дозволити</em> для цього сайту. На iPhone/iPad: Налаштування → Safari → Мікрофон.',
        samsung: '<strong>Samsung Internet:</strong> натисніть на значок замка в адресному рядку → <em>Дозволи</em> → <em>Мікрофон</em> → <em>Дозволити</em>, потім оновіть сторінку. <strong>Або:</strong> Меню (≡) → Налаштування → Сайти та завантаження → Дозволи для сайтів → Мікрофон → знайдіть цей сайт → Дозволити.',
        inapp: '<strong>Це вбудований браузер застосунку.</strong> У ньому немає адресного рядка, тому увімкнути мікрофон тут неможливо. Відкрийте сторінку в <b>Chrome</b>, <b>Яндекс Браузері</b> або іншому звичайному браузері — посилання нижче — і дозвольте мікрофон там. <b>Або</b> скористайтеся файловим режимом нижче: запишіть на телефон і завантажте аудіо, дозвіл мікрофона не потрібен.',
        stuck_title: 'Досі заблоковано?',
        stuck_body: 'Схоже, дозвіл ще не надано. Швидкий шлях — <strong>файловий режим</strong> нижче: запишіть на телефоні або завантажте аудіо, і Whisper розшифрує локально, без мікрофона.',
        fallback_label: 'Перейти до файлового режиму',
        retry: 'Я дозволив — спробувати знову',
        close: 'Закрити',
        nf_title: 'Мікрофон не знайдено',
        nf_body: 'Підключіть мікрофон, USB-гарнітуру або Bluetooth-пристрій і оновіть список. На ноутбуках перевірте, що вбудований мікрофон не вимкнено в налаштуваннях системи.',
        nf_refresh: 'Оновити і повторити',
        be_title: 'Мікрофон недоступний у цьому браузері',
        be_body: 'Цей браузер блокує доступ до мікрофона — так буває на дитячих планшетах, корпоративних пристроях і в застосунках, що відкривають веб-сторінки всередині себе. Щоб записати звук, відкрийте <strong>timbrica.com</strong> в Chrome, Safari, Firefox або Edge.',
        be_kids_body: 'Це дитячий браузер Amazon Kids — він блокує мікрофон для всіх сайтів, і увімкнути його тут не можна ні дитині, ні батьку. Попросіть батька відкрити цю сторінку в браузері <strong>Silk</strong> з батьківського профілю — або продовжіть тут, завантаживши файл.',
        busy_title: 'Мікрофон зайнятий',
        sm_title: 'iPhone не ввімкнув мікрофон',
        busy_body: 'Інша вкладка або застосунок використовує мікрофон (Zoom, Discord, Teams, інша вкладка браузера…). Закрийте його і спробуйте знову.',
        busy_retry: 'Спробувати знову',
        un_title: 'Мікрофон не відповідає',
        un_body: 'Браузер почав запитувати доступ до мікрофона, але запит так і не завершився. Зазвичай це означає, що мікрофон зайнятий іншим застосунком — Zoom, Discord, Teams, грою або іншою вкладкою браузера — або зависла системна служба звуку.<br><br>Закрийте інші застосунки, що використовують мікрофон, і спробуйте знову або оновіть сторінку. Якщо повторюється — перезапустіть браузер.',
        un_retry: 'Спробувати знову',
        os_title: 'Мікрофон заблоковано системою',
        os_intro: 'Браузер дозволив доступ до мікрофона, але його блокує операційна система. Увімкніть доступ у налаштуваннях системи:',
        os_win: '<strong>Windows:</strong> Пуск → Параметри → Конфіденційність і захист → Мікрофон → увімкніть <em>Доступ до мікрофона</em> і <em>Дозволити застосункам доступ до мікрофона</em>. Потім поверніться і натисніть кнопку нижче.',
        os_mac: '<strong>macOS:</strong> Системні налаштування → Конфіденційність і безпека → Мікрофон → увімкніть доступ для вашого браузера. Потім натисніть кнопку нижче.',
        os_retry: 'Я увімкнув доступ — спробувати знову',
        os_reload: 'Перезавантажити сторінку',
        os_reload_hint: 'Вже увімкнули, але доступ досі заблоковано? Зміна зазвичай набирає чинності лише після перезавантаження сторінки — натисніть кнопку нижче. Якщо у вас антивірус із захистом мікрофона (наприклад, Kaspersky), дозвольте доступ до цього сайту і в ньому.',
        chrome_android: '<strong>Chrome на Android:</strong> торкніться значка замка (або ⓘ) ліворуч від адреси → <em>Дозволи</em> → <em>Мікрофон</em> → <em>Дозволити</em>. Потім поверніться на цю вкладку — усе продовжиться саме, перезавантажувати не треба.',
        chrome_bounced: '<strong>Chrome не відкрився</strong> — найімовірніше, його немає на цьому пристрої. Скопіюйте посилання нижче в будь-який інший браузер і дозвольте мікрофон там — або скористайтеся файловим режимом.',
        status_blocked: 'Мікрофон для цього сайту: <strong>заборонено</strong>.',
        status_waiting: 'Мікрофон: браузер запитає знову — натисніть кнопку мікрофона.',
        status_granted: 'Мікрофон дозволено — запускаємо…',
        status_still: 'Досі заборонено. Перемикач — у браузері, а не на цій сторінці: пройдіть кроки вище; щойно він увімкнений, усе продовжиться саме.',
        check: 'Я увімкнув — перевірити',
        wait_hint: 'Браузер запитує дозвіл на мікрофон — знайдіть його запит (біля адресного рядка, а на телефоні — внизу екрана) і виберіть «Дозволити».',
        wait_title: 'Досі чекаємо на вашу відповідь',
        wait_body: 'Запит браузера на мікрофон відкритий уже давно, і поки на нього не відповісти, нічого не продовжиться. Виберіть у ньому <em>Дозволити</em>. Якщо жодного запиту не видно — перезавантажте сторінку й натисніть кнопку ще раз, або скористайтеся файловим режимом нижче.'
    },
};

/**
 * Device-picker copy per locale. Used by attachDevicePicker() so that the
 * picker UI is self-contained — tools don't need to add lang keys for "Choose
 * microphone" / "System default" / "Microphone 2". `device_n` is the fallback
 * label when MediaDeviceInfo.label is empty (e.g. before the first permission
 * grant): we substitute {n} with the 1-based device index.
 */
var PICKER = {
    en: { title: 'Microphone source',  defaultLabel: 'System default',   device_n: 'Microphone {n}',  change: 'Change microphone',  pill_aria: 'Current microphone: {name}. Click to change.' },
    ru: { title: 'Источник микрофона', defaultLabel: 'По умолчанию',      device_n: 'Микрофон {n}',     change: 'Сменить микрофон',    pill_aria: 'Текущий микрофон: {name}. Нажмите, чтобы сменить.' },
    ko: { title: '마이크 입력',          defaultLabel: '시스템 기본값',       device_n: '마이크 {n}',         change: '마이크 변경',           pill_aria: '현재 마이크: {name}. 변경하려면 클릭하세요.' },
    ar: { title: 'مصدر الميكروفون',     defaultLabel: 'الإعداد الافتراضي للنظام', device_n: 'ميكروفون {n}',   change: 'تغيير الميكروفون',     pill_aria: 'الميكروفون الحالي: {name}. اضغط للتغيير.' },
    id: { title: 'Sumber mikrofon',     defaultLabel: 'Default sistem',     device_n: 'Mikrofon {n}',     change: 'Ganti mikrofon',       pill_aria: 'Mikrofon saat ini: {name}. Klik untuk mengganti.' },
    pt: { title: 'Fonte do microfone',  defaultLabel: 'Padrão do sistema',  device_n: 'Microfone {n}',    change: 'Trocar microfone',     pill_aria: 'Microfone atual: {name}. Clique para trocar.' },
    de: { title: 'Mikrofonquelle',      defaultLabel: 'Systemstandard',     device_n: 'Mikrofon {n}',     change: 'Mikrofon wechseln',    pill_aria: 'Aktuelles Mikrofon: {name}. Klicke zum Wechseln.' },
    es: { title: 'Fuente del micrófono',defaultLabel: 'Predeterminado del sistema', device_n: 'Micrófono {n}', change: 'Cambiar micrófono', pill_aria: 'Micrófono actual: {name}. Haz clic para cambiar.' },
    fr: { title: 'Source du microphone',defaultLabel: 'Par défaut du système', device_n: 'Microphone {n}', change: 'Changer de micro',    pill_aria: 'Microphone actuel : {name}. Cliquez pour changer.' },
    uk: { title: 'Джерело мікрофона', defaultLabel: 'Системний за замовчуванням', device_n: 'Мікрофон {n}', change: 'Змінити мікрофон', pill_aria: 'Поточний мікрофон: {name}. Натисніть, щоб змінити.' },
    tr: { title: 'Mikrofon kaynağı', defaultLabel: 'Sistem varsayılanı', device_n: 'Mikrofon {n}', change: 'Mikrofonu değiştir', pill_aria: 'Şu anki mikrofon: {name}. Değiştirmek için tıklayın.' }
};

/**
 * Silence-watchdog copy per locale (watchSilence()). Same self-contained
 * principle as PICKER: no per-tool lang keys. The "change mic" affordance
 * reuses PICKER[locale].change.
 *
 * `yet` — nothing has come from the input since it opened. A gated microphone
 * whose owner has not spoken yet looks exactly like a dead one, so the line
 * says «yet» and «if you are already speaking» instead of accusing the device.
 * `live` — the device fell silent after it had carried sound, and a fact says
 * why (the system muted the track, or the device went away).
 */
var SILENCE = {
    en: { yet: 'No sound is coming from the microphone yet. If you are already speaking, check that the right mic is selected and it isn\'t muted.',
          live: 'We can\'t hear you — the microphone is recording silence. Check that the right mic is selected and it isn\'t muted.' },
    ru: { yet: 'С микрофона пока не приходит звук. Если вы уже говорите — проверьте, что выбран нужный микрофон и он не выключен.',
          live: 'Мы вас не слышим — микрофон пишет тишину. Проверьте, что выбран нужный микрофон и он не выключен.' },
    ko: { yet: '마이크에서 아직 소리가 들어오지 않습니다. 이미 말하고 있다면 올바른 마이크가 선택되어 있고 음소거가 아닌지 확인하세요.',
          live: '소리가 들리지 않습니다 — 마이크가 무음을 녹음하고 있습니다. 올바른 마이크가 선택되어 있고 음소거가 아닌지 확인하세요.' },
    ar: { yet: 'لم يصل أي صوت من الميكروفون بعد. إذا كنت تتحدث بالفعل، فتأكد من اختيار الميكروفون الصحيح وأنه غير مكتوم.',
          live: 'لا يمكننا سماعك — الميكروفون يسجّل صمتًا. تأكد من اختيار الميكروفون الصحيح وأنه غير مكتوم.' },
    id: { yet: 'Belum ada suara dari mikrofon. Jika Anda sudah berbicara, pastikan mikrofon yang benar dipilih dan tidak dibisukan.',
          live: 'Kami tidak dapat mendengar Anda — mikrofon merekam keheningan. Pastikan mikrofon yang benar dipilih dan tidak dibisukan.' },
    pt: { yet: 'Ainda não chega som do microfone. Se você já está falando, verifique se o microfone certo está selecionado e não está mudo.',
          live: 'Não estamos ouvindo você — o microfone está gravando silêncio. Verifique se o microfone certo está selecionado e não está mudo.' },
    de: { yet: 'Vom Mikrofon kommt noch kein Ton. Wenn Sie schon sprechen, prüfen Sie, ob das richtige Mikrofon gewählt und nicht stummgeschaltet ist.',
          live: 'Wir hören Sie nicht — das Mikrofon nimmt Stille auf. Prüfen Sie, ob das richtige Mikrofon gewählt und nicht stummgeschaltet ist.' },
    es: { yet: 'Todavía no llega sonido del micrófono. Si ya estás hablando, verifica que esté seleccionado el micrófono correcto y que no esté silenciado.',
          live: 'No te oímos — el micrófono está grabando silencio. Comprueba que está seleccionado el micrófono correcto y que no está silenciado.' },
    fr: { yet: 'Aucun son n\'arrive encore du microphone. Si vous parlez déjà, vérifiez que le bon micro est sélectionné et qu\'il n\'est pas coupé.',
          live: 'Nous ne vous entendons pas — le microphone enregistre du silence. Vérifiez que le bon micro est sélectionné et qu\'il n\'est pas coupé.' },
    uk: { yet: 'З мікрофона поки не надходить звук. Якщо ви вже говорите — перевірте, що вибрано потрібний мікрофон і він не вимкнений.',
          live: 'Ми вас не чуємо — мікрофон записує тишу. Перевірте, що вибрано потрібний мікрофон і він не вимкнений.' },
    tr: { yet: 'Mikrofondan henüz ses gelmiyor. Zaten konuşuyorsanız doğru mikrofonun seçili olduğunu ve kısılmadığını kontrol edin.',
          live: 'Sizi duyamıyoruz — mikrofon sessizlik kaydediyor. Doğru mikrofonun seçili olduğunu ve kısılmadığını kontrol edin.' }
};

/**
 * Audio-session-interruption copy (bindContext()). Same self-contained
 * principle as PICKER/SILENCE: no per-tool lang keys.
 *
 * Shown when the tool's AudioContext is not `running` while the mic is live —
 * i.e. the OS paused our sound and the tool would otherwise look alive but be
 * mute. Wording says what happened and what to do, in one line.
 */
var CTXPAUSE = {
    en: { paused: 'The system paused the sound — tap anywhere to bring it back.' },
    ru: { paused: 'Система поставила звук на паузу — коснитесь экрана, чтобы вернуть его.' },
    ko: { paused: '시스템이 소리를 일시정지했습니다 — 화면을 탭하면 다시 켜집니다.' },
    ar: { paused: 'أوقف النظام الصوت مؤقتًا — انقر في أي مكان لإعادته.' },
    id: { paused: 'Sistem menjeda suara — ketuk di mana saja untuk mengembalikannya.' },
    pt: { paused: 'O sistema pausou o som — toque em qualquer lugar para retomá-lo.' },
    de: { paused: 'Das System hat den Ton pausiert — tippen Sie irgendwo, um ihn zurückzuholen.' },
    es: { paused: 'El sistema pausó el sonido — toca en cualquier lugar para recuperarlo.' },
    fr: { paused: 'Le système a mis le son en pause — touchez l\'écran pour le rétablir.' },
    uk: { paused: 'Система призупинила звук — торкніться екрана, щоб відновити.' },
    tr: { paused: 'Sistem sesi duraklattı — geri getirmek için ekrana dokunun.' }
};

/**
 * Dead-input copy (watchDeadInput()). Self-contained like PICKER/SILENCE/CTXPAUSE.
 *
 * One line per CAUSE, because the four causes need four different actions and
 * a single "check your microphone" sends three quarters of the readers to the
 * wrong place. `silent` deliberately names the OS volume slider: that is where
 * a device that is alive, unmuted and delivering zeros is switched off.
 */
var DEAD = {
    en: {
        muted:  'The microphone is muted — unmute it in your system sound settings, then it will start reading.',
        ended:  'The microphone disconnected. Reconnect it or pick another input, then press start again.',
        paused: 'The system paused the sound — tap anywhere to bring the meter back.',
        silent: 'No signal from the microphone. Raise its input level in the system sound settings and check that nothing is covering it.'
    },
    ru: {
        muted:  'Микрофон выключен — включите его в настройках звука системы, и измерение пойдёт.',
        ended:  'Микрофон отключился. Подключите его снова или выберите другой вход и нажмите старт ещё раз.',
        paused: 'Система поставила звук на паузу — коснитесь экрана, чтобы вернуть измерение.',
        silent: 'Сигнала с микрофона нет. Поднимите уровень входа в настройках звука системы и проверьте, что микрофон ничем не закрыт.'
    },
    ko: {
        muted:  '마이크가 음소거되어 있습니다 — 시스템 사운드 설정에서 음소거를 해제하면 측정이 시작됩니다.',
        ended:  '마이크 연결이 끊겼습니다. 다시 연결하거나 다른 입력을 선택한 뒤 시작을 다시 누르세요.',
        paused: '시스템이 소리를 일시정지했습니다 — 화면을 탭하면 측정이 돌아옵니다.',
        silent: '마이크에서 신호가 오지 않습니다. 시스템 사운드 설정에서 입력 레벨을 올리고 마이크가 가려져 있지 않은지 확인하세요.'
    },
    ar: {
        muted:  'الميكروفون مكتوم — ألغِ كتمه من إعدادات الصوت في النظام وسيبدأ القياس.',
        ended:  'انقطع الاتصال بالميكروفون. أعد توصيله أو اختر مدخلًا آخر ثم اضغط ابدأ مرة أخرى.',
        paused: 'أوقف النظام الصوت مؤقتًا — انقر في أي مكان لإعادة القياس.',
        silent: 'لا توجد إشارة من الميكروفون. ارفع مستوى الإدخال في إعدادات صوت النظام وتأكد من عدم وجود ما يغطيه.'
    },
    id: {
        muted:  'Mikrofon dibisukan — nyalakan kembali di pengaturan suara sistem, lalu pengukuran akan berjalan.',
        ended:  'Mikrofon terputus. Sambungkan lagi atau pilih input lain, lalu tekan mulai sekali lagi.',
        paused: 'Sistem menjeda suara — ketuk di mana saja untuk mengembalikan pengukuran.',
        silent: 'Tidak ada sinyal dari mikrofon. Naikkan level input di pengaturan suara sistem dan pastikan mikrofon tidak tertutup.'
    },
    pt: {
        muted:  'O microfone está mudo — reative-o nas configurações de som do sistema e a medição começa.',
        ended:  'O microfone foi desconectado. Reconecte-o ou escolha outra entrada e pressione iniciar de novo.',
        paused: 'O sistema pausou o som — toque em qualquer lugar para retomar a medição.',
        silent: 'Sem sinal do microfone. Aumente o nível de entrada nas configurações de som do sistema e verifique se nada o está cobrindo.'
    },
    de: {
        muted:  'Das Mikrofon ist stummgeschaltet — heben Sie die Stummschaltung in den Systemton-Einstellungen auf, dann misst es.',
        ended:  'Das Mikrofon wurde getrennt. Schließen Sie es wieder an oder wählen Sie einen anderen Eingang und starten Sie erneut.',
        paused: 'Das System hat den Ton pausiert — tippen Sie irgendwo, um die Messung zurückzuholen.',
        silent: 'Kein Signal vom Mikrofon. Erhöhen Sie den Eingangspegel in den Systemton-Einstellungen und prüfen Sie, ob es verdeckt ist.'
    },
    es: {
        muted:  'El micrófono está silenciado — actívalo en los ajustes de sonido del sistema y empezará a medir.',
        ended:  'El micrófono se desconectó. Vuelve a conectarlo o elige otra entrada y pulsa iniciar de nuevo.',
        paused: 'El sistema pausó el sonido — toca en cualquier lugar para recuperar la medición.',
        silent: 'No llega señal del micrófono. Sube el nivel de entrada en los ajustes de sonido del sistema y comprueba que nada lo tape.'
    },
    fr: {
        muted:  'Le microphone est coupé — réactivez-le dans les réglages son du système et la mesure démarrera.',
        ended:  'Le microphone s\'est déconnecté. Rebranchez-le ou choisissez une autre entrée, puis relancez.',
        paused: 'Le système a mis le son en pause — touchez l\'écran pour relancer la mesure.',
        silent: 'Aucun signal du microphone. Montez son niveau d\'entrée dans les réglages son du système et vérifiez que rien ne le couvre.'
    },
    uk: {
        muted:  'Мікрофон вимкнено — увімкніть його в налаштуваннях звуку системи, і вимірювання піде.',
        ended:  'Мікрофон від\'єднався. Підключіть його знову або виберіть інший вхід і натисніть старт ще раз.',
        paused: 'Система призупинила звук — торкніться екрана, щоб повернути вимірювання.',
        silent: 'Сигналу з мікрофона немає. Підніміть рівень входу в налаштуваннях звуку системи та перевірте, чи ніщо його не закриває.'
    },
    tr: {
        muted:  'Mikrofon kısık — sistem ses ayarlarından kısmayı kaldırın, ölçüm başlasın.',
        ended:  'Mikrofon bağlantısı kesildi. Yeniden bağlayın veya başka bir giriş seçin, ardından Başlat’a tekrar basın.',
        paused: 'Sistem sesi duraklattı — ölçümü geri getirmek için ekrana dokunun.',
        silent: 'Mikrofondan sinyal gelmiyor. Sistem ses ayarlarından giriş düzeyini yükseltin ve mikrofonu bir şeyin kapatmadığını kontrol edin.'
    }
};

/**
 * Dead-input copy for a RECORDING tool (watchDeadInput({purpose:'record'})).
 * Same four causes as DEAD, different consequence: a meter shows «—", a
 * recorder quietly writes a silent file. The line has to say THAT, because the
 * person usually is not looking at a level bar — /dictaphone 2026-09-23: two
 * takes of 133 s and 251 s into a dead microphone, the tab in the background,
 * an MP3 of silence downloaded, «Записала а прослушать не могу нет звука».
 * `silent` names the three places a live, unmuted device is switched off —
 * the wrong input chosen, the device off, its level at zero (measured: Windows
 * input level 0 hands Chrome −156 dBFS with track.muted === false) — and says
 * «yet» / «if you are already speaking»: for a recorder it is given only while
 * nothing has EVER come from the input, and a gated microphone whose owner has
 * not spoken yet looks exactly like that (watchDeadInput, 2026-09-23 field data).
 */
var DEAD_REC = {
    en: {
        muted:  'The microphone is muted in the system — this recording has no sound. Unmute it in your sound settings.',
        ended:  'The microphone disconnected — from here on the recording has no sound. Reconnect it or pick another microphone.',
        paused: 'The system paused the sound — tap anywhere so the recording carries on.',
        silent: 'No sound has come from the microphone yet. If you are already speaking, check that the right microphone is selected, it is not switched off, and its input level in the system sound settings is not at zero.'
    },
    ru: {
        muted:  'Микрофон выключен в системе — запись идёт без звука. Включите его в настройках звука.',
        ended:  'Микрофон отключился — дальше запись идёт без звука. Подключите его снова или выберите другой микрофон.',
        paused: 'Система поставила звук на паузу — коснитесь экрана, чтобы запись продолжилась.',
        silent: 'С микрофона пока не приходит звук. Если вы уже говорите — проверьте, что выбран нужный микрофон, он не выключен, а уровень входа в настройках звука системы не на нуле.'
    },
    ko: {
        muted:  '시스템에서 마이크가 음소거되어 있습니다 — 소리 없이 녹음되고 있습니다. 사운드 설정에서 음소거를 해제하세요.',
        ended:  '마이크 연결이 끊겼습니다 — 지금부터 소리 없이 녹음됩니다. 다시 연결하거나 다른 마이크를 선택하세요.',
        paused: '시스템이 소리를 일시정지했습니다 — 녹음이 계속되도록 화면을 탭하세요.',
        silent: '마이크에서 아직 소리가 들어오지 않습니다. 이미 말하고 있다면 올바른 마이크가 선택되어 있는지, 꺼져 있지 않은지, 시스템 사운드 설정에서 입력 레벨이 0이 아닌지 확인하세요.'
    },
    ar: {
        muted:  'الميكروفون مكتوم في النظام — التسجيل يجري بلا صوت. ألغِ كتمه من إعدادات الصوت.',
        ended:  'انقطع اتصال الميكروفون — التسجيل من الآن بلا صوت. أعد توصيله أو اختر ميكروفونًا آخر.',
        paused: 'أوقف النظام الصوت مؤقتًا — انقر في أي مكان ليستمر التسجيل.',
        silent: 'لم يصل أي صوت من الميكروفون بعد. إذا كنت تتحدث بالفعل، فتأكد من اختيار الميكروفون الصحيح، وأنه غير مطفأ، وأن مستوى الإدخال في إعدادات صوت النظام ليس صفرًا.'
    },
    id: {
        muted:  'Mikrofon dibisukan di sistem — rekaman berjalan tanpa suara. Nyalakan di pengaturan suara.',
        ended:  'Mikrofon terputus — mulai sekarang rekaman tanpa suara. Sambungkan lagi atau pilih mikrofon lain.',
        paused: 'Sistem menjeda suara — ketuk di mana saja agar rekaman berlanjut.',
        silent: 'Belum ada suara dari mikrofon. Jika Anda sudah berbicara, pastikan mikrofon yang benar dipilih, tidak dimatikan, dan level input di pengaturan suara sistem tidak nol.'
    },
    pt: {
        muted:  'O microfone está mudo no sistema — a gravação está sem som. Reative-o nas configurações de som.',
        ended:  'O microfone foi desconectado — daqui em diante a gravação fica sem som. Reconecte-o ou escolha outro microfone.',
        paused: 'O sistema pausou o som — toque em qualquer lugar para a gravação continuar.',
        silent: 'Ainda não chegou som do microfone. Se você já está falando, verifique se o microfone certo está selecionado, se não está desligado e se o nível de entrada nas configurações de som do sistema não está em zero.'
    },
    de: {
        muted:  'Das Mikrofon ist im System stummgeschaltet — die Aufnahme läuft ohne Ton. Heben Sie die Stummschaltung in den Toneinstellungen auf.',
        ended:  'Das Mikrofon wurde getrennt — ab hier ist die Aufnahme ohne Ton. Schließen Sie es wieder an oder wählen Sie ein anderes Mikrofon.',
        paused: 'Das System hat den Ton pausiert — tippen Sie irgendwo, damit die Aufnahme weiterläuft.',
        silent: 'Vom Mikrofon kommt noch kein Ton. Wenn Sie schon sprechen, prüfen Sie, ob das richtige Mikrofon gewählt ist, ob es eingeschaltet ist und ob sein Eingangspegel in den Systemton-Einstellungen nicht auf null steht.'
    },
    es: {
        muted:  'El micrófono está silenciado en el sistema — la grabación va sin sonido. Actívalo en la configuración de sonido.',
        ended:  'El micrófono se desconectó — desde aquí la grabación va sin sonido. Vuelve a conectarlo o elige otro micrófono.',
        paused: 'El sistema pausó el sonido — toca en cualquier lugar para que la grabación continúe.',
        silent: 'Todavía no llega sonido del micrófono. Si ya estás hablando, comprueba que esté seleccionado el micrófono correcto, que no esté apagado y que su nivel de entrada en la configuración de sonido del sistema no esté en cero.'
    },
    fr: {
        muted:  'Le microphone est coupé dans le système — l\'enregistrement se fait sans son. Réactivez-le dans les réglages son.',
        ended:  'Le microphone s\'est déconnecté — à partir d\'ici l\'enregistrement est sans son. Rebranchez-le ou choisissez un autre micro.',
        paused: 'Le système a mis le son en pause — touchez l\'écran pour que l\'enregistrement continue.',
        silent: 'Aucun son n\'arrive encore du microphone. Si vous parlez déjà, vérifiez que le bon micro est sélectionné, qu\'il n\'est pas éteint et que son niveau d\'entrée dans les réglages son du système n\'est pas à zéro.'
    },
    uk: {
        muted:  'Мікрофон вимкнено в системі — запис іде без звуку. Увімкніть його в налаштуваннях звуку.',
        ended:  'Мікрофон від\'єднався — далі запис іде без звуку. Підключіть його знову або виберіть інший мікрофон.',
        paused: 'Система призупинила звук — торкніться екрана, щоб запис продовжився.',
        silent: 'З мікрофона поки не надходить звук. Якщо ви вже говорите — перевірте, що вибрано потрібний мікрофон, він не вимкнений, а рівень входу в налаштуваннях звуку системи не на нулі.'
    },
    tr: {
        muted:  'Mikrofon sistemde kısık — kayıt sessiz ilerliyor. Ses ayarlarından kısmayı kaldırın.',
        ended:  'Mikrofon bağlantısı kesildi — kayıt bundan sonra sessiz. Yeniden bağlayın veya başka bir mikrofon seçin.',
        paused: 'Sistem sesi duraklattı — kaydın devam etmesi için ekrana dokunun.',
        silent: 'Mikrofondan henüz ses gelmiyor. Zaten konuşuyorsanız doğru mikrofonun seçili olduğunu, kapalı olmadığını ve sistem ses ayarlarındaki giriş düzeyinin sıfırda olmadığını kontrol edin.'
    }
};

/** Site-wide localStorage key — selection is shared across all 40 mic tools so
 *  the user picks their preferred mic once and every tool inherits it. */
var PICKER_STORAGE_KEY = 'timbrica_mic_device_id';

/** Shared module-level state — dedup across all MicManager instances on the page. */
var _errDedup = {}; // key: slug + ':' + code  →  timestamp ms (telemetry-only)
// Modal visibility state — drives "should I render another recovery modal?". A
// click that fails while one is already on screen is a no-op; a click within
// MODAL_SPAM_GUARD_MS of dismissal is treated as continuation of the same
// dismiss-then-reclick spam and skipped. Anything else re-shows the modal so a
// user clicking the mic button N minutes later still gets visible feedback.
var _modalState = { open: false, closedAt: 0 };

/* Live MicManager instances on this page. Only used by the auto-bind hook
   below, which needs to answer "whose stream is this?". */
var _instances = [];
var MODAL_SPAM_GUARD_MS = 1500;

/** Detect browser family for recovery instructions. Yandex Browser must be
 *  matched BEFORE Chrome — its UA contains both `YaBrowser/` and `Chrome/`,
 *  but its permission UX is significantly different (no padlock; coloured
 *  Protect-shield badge → Подробнее → Настройки сайта). Bug-report
 *  2026-05-07 sid 110fb0dd showed a Yandex user clicking "I allowed it"
 *  4 times across 16 minutes because she was looking for a Chrome-style
 *  padlock that doesn't exist in YaBrowser. */
function detectBrowser() {
    var ua = navigator.userAgent;
    // Samsung Internet MUST be checked before the generic Chrome match — its
    // UA contains "Chrome/1xx" (Chromium base) but its permission UI lives in
    // a completely different menu (Menu ≡ → Settings → Sites and downloads).
    // Real case 2026-07-15 (v_kvlv, SamsungBrowser/30): mic hard-denied, the
    // modal showed CHROME unblock steps, user retried blind 4× and left.
    if (/SamsungBrowser\//.test(ua)) return 'samsung';
    if (/YaBrowser\//.test(ua)) return 'yandex';
    if (/Edg\//.test(ua)) return 'chrome'; // Edge uses Chromium permission UX
    if (/OPR\/|Opera/.test(ua)) return 'chrome';
    if (/Firefox\//.test(ua)) return 'firefox';
    if (/Chrome\//.test(ua)) return 'chrome';
    if (/Safari\//.test(ua) && !/Chrome/.test(ua)) return 'safari';
    return 'chrome';
}

/** Android needs its own recipes: the desktop unblock controls simply do not
 *  exist there. Measured 2026-08-14 (user 4891, Pro, SM-S918B / Android 16 /
 *  YaBrowser 26.6): mic_perm_state 'denied', the modal showed the DESKTOP
 *  Yandex recipe — Protect shield, Settings → Sites → Advanced site settings —
 *  and mobile Yandex Browser has none of those screens. Two support agents and
 *  40 minutes later the user still could not record. Same class of bug as the
 *  Samsung Internet one fixed 2026-07-15; that branch got a mobile split, this
 *  one never did. */
function isAndroid() { return /Android/i.test(navigator.userAgent || ''); }

/** Hand the current page to Chrome. On Android any Chromium browser resolves
 *  an `intent:` URI, and Chrome keeps its OWN per-origin permission store — so
 *  a site the user once blocked in Yandex Browser is asked about again there.
 *  This is the only "re-ask" a web page can offer: once an origin is hard-
 *  denied, getUserMedia rejects without a prompt and no API can reset it or
 *  open the browser's settings. `browser_fallback_url` keeps a missing-Chrome
 *  tap harmless (it reloads the same page); the copyable link in the modal body
 *  is the manual path for that case. */
function openInChrome() {
    var clean = location.origin + location.pathname;
    var host = location.host + location.pathname;
    /* If Chrome is absent the intent's browser_fallback_url reloads THIS page
       in THIS browser, and the person is back in front of the same wall with
       the same "Open in Chrome" button. Measured 2026-09-06: 13 % of hand-offs
       came back to the same session and 45 of those 67 hit mic_denied again.
       sessionStorage survives that reload (same tab) and nothing else. */
    try { sessionStorage.setItem('mm_chrome_handoff', String(Date.now())); } catch (_) {}
    var intent = 'intent://' + host + '#Intent;scheme=https;package=com.android.chrome;'
        + 'S.browser_fallback_url=' + encodeURIComponent(clean) + ';end';
    try { location.href = intent; } catch (_) { location.href = clean; }
}

/** Did an "Open in Chrome" hand-off from THIS tab bounce back here? True for
 *  ten minutes after the click — long enough to cover the reload, short enough
 *  that tomorrow's visit is judged on its own. */
function chromeHandoffBounced() {
    try {
        var t = parseInt(sessionStorage.getItem('mm_chrome_handoff') || '0', 10);
        return t > 0 && (Date.now() - t) < 10 * 60 * 1000;
    } catch (_) { return false; }
}

/** Detect OS family for the system-level mic-block ('os_blocked') recovery
 *  modal — Windows and macOS hide the OS microphone toggle in completely
 *  different places. Prefers the modern UA-Client-Hints platform, falls back
 *  to navigator.platform / the UA string. A wrong guess is harmless: the
 *  'other' branch simply shows both sets of instructions. */
function detectOS() {
    try {
        var uaData = navigator.userAgentData;
        var plat = (uaData && uaData.platform) || navigator.platform || '';
        var ua = navigator.userAgent || '';
        if (/win/i.test(plat) || /Windows/i.test(ua)) return 'windows';
        if (/mac/i.test(plat) || /Mac OS X/i.test(ua)) return 'macos';
    } catch (_) {}
    return 'other';
}

/** Inject modal CSS once per page. */
function ensureModalStyles() {
    if (document.getElementById('mmModalStyle')) return;
    var s = document.createElement('style');
    s.id = 'mmModalStyle';
    s.textContent =
        '@keyframes mmFadeIn{from{opacity:0}to{opacity:1}}' +
        '@keyframes mmSlideUp{from{transform:translateY(12px);opacity:0}to{transform:translateY(0);opacity:1}}' +
        '.mm-modal-ov{position:fixed;top:0;left:0;right:0;bottom:0;z-index:99999;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.55);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);padding:16px;animation:mmFadeIn .2s ease-out}' +
        '.mm-modal{background:var(--bg-primary,#1a1a2e);border:1px solid var(--border,#333);border-radius:16px;padding:24px;max-width:460px;width:100%;color:var(--text-primary,#fff);animation:mmSlideUp .25s ease-out;max-height:85vh;overflow-y:auto;box-shadow:0 20px 60px rgba(0,0,0,.4)}' +
        '.mm-modal h3{margin:0 0 12px;font-size:17px;font-weight:700;text-align:center;color:var(--text-primary,#fff)}' +
        '.mm-modal .mm-icon{font-size:40px;text-align:center;margin-bottom:8px}' +
        '.mm-modal .mm-body{font-size:13px;color:var(--text-secondary,#bbb);line-height:1.55;margin-bottom:18px}' +
        '.mm-modal .mm-body em{font-style:normal;padding:1px 5px;border-radius:4px;background:var(--glass,rgba(255,255,255,.06));color:var(--text-primary,#fff);font-weight:500}' +
        '.mm-modal .mm-actions{display:flex;flex-direction:column;gap:8px}' +
        '.mm-modal .mm-btn-primary{padding:12px;background:var(--accent-gradient,linear-gradient(135deg,#38bdf8,#818cf8));color:var(--on-accent);border:none;border-radius:10px;font-size:14px;font-weight:600;cursor:pointer;transition:transform .1s}' +
        '.mm-modal .mm-btn-primary:hover{transform:translateY(-1px)}' +
        '.mm-modal .mm-btn-fallback{padding:11px;background:transparent;color:var(--accent,#38bdf8);border:1px solid rgba(56,189,248,.3);border-radius:10px;font-size:13px;font-weight:600;cursor:pointer;transition:background .15s}' +
        '.mm-modal .mm-btn-fallback:hover{background:rgba(56,189,248,.08)}' +
        '.mm-modal .mm-btn-secondary{padding:10px;background:none;color:var(--text-muted,#888);border:none;font-size:13px;cursor:pointer}' +
        '.mm-modal .mm-btn-secondary:hover{color:var(--text-secondary,#bbb)}' +
        /* Live permission state — the modal's one line that changes by itself. */
        '.mm-modal .mm-status{display:flex;align-items:center;gap:8px;margin:-6px 0 14px;padding:10px 12px;border-radius:10px;background:var(--glass,rgba(255,255,255,.06));font-size:13px;color:var(--text-primary,#fff);line-height:1.45}' +
        '.mm-modal .mm-status-dot{flex:0 0 10px;width:10px;height:10px;border-radius:50%;background:var(--danger,#e74c3c);box-shadow:0 0 0 3px rgba(231,76,60,.18)}' +
        '.mm-modal .mm-status[data-state="granted"] .mm-status-dot{background:var(--success,#22c55e);box-shadow:0 0 0 3px rgba(34,197,94,.18)}' +
        '.mm-modal .mm-status[data-state="prompt"] .mm-status-dot{background:var(--warning,#e0a800);box-shadow:0 0 0 3px rgba(224,168,0,.18)}' +
        '@keyframes mmNudge{0%,100%{transform:translateX(0)}25%{transform:translateX(-4px)}75%{transform:translateX(4px)}}' +
        '.mm-modal .mm-status.mm-nudge{animation:mmNudge .35s ease-in-out}' +
        '[dir=rtl] .mm-modal{text-align:right}';
    document.head.appendChild(s);
}

/** Inject device-picker CSS once per page (pill button + popover list). */
function ensurePickerStyles() {
    if (document.getElementById('mmPickerStyle')) return;
    var s = document.createElement('style');
    s.id = 'mmPickerStyle';
    s.textContent =
        /* Pill — hidden by default; .mm-pick-show flips it visible when 2+ devices */
        '.mm-pick{display:none;align-items:center;gap:6px;margin:6px auto 8px;padding:6px 10px;max-width:min(320px,90%);background:var(--glass,rgba(255,255,255,.06));border:1px solid var(--border,rgba(255,255,255,.12));border-radius:999px;color:var(--text-primary,#fff);font-size:12px;font-weight:500;cursor:pointer;transition:background .15s,border-color .15s;line-height:1.3;font-family:inherit}' +
        '.mm-pick:hover{background:var(--glass-hover,rgba(255,255,255,.1));border-color:var(--border-hover,rgba(255,255,255,.2))}' +
        '.mm-pick:focus-visible{outline:2px solid var(--accent,#38bdf8);outline-offset:2px}' +
        '.mm-pick-show{display:inline-flex}' +
        '.mm-pick-ico{flex:0 0 auto;font-size:13px;line-height:1}' +
        '.mm-pick-lbl{flex:1 1 auto;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;text-align:center}' +
        '.mm-pick-chev{flex:0 0 auto;opacity:.7;font-size:12px;transition:transform .15s}' +
        '.mm-pick[aria-expanded="true"] .mm-pick-chev{transform:rotate(180deg)}' +
        /* Popover — fixed-positioned, anchored by JS to the pill */
        '.mm-pop{position:fixed;z-index:99998;background:var(--bg-primary,#1a1a2e);border:1px solid var(--border,#333);border-radius:12px;padding:6px;min-width:240px;max-width:360px;max-height:320px;overflow-y:auto;box-shadow:0 12px 40px rgba(0,0,0,.45);color:var(--text-primary,#fff);animation:mmFadeIn .12s ease-out;font-family:inherit;font-size:13px}' +
        '.mm-pop-hd{padding:6px 10px 8px;font-size:12px;font-weight:600;color:var(--text-muted,#888);text-transform:uppercase;letter-spacing:.04em;border-bottom:1px solid var(--border,rgba(255,255,255,.08));margin-bottom:4px}' +
        '.mm-pop ul{list-style:none;margin:0;padding:0}' +
        '.mm-pop li{margin:0}' +
        '.mm-pop-opt{display:flex;align-items:center;gap:8px;width:100%;padding:8px 10px;background:none;border:none;color:var(--text-primary,#fff);font-size:13px;text-align:left;cursor:pointer;border-radius:8px;font-family:inherit;line-height:1.35}' +
        '.mm-pop-opt:hover{background:var(--glass-hover,rgba(255,255,255,.08))}' +
        '.mm-pop-opt:focus-visible{outline:2px solid var(--accent,#38bdf8);outline-offset:-2px}' +
        '.mm-pop-opt[aria-checked="true"]{background:rgba(56,189,248,.12);color:var(--accent,#38bdf8)}' +
        '.mm-pop-dot{flex:0 0 14px;width:14px;height:14px;border:1.5px solid var(--text-muted,#888);border-radius:50%;display:inline-flex;align-items:center;justify-content:center}' +
        '.mm-pop-opt[aria-checked="true"] .mm-pop-dot{border-color:var(--accent,#38bdf8)}' +
        '.mm-pop-opt[aria-checked="true"] .mm-pop-dot::after{content:"";width:6px;height:6px;background:var(--accent,#38bdf8);border-radius:50%}' +
        '.mm-pop-lbl{flex:1 1 auto;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}' +
        '[dir=rtl] .mm-pop-opt{text-align:right}';
    document.head.appendChild(s);
}

/** Force-close any visible MicManager modal — used when the user grants access
 *  through the address bar (Permissions API change) so the help dialog goes away
 *  without them having to dismiss it. Also keeps _modalState in sync so a later
 *  error can re-show the modal cleanly. */
function _closeOpenModal() {
    var existing = document.getElementById('mmModal');
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
    _modalState.open = false;
    _modalState.closedAt = Date.now();
}

/** Render a recovery modal.
 *  opts: {icon,title,body,primaryLabel,primaryAction,closeLabel,onOutcome,
 *         fallbackLabel,fallbackAction}.
 *  fallbackLabel/fallbackAction render an additional secondary button between
 *  primary (try again) and close (dismiss) — used to offer a "Or upload a file"
 *  bridge when the tool has a non-mic path to the same outcome. */
function renderModal(opts) {
    ensureModalStyles();
    // Remove any existing MicManager modal first so we never stack.
    var old = document.getElementById('mmModal');
    if (old && old.parentNode) old.parentNode.removeChild(old);

    var ov = document.createElement('div');
    ov.id = 'mmModal';
    ov.className = 'mm-modal-ov';

    var d = document.createElement('div');
    d.className = 'mm-modal';
    d.setAttribute('role', 'dialog');
    d.setAttribute('aria-modal', 'true');

    var inner = '';
    if (opts.icon) inner += '<div class="mm-icon">' + opts.icon + '</div>';
    inner += '<h3>' + opts.title + '</h3>';
    inner += '<div class="mm-body">' + opts.body + '</div>';
    /* Live state line (denied-without-prompt modal). Text is updated in place
       by the caller through opts.status; the dot colour follows data-state. */
    if (opts.status) inner += '<div class="mm-status" data-state="' + (opts.statusState || 'denied') + '" role="status" aria-live="polite"><span class="mm-status-dot"></span><span class="mm-status-text">' + opts.status + '</span></div>';
    inner += '<div class="mm-actions">';
    if (opts.primaryLabel) inner += '<button class="mm-btn-primary" data-a="primary">' + opts.primaryLabel + '</button>';
    /* "check" asks the Permissions API and does NOT close the modal — a
       button that cannot succeed must not look like one that can. */
    if (opts.checkLabel) inner += '<button class="mm-btn-fallback" data-a="check">' + opts.checkLabel + '</button>';
    if (opts.fallbackLabel) inner += '<button class="mm-btn-fallback" data-a="fallback">' + opts.fallbackLabel + '</button>';
    inner += '<button class="mm-btn-secondary" data-a="close">' + (opts.closeLabel || 'Close') + '</button>';
    inner += '</div>';
    d.innerHTML = inner;

    ov.appendChild(d);
    document.body.appendChild(ov);
    _modalState.open = true;

    var shownAt = Date.now();
    var outcome = null; // 'retry' | 'dismiss' | 'escape' | 'backdrop'

    function close() {
        if (ov.parentNode) ov.parentNode.removeChild(ov);
        _modalState.open = false;
        _modalState.closedAt = Date.now();
        if (typeof opts.onOutcome === 'function') {
            opts.onOutcome({
                outcome: outcome || 'dismiss', // if modal was forcibly re-rendered, default to 'dismiss'
                viewSec: Math.round((Date.now() - shownAt) / 1000)
            });
        }
    }
    function onKey(ev) { if (ev.key === 'Escape') { outcome = 'escape'; document.removeEventListener('keydown', onKey); close(); } }
    document.addEventListener('keydown', onKey);

    d.addEventListener('click', function(ev) {
        var btn = ev.target.closest('[data-a]');
        if (!btn) return;
        var a = btn.dataset.a;
        if (a === 'check') {
            /* Stays open: the answer is written into the status line. */
            if (typeof opts.checkAction === 'function') opts.checkAction(d);
            return;
        }
        document.removeEventListener('keydown', onKey);
        outcome = a === 'primary' ? 'retry'
                : a === 'fallback' ? 'fallback'
                : 'dismiss';
        close();
        if (a === 'primary'  && typeof opts.primaryAction  === 'function') opts.primaryAction();
        if (a === 'fallback' && typeof opts.fallbackAction === 'function') opts.fallbackAction();
    });
    ov.addEventListener('click', function(ev) {
        if (ev.target === ov) { document.removeEventListener('keydown', onKey); outcome = 'backdrop'; close(); }
    });

    // Focus primary button for keyboard users
    setTimeout(function() {
        var b = d.querySelector('.mm-btn-primary') || d.querySelector('.mm-btn-secondary');
        if (b) b.focus();
    }, 100);
}

function MicManager(opts) {
    opts = opts || {};
    this._locale = opts.locale || window.__LOCALE || 'en';
    this._stream = null;
    this._audioCtx = null;
    this._analyser = null;
    this._levelRaf = null;
    this._levelCb = null;
    this._slug = opts.slug || (document.querySelector('meta[name="tool-slug"]') || {}).content || '';
    this._autoHelp = opts.autoHelp !== false; // default: true
    // Optional override for the getUserMedia watchdog window (see
    // _acquireStream). Default null = use the permission-aware sizing.
    // A positive number forces a fixed window — used by the watchdog
    // regression test to avoid a 12 s wall-clock wait.
    this._acquireTimeoutMs = (typeof opts.acquireTimeoutMs === 'number' && opts.acquireTimeoutMs > 0)
        ? opts.acquireTimeoutMs : null;
    /* Prompt-state pacing (see _acquireStream). A pending getUserMedia in
       'prompt' state is a person deciding, not a hang: after promptWaitHintMs
       we say where the browser's prompt is, after promptWaitModalMs we show
       the honest "still waiting" modal. Both overridable for tests. */
    this._promptWaitHintMs  = (typeof opts.promptWaitHintMs  === 'number' && opts.promptWaitHintMs  > 0) ? opts.promptWaitHintMs  : 15000;
    this._promptWaitModalMs = (typeof opts.promptWaitModalMs === 'number' && opts.promptWaitModalMs > 0) ? opts.promptWaitModalMs : 180000;
    this._onRetry = typeof opts.onRetry === 'function' ? opts.onRetry : null;
    /* fallbackAction — optional opt-in for tools that have a non-mic path
       to the same outcome (file upload, preset playback, etc). When the mic
       fails (denied / dismissed / not_found / busy), we render its label as
       a secondary button inside both the recovery modal AND the tool's
       inline mic-status element if statusEl is provided. Click invokes the
       handler and emits feature_use {f:'mic_fallback_action_clicked'}.

       Shape:
         { label: 'Or upload a file', handler: function(){ ... },
           statusEl: HTMLElement }      // optional: also render inline */
    this._fallback = (opts.fallbackAction && typeof opts.fallbackAction.handler === 'function')
        ? opts.fallbackAction : null;

    /* Project-wide default fallback. If no explicit fallbackAction was passed
       AND the tool's slug appears in config/mic-fallback.php (exposed by the
       layout as window.__MIC_FALLBACK), build a navigate handler. Keeps
       38/40 mic tools out of "I forgot to wire fallbackAction" territory —
       opt-in is centralised in PHP config, not 38 separate blade files.
       Tools with custom in-page fallback (voice-changer mode tabs, etc.)
       still pass fallbackAction explicitly and override this default.
       Label comes from HELP[locale].fallback_label so all 7 locales stay
       consistent with the rest of the modal copy. */
    if (!this._fallback && typeof window !== 'undefined' && window.__MIC_FALLBACK) {
        var __cfg = window.__MIC_FALLBACK;
        if (__cfg && typeof __cfg.url === 'string') {
            var __h = HELP[this._locale] || HELP.en;
            this._fallback = {
                label: __h.fallback_label || HELP.en.fallback_label || 'Switch to file mode',
                handler: function () { window.location.href = __cfg.url; },
            };
        }
    }
    // Remember the last start call so we can auto-retry after the user grants
    // permission via browser settings. Without this, the recovery modal had
    // no primary "try again" button and 78% of users dismissed it (78/100 of
    // perm_help_shown events had hasRetry=0). Tools no longer need to pass
    // onRetry — re-invoking the same start method is the correct default.
    this._lastStart = null;

    /* audioContext — opt-in binding of the TOOL's AudioContext so the mic
       lifecycle can keep it running. See bindContext() for the whole story.
       Accepts a context or a getter (tools usually create theirs lazily,
       after the MicManager is constructed). */
    this._boundCtx = null;
    this._ctxGetters = [];
    this._ctxPauseEl = null;
    this._ctxPauseReported = false;
    this._ctxHealPending = false;
    if (opts.audioContext) this.bindContext(opts.audioContext, { anchorEl: opts.audioContextAnchorEl });

    _instances.push(this);

    // Auto-cleanup on navigation
    var self = this;
    this._cleanupFn = function() { self.stop(); };
    window.addEventListener('beforeunload', this._cleanupFn);
}

/* A generic "did the microphone actually produce sound?" signal was attempted
   here and ABANDONED on measurement — recorded so the next person does not
   rebuild it. Four shapes, all wrong on at least one real tool:
     · a private AudioContext on the stream      → flat zero on /voice-changer
     · a cloned track                            → flat zero on /reverse, which
                                                   the shared stream read fine
     · the tool's own bound context              → same contention
     · the tool's own input AnalyserNode         → flat zero on /reverse for
                                                   10 s while a direct read of
                                                   that same node gave 127
   Chromium shares one MediaStream between consumers unevenly, and the last
   case says even the no-extra-consumer route is not dependable everywhere. A
   metric that reports working microphones as dead is worse than no metric —
   it would have poisoned exactly the numbers this whole investigation needed.
   What DOES work is per-tool and explicit: /megaphone samples its own OUTPUT
   analyser and emits `mic_monitor`, verified silent=0 with sound and silent=1
   with a dead clock. Copy that shape per tool; do not generalise it here. */

/** Is `stream` ours? Matches the stream we handed out, or any stream built
 *  from our tracks (tools routinely wrap/clone it before Web Audio). */
MicManager.prototype._ownsStream = function(stream) {
    if (!stream || !this._stream) return false;
    if (stream === this._stream) return true;
    try {
        var mine = this._stream.getAudioTracks();
        return stream.getAudioTracks().some(function(t) { return mine.indexOf(t) !== -1; });
    } catch (_) { return false; }
};

/**
 * Automatic context binding — the reason 20-odd tools did not each need a
 * one-line migration they would never all get.
 *
 * `createMediaStreamSource(ourStream)` is the exact moment a tool marries our
 * microphone to its AudioContext, and it happens immediately after the
 * getUserMedia await — i.e. precisely where iOS may have left that context
 * suspended (see bindContext). So the hook fires at the right instant in every
 * tool that has the problem, and nowhere else: a context that never touches a
 * MicManager stream is never bound.
 *
 * Tools keep full control of the visible half — the "sound is paused" notice
 * only renders for a tool that passed `audioContextAnchorEl` (no-sticky rule:
 * we will not invent a floating chip on somebody's page). Healing and
 * reporting need no opt-in.
 */
(function installAutoBind() {
    var ACs = [];
    if (typeof window !== 'undefined') {
        if (window.AudioContext) ACs.push(window.AudioContext);
        if (window.webkitAudioContext && window.webkitAudioContext !== window.AudioContext) {
            ACs.push(window.webkitAudioContext);
        }
    }
    ACs.forEach(function(AC) {
        var proto = AC && AC.prototype;
        if (!proto || proto.__mmAutoBind || typeof proto.createMediaStreamSource !== 'function') return;
        var orig = proto.createMediaStreamSource;
        proto.createMediaStreamSource = function(stream) {
            var ctx = this;
            try {
                for (var i = 0; i < _instances.length; i++) {
                    var mm = _instances[i];
                    // Bind EVERY context our stream reaches, not just the first
                    // one. A single session commonly has two — the tool's graph
                    // and the silence watchdog's private context — and binding
                    // only the first meant whichever won the race got the
                    // protection while the other stayed stopped. On /record the
                    // watchdog wins, so the tool itself was left unguarded.
                    if (!mm._ownsStream(stream) || mm._hasCtx(ctx)) continue;
                    mm.bindContext(ctx);
                    mm._telemetry('audioctx_autobound', { ctx_state: ctx.state });
                    // The whole point: this context may have been stopped while
                    // the permission sheet was up, and the tool is about to
                    // build its graph on it.
                    mm.ensureContextRunning('auto_bind', 2000).catch(function() {});
                    break;
                }
            } catch (_) {}
            return orig.call(this, stream);
        };
        proto.__mmAutoBind = true;
    });
})();

/**
 * Bind the tool's AudioContext to the mic lifecycle.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 * A live mic and a running AudioContext are two independent things, and the
 * OS can break the second one while leaving the first intact. Then the tool
 * looks perfectly alive — stream live, meter drawn, "recording" offered — and
 * is stone silent. There is no error to catch: nothing threw.
 *
 * The window that opens it on iOS is the permission sheet itself. Safari
 * backgrounds the page while the sheet is up (WebKit 237878) and does not
 * reliably resume the AudioContext when the page comes back (WebKit 263627),
 * so the context a tool resumed BEFORE getUserMedia is frequently suspended
 * by the time the stream resolves. iOS also has a fourth state, `interrupted`
 * (phone call, Siri, route change), where `resume()` can return a promise
 * that never settles at all (WebAudio/web-audio-api#2585).
 *
 * This is not theoretical: voice-changer instruments it and sees the context
 * out of `running` while the user expects sound in thousands of sessions —
 * 267 of them in `interrupted` on iOS engines alone. Bug report 2026-07-26
 * (iPhone 18.7, /megaphone, sid 803e9c43) is the same failure with no guard:
 * `mic_acquire ok`, then zero downstream events, three rage-clicks, 1 star.
 *
 * ── What binding gives a tool (all of it, for one constructor line) ────────
 *   1. after getUserMedia resolves, the context is put back to `running`
 *      before the tool builds its graph on it;
 *   2. `interrupted` counts as "not running" — a bare
 *      `if (state === 'suspended') resume()` silently skips it;
 *   3. resume() is raced against a timeout so a wedged context cannot hang
 *      the caller's await forever;
 *   4. any later interruption self-heals on the user's next tap — the
 *      reporter above tapped three times while the tool stayed mute;
 *   5. while it is paused and the mic is live, the user is TOLD, instead of
 *      staring at a tool that claims to be working;
 *   6. it is all reported (`audioctx_paused` / `audioctx_resume_stall` /
 *      `audioctx_healed`), so the next occurrence is diagnosable.
 *
 * @param {AudioContext|function(): AudioContext} ctxOrGetter
 * @param {Object} [opts]
 * @param {HTMLElement} [opts.anchorEl] — the paused notice is inserted right
 *   after this element, in normal document flow (same shape as
 *   watchSilence()). Without it the healing and the telemetry still run but
 *   nothing is drawn — a floating/fixed chip is not an option on this project
 *   (no-sticky rule), so a tool that wants the message must say where it goes.
 */
MicManager.prototype.bindContext = function(ctxOrGetter, opts) {
    var getter = (typeof ctxOrGetter === 'function')
        ? ctxOrGetter
        : function() { return ctxOrGetter; };
    this._ctxGetters = this._ctxGetters || [];
    this._ctxGetters.push(getter);
    if (opts && opts.anchorEl) this._ctxAnchor = opts.anchorEl;
    this._installCtxHealer();
    return this;
};

/** Every bound context. A single mic session routinely feeds more than one:
 *  the tool's own graph plus the silence watchdog's private context — and the
 *  one that is left stopped is not always the same one, so all of them are
 *  kept alive. Never throws on a getter that isn't ready yet. */
MicManager.prototype._ctxs = function() {
    var out = [];
    (this._ctxGetters || []).forEach(function(g) {
        var c = null;
        try { c = g(); } catch (_) { c = null; }
        if (c && typeof c.state === 'string' && out.indexOf(c) === -1) out.push(c);
    });
    return out;
};

/** Back-compat single-context accessor: the first bound context, or null. */
MicManager.prototype._ctx = function() {
    return this._ctxs()[0] || null;
};

/** Is this exact context already bound? (auto-bind dedup) */
MicManager.prototype._hasCtx = function(ctx) {
    return this._ctxs().indexOf(ctx) !== -1;
};

/** `suspended`, `interrupted` and `closed` all mean "produces no sound". */
function _ctxIsRunning(ctx) { return !!ctx && ctx.state === 'running'; }

/**
 * Resume a context, racing resume() against a timeout.
 *
 * Static so the non-mic half of a tool (file playback, preview, demo) can use
 * the same guard — the interruption is a property of the page's audio session,
 * not of the microphone. Never throws.
 *
 * @param {AudioContext} ctx
 * @param {number} [timeoutMs=4000]
 * @returns {Promise<boolean>} — is it running afterwards?
 */
MicManager.ensureRunning = function(ctx, timeoutMs) {
    if (!ctx || typeof ctx.state !== 'string') return Promise.resolve(true);
    if (_ctxIsRunning(ctx)) return Promise.resolve(true);
    // `closed` is TERMINAL by spec — resume() on it rejects and the context can
    // never run again; only a NEW context can (memory: the device was taken and
    // resume hangs forever). Retrying it is not a slow heal, it is an
    // impossible one, and it was drowning the signal it exists to produce:
    // 7 days of prod, 62 621 resume-stalls on `closed` against 30 on
    // `suspended` — the 30 genuine stalls buried 1:2087 under an outcome ruled
    // out in advance. Report the terminal state once per context, under its own
    // name, and stop asking.
    if (ctx.state === 'closed') return Promise.resolve(false);
    // Delegate the resume itself to the project-wide helper so its
    // audio_resume_blocked telemetry keeps working and there is only one
    // place that knows how to talk to a stalled context. What this adds on
    // top is the deadline: on iOS resume() can return a promise that never
    // settles at all, and an await on that is a frozen tool.
    var attempt;
    try {
        attempt = window.__safeResume ? window.__safeResume(ctx) : Promise.resolve(ctx.resume());
    } catch (_) { attempt = Promise.resolve(false); }
    return new Promise(function(resolve) {
        var settled = false;
        var done = function() { if (!settled) { settled = true; resolve(_ctxIsRunning(ctx)); } };
        setTimeout(done, timeoutMs || 4000);
        Promise.resolve(attempt).then(done, done);
    });
};

/**
 * Put the bound context back to `running`, racing resume() against a timeout
 * so a wedged context cannot swallow the caller's await. Safe to call when
 * nothing is bound (resolves immediately).
 *
 * @param {string} where — telemetry tag for the call site
 * @param {number} [timeoutMs=4000]
 * @returns {Promise<boolean>} — true if the context is running afterwards
 */
MicManager.prototype.ensureContextRunning = async function(where, timeoutMs) {
    var ctxs = this._ctxs();
    if (!ctxs.length) return true;            // nothing bound — not our problem
    var stopped = ctxs.filter(function(c) { return !_ctxIsRunning(c); });
    if (!stopped.length) return true;
    var self = this;
    var results = await Promise.all(stopped.map(function(c) {
        var was = c.state;
        return MicManager.ensureRunning(c, timeoutMs).then(function(ok) {
            if (ok) { self._telemetry('audioctx_healed', { where: where || 'unknown', from: was }); return ok; }
            // A closed context is a different fact from a stalled one, and it
            // needs a different answer from the tool (rebuild, not retry), so
            // it gets its own event — once, not on every gesture for the rest
            // of the session.
            if (c.state === 'closed') {
                if (!c.__mmClosedReported) {
                    c.__mmClosedReported = 1;
                    self._telemetry('audioctx_closed', { where: where || 'unknown', from: was });
                }
                return ok;
            }
            self._telemetry('audioctx_resume_stall', { where: where || 'unknown', ctx_state: c.state, from: was });
            return ok;
        });
    }));
    this._syncCtxPauseNotice();
    return results.every(Boolean);
};

/**
 * Opportunistic healing on any user gesture + the "sound is paused" notice.
 * Installed once per bound MicManager. The gesture listeners are capture-phase
 * and permanent: an interruption can arrive at any point in the session, and
 * the cure must not depend on the user finding a particular button — in the
 * report that motivated this, the user's three frustrated taps on an unrelated
 * button would each have been enough to bring the sound back.
 */
MicManager.prototype._installCtxHealer = function() {
    if (this._ctxHealerInstalled) return;
    this._ctxHealerInstalled = true;
    var self = this;

    var heal = function() {
        var stopped = self._ctxs().filter(function(c) { return !_ctxIsRunning(c); });
        if (!stopped.length || self._ctxHealPending || document.hidden) return;
        if (!self._stream) { self._syncCtxPauseNotice(); return; }  // no live mic → nothing expects sound
        self._ctxHealPending = true;
        var settle = function() {
            self._ctxHealPending = false;
            if (stopped.some(_ctxIsRunning)) self._telemetry('audioctx_healed', { where: 'gesture', from: 'paused' });
            self._syncCtxPauseNotice();
        };
        try {
            Promise.all(stopped.map(function(c) {
                return Promise.resolve(c.resume()).catch(function() {});
            })).then(settle, settle);
        } catch (_) { settle(); }
        // A wedged resume() never settles — don't let one hung promise disable
        // healing for the rest of the session.
        setTimeout(function() { self._ctxHealPending = false; }, 3000);
    };
    ['touchend', 'click', 'keydown'].forEach(function(ev) {
        document.addEventListener(ev, heal, true);
    });

    // The OS can pause us with no gesture anywhere near it, and then nobody
    // taps because from the outside the tool simply looks broken. Poll while a
    // mic is live and RETRY, not just render: only some pauses are
    // gesture-gated, and the ones that are not — an interruption the page
    // caused itself — lift silently. Quiet by design: `audioctx_paused` has
    // already been reported once, and a rejected resume every second would
    // turn a stuck context into a telemetry flood.
    this._ctxWatchTimer = setInterval(function() {
        if (!self._stream) return;
        var stopped = self._ctxs().filter(function(c) { return !_ctxIsRunning(c); });
        if (stopped.length && !self._ctxHealPending && !document.hidden) {
            self._ctxHealPending = true;
            var settle = function() {
                self._ctxHealPending = false;
                if (stopped.some(_ctxIsRunning) && !self._ctxQuietHealReported) {
                    self._ctxQuietHealReported = true;
                    self._telemetry('audioctx_healed', { where: 'watchdog', from: 'paused' });
                }
                self._syncCtxPauseNotice();
            };
            try {
                Promise.all(stopped.map(function(c) {
                    return Promise.resolve(c.resume()).catch(function() {});
                })).then(settle, settle);
            } catch (_) { settle(); }
            setTimeout(function() { self._ctxHealPending = false; }, 3000);
        }
        self._syncCtxPauseNotice();
    }, 1000);
    if (document.addEventListener) {
        document.addEventListener('visibilitychange', function() {
            if (!document.hidden) heal();
        });
    }
};

/** Show/hide the "system paused the sound" notice. Only while a mic is live. */
MicManager.prototype._syncCtxPauseNotice = function() {
    var ctxs = this._ctxs();
    var stopped = ctxs.filter(function(c) { return !_ctxIsRunning(c); });
    var ctx = stopped[0] || ctxs[0] || null;
    var live = !!this._stream && stopped.length > 0;

    /* Only tell the user about a pause that OUTLASTS our own attempts to lift
       it. Most stops are gone within a second — the periodic retry above takes
       care of them and the user never needs to know. Flashing "the system
       paused your sound" for one second and then withdrawing it is worse than
       silence: it reads as a glitch and teaches people to distrust the tool.
       The message is for the pause that actually needs their tap. */
    var NOTICE_AFTER_MS = 3000;
    if (!live) { this._ctxPausedSince = 0; }
    else if (!this._ctxPausedSince) { this._ctxPausedSince = Date.now(); }
    var paused = live && (Date.now() - this._ctxPausedSince) >= NOTICE_AFTER_MS;

    if (paused) {
        // Telemetry does not depend on having somewhere to draw — a tool that
        // passed no anchor is still counted, it just stays quiet on screen.
        if (!this._ctxPauseReported) {
            this._ctxPauseReported = true;
            this._telemetry('audioctx_paused', { ctx_state: ctx.state, shown: this._ctxAnchor ? 1 : 0 });
        }
        if (!this._ctxAnchor || !this._ctxAnchor.parentNode) return;
        if (!this._ctxPauseEl) {
            var L = CTXPAUSE[this._locale] || CTXPAUSE.en;
            var el = document.createElement('div');
            el.className = 'mm-ctxpause';
            el.setAttribute('role', 'status');
            el.textContent = L.paused;
            ensureCtxPauseStyles();
            this._ctxAnchor.parentNode.insertBefore(el, this._ctxAnchor.nextSibling);
            this._ctxPauseEl = el;
        }
        this._ctxPauseEl.hidden = false;
    } else if (this._ctxPauseEl) {
        this._ctxPauseEl.hidden = true;
    }
};

/** Remove the notice + stop watching (mic went away). */
MicManager.prototype._teardownCtxNotice = function() {
    if (this._ctxPauseEl) { this._ctxPauseEl.hidden = true; }
    this._ctxPauseReported = false;
};

function ensureCtxPauseStyles() {
    if (document.getElementById('mm-ctxpause-styles')) return;
    var s = document.createElement('style');
    s.id = 'mm-ctxpause-styles';
    // In normal document flow, next to the control the user is looking at —
    // NOT a fixed/floating chip. Sticky bars and floating CTAs are ruled out
    // on this project (Farid, 2026-07-10), and a message about the tool's own
    // state belongs beside the tool anyway.
    s.textContent =
        '.mm-ctxpause{margin:8px auto 0;max-width:420px;padding:8px 12px;border-radius:10px;' +
        'text-align:center;font-size:13px;line-height:1.45;color:#fff;background:#b45309}' +
        '.mm-ctxpause[hidden]{display:none}';
    document.head.appendChild(s);
}

/**
 * Check if getUserMedia is available.
 * @returns {boolean}
 */
MicManager.prototype.isSupported = function() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
};

/**
 * Start microphone.
 * @param {Object} [opts]
 * @param {string} [opts.deviceId] — specific device ID
 * @param {boolean} [opts.echoCancellation=true]
 * @param {boolean} [opts.noiseSuppression=true]
 * @param {boolean} [opts.autoGainControl=true]
 * @param {number} [opts.sampleRate] — ideal sample rate
 * @param {number} [opts.channelCount=1] — ideal channel count
 * @returns {Promise<MediaStream>}
 * @throws {MicError} with .code and .message
 */
/* The microphone request goes through the core's door (timbrica-core.js
 * window.__tmbGum), not straight to navigator.mediaDevices.getUserMedia: on a
 * real iPhone in Yandex the property is not our wrapper (field trace 29.10.2026,
 * gw=0, the session module never ran), so the iPhone audio-session handling
 * (ios-audio-unmute.js request()) has to be reached by the door, whatever stands
 * on the property. Without the core (old cached page) — the property as before. */
function _micGum(c) {
    if (typeof window !== 'undefined' && typeof window.__tmbGum === 'function') return window.__tmbGum(c);
    return navigator.mediaDevices.getUserMedia(c);
}

MicManager.prototype.start = async function(opts) {
    opts = opts || {};
    this._lastStart = { kind: 'start', args: opts };

    if (!this.isSupported()) throw this._fail('not_supported');

    this._probePermissionState();
    this.stop();

    var constraints = { audio: {} };
    // Caller-supplied deviceId wins; otherwise fall back to the picker's
    // current selection if that device is actually present (avoids
    // OverconstrainedError when the stored device has been unplugged).
    var deviceId = opts.deviceId || (this._pickedDeviceAvailable() ? this._pickedDeviceId : null);
    if (deviceId) constraints.audio.deviceId = { exact: deviceId };
    constraints.audio.echoCancellation = opts.echoCancellation !== undefined ? opts.echoCancellation : true;
    constraints.audio.noiseSuppression = opts.noiseSuppression !== undefined ? opts.noiseSuppression : true;
    constraints.audio.autoGainControl  = opts.autoGainControl  !== undefined ? opts.autoGainControl  : true;
    if (opts.sampleRate) constraints.audio.sampleRate = { ideal: opts.sampleRate };
    constraints.audio.channelCount = { ideal: opts.channelCount || 1 };

    var _permState = await this._resolvePermState();
    var _t0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    try {
        this._stream = await this._acquireStream(_micGum(constraints), _permState);
        var _acquired = this._stream;
        _closeOpenModal(); // user granted access — drop any leftover modal + reset state
        this._clearInlineFallback();  // clear stale CTA from a prior denial
        this._deniedRetries = 0;       // user resolved it — reset escalation counter
        this._dismissCount = 0;        // reset stuck-dismissal counter
        this._osRetries = 0;           // reset os-block reload-escalation counter
        // First successful grant unlocks MediaDeviceInfo.label — repopulate the
        // picker so the user sees real device names instead of "Microphone N".
        if (this._picker) this._refreshPickerDevices();
        // See startRaw() / bindContext(): the permission sheet can leave the
        // tool's AudioContext suspended, and nothing else ever resumes it.
        // 2 s, not the default 4: this context was running moments ago, so a
        // real resume settles in well under a second. Anything slower is the
        // wedged case, and there the caller must not sit in a dead await —
        // the graph works fine built on a paused context and starts the
        // moment the gesture-healer or the context itself brings it back.
        await this.ensureContextRunning('after_gum', 2000);
        // Return the stream THIS call acquired, not the shared `this._stream`:
        // stop() (which every start() calls on entry) nulls that field, so a
        // concurrent stop between the awaits above made a perfectly successful
        // start resolve with null. Same object in the normal case.
        return _acquired;
    } catch (err) {
        var code = this._classifyError(err);
        if (code === 'pending_perm') {
            var _el = ((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()) - _t0;
            code = await this._refinePermissionError(_el, err);
        }
        throw this._fail(code, err);
    }
};

/**
 * Start with raw constraints (for tools needing echoCancellation:false etc).
 * @param {Object} constraints — raw getUserMedia constraints
 * @returns {Promise<MediaStream>}
 */
MicManager.prototype.startRaw = async function(constraints) {
    this._lastStart = { kind: 'startRaw', args: constraints };
    if (!this.isSupported()) throw this._fail('not_supported');
    this._probePermissionState();
    this.stop();
    // Inject the picker's deviceId if the caller did not specify one. Never
    // mutates the caller's object so _lastStart can replay against the latest
    // selection after the user changes mics mid-session.
    var effective = this._applyPickedDevice(constraints);
    var _permState = await this._resolvePermState();
    var _t0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    try {
        this._stream = await this._acquireStream(_micGum(effective), _permState);
        var _acquired = this._stream;
        _closeOpenModal();
        this._clearInlineFallback();
        this._deniedRetries = 0;       // user resolved it — reset escalation counter
        this._dismissCount = 0;        // reset stuck-dismissal counter
        this._osRetries = 0;           // reset os-block reload-escalation counter
        if (this._picker) this._refreshPickerDevices();
        // The permission sheet backgrounds the page on iOS and the context it
        // suspended is frequently NOT resumed when we come back — see
        // bindContext(). Put it back before the caller wires its graph onto it.
        // 2 s, not the default 4: this context was running moments ago, so a
        // real resume settles in well under a second. Anything slower is the
        // wedged case, and there the caller must not sit in a dead await —
        // the graph works fine built on a paused context and starts the
        // moment the gesture-healer or the context itself brings it back.
        await this.ensureContextRunning('after_gum', 2000);
        // Return the stream THIS call acquired, not the shared `this._stream`:
        // stop() (which every start() calls on entry) nulls that field, so a
        // concurrent stop between the awaits above made a perfectly successful
        // start resolve with null. Same object in the normal case.
        return _acquired;
    } catch (err) {
        var code = this._classifyError(err);
        if (code === 'pending_perm') {
            var _el = ((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()) - _t0;
            code = await this._refinePermissionError(_el, err);
        }
        throw this._fail(code, err);
    }
};

/**
 * Show the recovery modal for a given error code. Useful when a tool wants
 * to trigger the UI manually (e.g. after catching an error from startRaw).
 * No-op for not_supported (the browser cannot recover).  All other codes —
 * including 'generic' (added 2026-06-02) — render a modal with a localised
 * body, a retry button, and the tool's fallback CTA if configured.
 * @param {string} code — 'denied', 'not_found', 'busy', 'blocked_env', 'unresponsive', 'os_blocked', 'generic'
 * @param {function} [onRetry] — overrides the constructor's onRetry for this call
 */
MicManager.prototype.showRecoveryModal = function(code, onRetry) {
    var help = HELP[this._locale] || HELP.en;
    var self = this;
    // Resolution order: explicit callback > constructor onRetry > auto-replay
    // of the last start/startRaw call. The auto-replay is what 30+ tools relied
    // on implicitly — without it, the modal had no "try again" button.
    var retry = onRetry || this._onRetry || (this._lastStart ? function() {
        var ls = self._lastStart;
        var p;
        try { p = self[ls.kind](ls.args); }
        catch (_) { return; /* sync throw path — start/startRaw are async, rare */ }
        // start()/startRaw() are async and reject on repeated denial. The call
        // site (setTimeout(retry, 250) inside renderModal) doesn't chain .catch(),
        // which leaked ~75 unhandled-rejection events/week from voice-changer
        // alone. The rejection is already handled inside _fail (telemetry +
        // modal dedup) so silencing here is not error hiding.
        if (p && typeof p.catch === 'function') p.catch(function(){});
        return p;
    } : null);
    var b = detectBrowser();

    // Shown-event payload is recorded per modal; outcome event adds viewSec +
    // outcome (retry/dismiss/escape/backdrop) so dashboards can compute
    // CTA effectiveness = retry / shown, and recovery = complete after retry.
    function onOutcome(result) {
        self._telemetry(code + '_help_outcome', {
            br: b,
            hasRetry: retry ? 1 : 0,
            outcome: result.outcome,
            viewSec: result.viewSec
        });
    }

    function mkOpts(icon, title, body, primaryLabel, primaryFn) {
        var base = { icon: icon, title: title, body: body, closeLabel: help.close, onOutcome: onOutcome };
        if (retry) { base.primaryLabel = primaryLabel; base.primaryAction = primaryFn; }
        // Tool-supplied fallback (e.g. "Or upload a file" → switch to file tab).
        // Shows as a secondary button between primary and close.
        if (self._fallback) {
            base.fallbackLabel = self._fallback.label;
            base.fallbackAction = function() {
                self._telemetry('mic_fallback_action_clicked', { br: b, code: code, src: 'modal' });
                try { self._fallback.handler(); } catch (e) {
                    if (window.__handleError) window.__handleError(e, 'mic-manager:fallback');
                }
            };
        }
        return base;
    }

    if (code === 'denied') {
        /* In an in-app browser every one of the per-browser recipes below is
           dead advice: there is no address bar to click a padlock in, no
           Protect shield, no Settings → Sites menu. Measured 2026-08-03: of 32
           mobile sessions that hit a mic denial, ZERO recovered — while a sixth
           of RU mobile traffic arrives inside the Yandex Search app. So the
           in-app case gets the only instruction that can work — open the page
           in a real browser — plus the link, since there is no address bar to
           copy it from either. */
        var inApp = !!window.__isInAppBrowser;
        /* Mobile Yandex Browser has no per-site permission UI at all (see
           isAndroid above), so `help.yandex` — written for the desktop Protect
           shield — is dead advice there. Its own recipe names the only two
           levers that exist on a phone: hand the page to Chrome, or clear the
           browser's site data so the remembered denial is forgotten. */
        var yaMobile = (b === 'yandex' && isAndroid());
        var instructions = inApp
            ? (help.inapp || help.chrome)
            : (yaMobile ? (help.yandex_mobile || help.yandex) : (help[b] || help.chrome));
        if (inApp || yaMobile) {
            /* The text says "the link is below", so put it there. Without an
               address bar this is the only way the user can carry the page to a
               real browser, and it is the whole point of the message. Query and
               hash are dropped — a ysclid tracking tail is noise to retype.
               On mobile Yandex the button below normally does the carrying;
               this stays as the manual path when the intent finds no Chrome. */
            var clean = location.origin + location.pathname;
            instructions += '<br><br><span style="display:inline-block;padding:8px 12px;border-radius:8px;'
                + 'background:rgba(127,127,127,.14);font-family:ui-monospace,SFMono-Regular,Menlo,monospace;'
                + 'font-size:13px;word-break:break-all;user-select:all;-webkit-user-select:all">'
                + clean.replace(/[<>&]/g, '') + '</span>';
        }
        /* Per-instance retry counter. Bug-report 2026-05-07 sid 110fb0dd:
           Yandex Browser user clicked "I allowed it — try again" 4 times
           across 10 minutes before figuring out where the unblock control
           was. After 2 failed retries the modal escalates: lead with a
           "Still blocked?" headline and surface the file-mode CTA above
           the instructions so the alternative is the obvious next step. */
        this._deniedRetries = (this._deniedRetries || 0) + 1;
        var stuck = this._deniedRetries >= 3;          /* shown 3rd time = 2 retries failed */
        var icon = stuck ? '🆘' : '🔒';
        var title = stuck ? (help.stuck_title || help.title) : help.title;
        /* HOTFIX 2026-07-15: stuck_body promises "the file mode below" — only
           true when the tool actually HAS a fallback action. Tools without one
           (voice-translator etc.) rendered a promise with no button under it
           (v_kvlv session: 4 blind retries, viewSec=2 — the copy didn't match
           the modal). Without a fallback, keep the escalated headline but lead
           straight into the per-browser settings steps. */
        var stuckLead = (stuck && self._fallback) ? (help.stuck_body || '') + '<br><br>' : '';
        /* The generic lead ("here is how to enable it in your browser") is
           wrong in an in-app browser — there is nothing to enable there — so
           the in-app instructions stand on their own. Mobile Yandex is the
           same case for the same reason: its recipe opens by saying the block
           CANNOT be lifted from inside, and the lead promised the opposite one
           line above (seen on the deployed page, 2026-08-14). */
        var lead = (inApp || yaMobile) ? '' : help.intro + '<br><br>';
        var body = stuck ? stuckLead + lead + instructions : lead + instructions;
        /* Where the user cannot lift the block in THIS browser, "I allowed it —
           try again" is a button that cannot succeed: at state 'denied'
           getUserMedia rejects without ever showing a prompt. Mari (user 4891)
           pressed it and left after 2 s. So on Android inside mobile Yandex or
           an in-app browser the primary action becomes the one that can work —
           carry the page to Chrome, which asks again. mkOpts() only renders a
           primary button when `retry` is truthy, hence the local override. */
        var toChrome = isAndroid() && (yaMobile || inApp);
        /* The hand-off already happened from this tab and we are still here:
           Chrome is not installed (the intent's fallback reloads the page).
           Offering the same button again is the loop; say what happened and
           leave the two levers that exist — the copyable link, the file lane. */
        var bounced = toChrome && chromeHandoffBounced();
        if (bounced) {
            toChrome = false;
            instructions = (help.chrome_bounced || help.inapp || help.chrome) + instructions.substring(instructions.indexOf('<br><br><span'));
            body = lead + instructions;
            self._telemetry('open_in_chrome_bounced', { br: b, inapp: inApp ? 1 : 0 });
        }
        /* Chrome on Android has the per-site switch, but the desktop recipe
           ("click", "reload the page") reads wrong there — and the reload is a
           step backwards now that a flip auto-resumes the tool. */
        if (!inApp && !yaMobile && b === 'chrome' && isAndroid() && help.chrome_android) {
            instructions = help.chrome_android;
            body = stuck ? stuckLead + lead + instructions : lead + instructions;
        }
        /* Can a browser prompt still appear? At state 'denied' getUserMedia
           rejects without asking — measured 2026-09-06: 2 868 such calls in 916
           sessions, all doomed, 67 % pressed within 3 s of the modal. So the
           "try again" button exists only where a prompt CAN follow it; where it
           cannot, the modal shows the live permission state and waits. */
        var promptPossible = self._promptStillPossible();
        if (toChrome) {
            var chromeOpts = mkOpts(icon, title, body);
            /* Set directly, not through mkOpts: it renders a primary button only
               when a retry callback exists, and the Chrome hand-off must appear
               even for tools that never supplied one. */
            chromeOpts.primaryLabel = help.open_chrome || 'Open in Chrome';
            chromeOpts.primaryAction = function() {
                self._telemetry('open_in_chrome_clicked', { br: b, inapp: inApp ? 1 : 0 });
                openInChrome();
            };
            /* renderModal reports a primary press as outcome 'retry'. On this
               branch the primary is NOT a retry — relabel it so the dashboard
               does not count Chrome hand-offs as blind re-attempts. */
            chromeOpts.onOutcome = function(result) {
                self._telemetry(code + '_help_outcome', {
                    br: b,
                    hasRetry: 0,
                    outcome: result.outcome === 'retry' ? 'chrome' : result.outcome,
                    viewSec: result.viewSec
                });
            };
            renderModal(chromeOpts);
        } else if (!promptPossible && !bounced) {
            self._renderDeniedWithoutPrompt(mkOpts(icon, title, body), help, b, retry);
        } else if (bounced) {
            renderModal(mkOpts(icon, title, body));
        } else {
            renderModal(mkOpts(icon, title, body, help.retry,
                function() { setTimeout(retry, 250); })); // Chrome needs a frame to register new state
        }
        self._telemetry('perm_help_shown', { br: b, inapp: inApp ? 1 : 0, hasRetry: retry ? 1 : 0, stuck: stuck ? 1 : 0, attempt: self._deniedRetries,
            cta: bounced ? 'bounced' : (toChrome ? 'chrome' : (promptPossible ? 'retry' : 'check')) });
    } else if (code === 'not_found') {
        renderModal(mkOpts('🎤', help.nf_title, help.nf_body, help.nf_refresh, async function() {
            try { if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) await navigator.mediaDevices.enumerateDevices(); } catch (_) {}
            setTimeout(retry, 200);
        }));
        self._telemetry('not_found_help_shown', { br: b, hasRetry: retry ? 1 : 0 });
    } else if (code === 'busy') {
        renderModal(mkOpts('🔒', help.busy_title, help.busy_body, help.busy_retry,
            function() { setTimeout(retry, 200); }));
        self._telemetry('busy_help_shown', { br: b, hasRetry: retry ? 1 : 0 });
    } else if (code === 'blocked_env') {
        // Environment block — no retry button: re-requesting will be blocked
        // again. The honest path is a different browser. mkOpts() with no
        // primaryLabel renders only the fallback CTA (if the tool set one)
        // and Close. Kids browsers (Amazon Kids) get their own body: the
        // generic "open in Chrome" is unactionable there — no other browser
        // exists inside the kids profile; the working advice is parent+Silk
        // (and the file-lane fallback button right below).
        var kidsEnv = KIDS_BROWSER_RE.test(navigator.userAgent || '');
        renderModal(mkOpts('🚫', help.be_title, (kidsEnv && help.be_kids_body) || help.be_body));
        self._telemetry('blocked_env_help_shown', { br: b, kids: kidsEnv ? 1 : 0 });
    } else if (code === 'unresponsive') {
        // getUserMedia never settled (see _acquireStream). The hang is
        // usually transient — close the app holding the mic, reload — so
        // the retry button stays. File fallback shows alongside.
        renderModal(mkOpts('⏳', help.un_title, help.un_body, help.un_retry,
            function() { setTimeout(retry, 200); }));
        self._telemetry('unresponsive_help_shown', { br: b, hasRetry: retry ? 1 : 0 });
    } else if (code === 'generic' || code === 'session_mode') {
        /* 2026-06-02 etalon: unknown err.name used to dead-end with no UI
           because the recovery-modal allowlist excluded 'generic'.  Reuses the
           localized ERRORS[locale].generic body string (already present in all
           9 locales) and busy_retry button label.  Optional gen_title falls
           back to 'busy_title' (also localized) if not provided.
           'session_mode' (2026-10-23): iPhone refused capture under the page's
           audio session type — its own words, same retry (it works once the
           type ios-audio-unmute.js wrote has landed). */
        var sm = code === 'session_mode';
        var genTitle = (sm && help.sm_title) || help.gen_title || help.busy_title;
        var genBody  = (ERRORS[this._locale] && ERRORS[this._locale][code]) || ERRORS.en[code] || ERRORS.en.generic;
        renderModal(mkOpts('⚠️', genTitle, genBody, help.busy_retry,
            function() { setTimeout(retry, 200); }));
        self._telemetry(sm ? 'session_mode_help_shown' : 'generic_help_shown', { br: b, hasRetry: retry ? 1 : 0 });
    } else if (code === 'prompt_unanswered') {
        /* The browser's own prompt has been open for minutes. Nothing here is
           broken and no retry can help — a second request would queue behind
           the first. Say so, keep the request pending, offer the file lane. */
        renderModal(mkOpts('⏳', help.wait_title || help.un_title, help.wait_body || help.un_body));
        self._telemetry('prompt_unanswered_help_shown', { br: b });
    } else if (code === 'os_blocked') {
        // System-level block (Windows/macOS privacy settings, AV mic-shield).
        // The browser permission is already granted, so the retry button is
        // kept — once the user flips the OS toggle, retrying succeeds without
        // a fresh browser prompt. OS-specific copy; an unknown OS shows both.
        var os = detectOS();
        var osBody = help.os_intro + '<br><br>' + (
            os === 'macos'   ? help.os_mac :
            os === 'windows' ? help.os_win :
            (help.os_win + '<br><br>' + help.os_mac));
        /* Escalation (telemetry audit 2026-06-18: 25 os_blocked, only 12/55
           blocked sessions ever recovered — users loop on "try again"). An
           in-page getUserMedia retry frequently can't see a freshly-flipped
           Windows privacy toggle, and AV mic-shields (Kaspersky &c.) need the
           tab reloaded — so the first retry silently fails again. After the
           first failed retry, swap the primary button to a full page reload,
           which re-requests the mic in a fresh context that picks up the OS
           change, and add a line explaining why. Mirrors the denied-branch
           _deniedRetries escalation. */
        this._osRetries = (this._osRetries || 0) + 1;
        var osReload = this._osRetries >= 2;
        if (osReload && help.os_reload_hint) osBody += '<br><br>' + help.os_reload_hint;
        renderModal(mkOpts('🛑', help.os_title, osBody,
            osReload ? (help.os_reload || help.os_retry) : help.os_retry,
            osReload ? function() { try { location.reload(); } catch (_) {} }
                     : function() { setTimeout(retry, 250); }));
        self._telemetry('os_blocked_help_shown', { br: b, os: os, hasRetry: retry ? 1 : 0, reload: osReload ? 1 : 0, attempt: self._osRetries });
    }
};

/**
 * Whether a browser prompt can still follow a retry. Decided from the
 * Permissions API state recorded at the last rejection, not from the browser
 * name: 'denied' means the browser answers from memory and getUserMedia will
 * reject again without asking; an instant (<500 ms) rejection at 'prompt' is
 * the same memory in a browser that does not reflect it (mobile Yandex).
 * 'prompt' with a slow rejection is a person who closed the dialog — asking
 * again is exactly right. No API at all (Safari < 16, WebKit) → unknown →
 * keep the retry: Safari re-prompts every time anyway.
 */
MicManager.prototype._promptStillPossible = function() {
    var st = this._lastRejectState;
    if (st === 'denied') return false;
    if (st === 'prompt' && typeof this._lastRejectMs === 'number' && this._lastRejectMs >= 0 && this._lastRejectMs < 500) return false;
    return true;
};

/**
 * The denied modal for the case where no prompt can follow: instructions +
 * a live state line + a "check" button that asks the Permissions API (never
 * getUserMedia — that is the doomed call). The state line is updated by the
 * PermissionStatus `change` event, and the moment the state leaves 'denied'
 * the last start is replayed and the modal closes on success (the same
 * auto-resume that _watchPermissionGrant already provided, 74 % effective
 * once the flip happens).
 */
MicManager.prototype._renderDeniedWithoutPrompt = function(opts, help, browserName, retry) {
    var self = this;
    opts.status = help.status_blocked || 'Microphone: blocked';
    opts.statusState = 'denied';
    if (retry && help.check) {
        opts.checkLabel = help.check;
        opts.checkAction = function(dialogEl) {
            var line = dialogEl.querySelector('.mm-status');
            var txt = dialogEl.querySelector('.mm-status-text');
            var apply = function(state) {
                if (state && state !== 'denied') {
                    self._telemetry('perm_recheck', { br: browserName, state: state, result: 'resume' });
                    if (line) { line.setAttribute('data-state', state); }
                    if (txt) txt.innerHTML = help.status_granted || '';
                    setTimeout(function() { _closeOpenModal(); retry(); }, 250);
                } else {
                    self._telemetry('perm_recheck', { br: browserName, state: state || 'unknown', result: 'still' });
                    if (txt) txt.innerHTML = help.status_still || help.status_blocked || '';
                    if (line) { line.classList.remove('mm-nudge'); void line.offsetWidth; line.classList.add('mm-nudge'); }
                }
            };
            try {
                if (!navigator.permissions || !navigator.permissions.query) { apply('unknown'); return; }
                navigator.permissions.query({ name: 'microphone' }).then(function(r) { apply(r.state); }, function() { apply('unknown'); });
            } catch (_) { apply('unknown'); }
        };
    }
    renderModal(opts);
    /* Keep the line truthful without a click: the same `change` event that
       drives the auto-resume rewrites the text the instant the switch flips. */
    try {
        if (navigator.permissions && navigator.permissions.query) {
            navigator.permissions.query({ name: 'microphone' }).then(function(r) {
                var onChange = function() {
                    var modal = document.getElementById('mmModal');
                    if (!modal) { try { r.removeEventListener('change', onChange); } catch (_) {} return; }
                    var line = modal.querySelector('.mm-status'), txt = modal.querySelector('.mm-status-text');
                    if (line) line.setAttribute('data-state', r.state);
                    if (txt) txt.innerHTML = r.state === 'granted' ? (help.status_granted || '') : (r.state === 'prompt' ? (help.status_waiting || '') : (help.status_blocked || ''));
                };
                try { r.addEventListener('change', onChange); } catch (_) {}
            }).catch(function() {});
        }
    } catch (_) {}
};

/**
 * In-page hint while the browser's own prompt is open. Written into the tool's
 * status element when it has one (the same slot the inline fallback uses) and
 * shown as a toast either way — the person is looking at the browser chrome,
 * not at us, so both are cheap and neither is the point.
 */
MicManager.prototype._showPromptWaitHint = function() {
    var help = HELP[this._locale] || HELP.en;
    var msg = help.wait_hint || '';
    if (!msg) return;
    try {
        var fb = this._fallback;
        if (fb && fb.statusEl) {
            var span = fb.statusEl.querySelector('.mm-status-text');
            if (!span) { span = document.createElement('span'); span.className = 'mm-status-text'; fb.statusEl.textContent = ''; fb.statusEl.appendChild(span); }
            span.textContent = msg;
        }
    } catch (_) {}
    try { if (typeof window.__toast === 'function') window.__toast(msg, 'info'); } catch (_) {}
};

/**
 * Query Permissions API (if available) to tell whether the mic was already
 * granted/denied before the first tool request. Called lazily once per
 * MicManager instance. Emits one `mic_perm_state` feature_use event so we
 * can distinguish "denied just now" from "browser remembered previous denial".
 *
 * P5: also subscribe to `change` events on the PermissionStatus so we capture
 * the prompt → granted / prompt → denied transition explicitly. Previously
 * we inferred this from subsequent `tool_blocked` events; the explicit
 * transition lets us measure prompt-acceptance rate per browser.
 */
MicManager.prototype._probePermissionState = function() {
    if (this._permProbed) return;
    this._permProbed = true;
    var self = this;
    try {
        if (!navigator.permissions || !navigator.permissions.query) return;
        // Safari prior to 16 throws on 'microphone' — guarded.
        navigator.permissions.query({ name: 'microphone' }).then(function(result) {
            var br = detectBrowser();
            self._telemetry('mic_perm_state', { state: result.state, br: br });
            // Feed the watchdog: the timeout window for getUserMedia depends
            // on whether a browser prompt will block the call (see
            // _acquireStream). Resolving the shared promise here means the
            // permission query runs ONCE, not twice per start.
            if (self._permStateResolve) { self._permStateResolve(result.state); self._permStateResolve = null; }
            // Listen for transitions. result.state caches the value at query
            // time; the change event fires when the user grants/denies via the
            // browser prompt or revokes via site settings.
            try {
                var prev = result.state;
                result.addEventListener('change', function() {
                    if (result.state === prev) return;
                    self._telemetry('mic_perm_change', {
                        from: prev,
                        to: result.state,
                        br: br,
                    });
                    prev = result.state;
                });
            } catch (_) { /* PermissionStatus.addEventListener may not exist on older Safari */ }
        }).catch(function() { /* unsupported, skip */ });
    } catch (_) {}
};

/**
 * Resolve the current `microphone` permission state ('granted' | 'denied' |
 * 'prompt' | 'unknown'), used to size the getUserMedia watchdog window.
 *
 * The promise is created once per instance and resolved by
 * _probePermissionState's query (so the Permissions API is hit once, not
 * twice per start). A 2 s fallback covers browsers without the Permissions
 * API (Safari < 16, some webviews) — there we simply don't know, and
 * _acquireStream picks the moderate 'unknown' window.
 */
MicManager.prototype._resolvePermState = function() {
    if (!this._permStatePromise) {
        var self = this;
        this._permStatePromise = new Promise(function(resolve) {
            self._permStateResolve = resolve;
            setTimeout(function() {
                if (self._permStateResolve) { self._permStateResolve = null; resolve('unknown'); }
            }, 2000);
        });
    }
    return this._permStatePromise;
};

/**
 * Watchdog wrapper around a getUserMedia promise.
 *
 * getUserMedia has no AbortController / signal support, so a hung call —
 * wedged OS audio service, conflicting driver, another app holding the
 * device in exclusive mode, antivirus mic-shield — NEVER settles. The bare
 * `await` then blocks the whole tool forever with zero feedback: the user
 * clicks the mic, nothing happens, and they file "mic not work" (real
 * bug-report 2026-05-16, IP 142.118.115.156, Edge 148 — 12 distinct users
 * in 14 days hit this, avg 6.5 min stuck before giving up).
 *
 * Promise.race against a timeout is the only available primitive. The
 * late-resolution branch stops the orphaned MediaStream so a device that
 * frees up *after* the timeout doesn't leave a zombie "recording"
 * indicator or hold the mic hostage.
 *
 * Window is permission-aware: with 'granted'/'denied' no browser prompt
 * blocks the call so it must settle in well under a second — a long stall
 * is a genuine hang (12 s ceiling, huge margin over realistic 1-3 s cold
 * starts). 'unknown' (no Permissions API) gets a moderate 20 s.
 *
 * 'prompt' has NO watchdog since 2026-09-06: the browser's own dialog is
 * open and the person has not answered, which is not a hang and must not be
 * reported as one. Measured (RU, 90 d): 728 of 822 timeouts were prompt-state
 * — 60 s of a dialog nobody had answered yet — and 34 % of those people DID
 * allow the microphone later, only to have that stream stopped as a zombie
 * under a modal saying the microphone "is not responding". The request now
 * stays pending and is used when it settles; a hint at promptWaitHintMs says
 * where the prompt is, and at promptWaitModalMs an honest, non-rejecting
 * "still waiting" modal offers the file lane. An explicit acquireTimeoutMs
 * (the test knob) keeps the race in every state.
 *
 * Emits `mic_acquire` breadcrumbs (begin / ok / timeout) so the telemetry
 * can finally distinguish a getUserMedia hang from a downstream worklet /
 * audio-graph hang — previously both looked identical (silence after
 * `mic_perm_state`), an un-diagnosable blind spot.
 */
MicManager.prototype._acquireStream = function(gumPromise, permState) {
    var self = this;
    var nowFn = (typeof performance !== 'undefined' && performance.now)
        ? function() { return performance.now(); } : function() { return Date.now(); };
    var t0 = nowFn();
    self._telemetry('mic_acquire', { phase: 'begin', perm: permState });

    /* 'prompt' is not a hang: the browser is asking and the person has not
       answered. Measured 2026-09-06 (RU, 90 d): 728 of 822 watchdog timeouts
       were prompt-state, 34 % of those people allowed the microphone LATER
       — and the stream they finally granted was stopped as a zombie while the
       modal told them the microphone was "not responding". So in 'prompt'
       state the request stays pending and is USED when it settles; we only
       say where the prompt is (hint) and, much later, admit we are still
       waiting (modal, non-rejecting, with the file lane). An explicit
       acquireTimeoutMs keeps the old race — that is the test knob. */
    if (permState === 'prompt' && !this._acquireTimeoutMs) {
        return new Promise(function(resolve, reject) {
            var hintTimer = setTimeout(function() {
                self._telemetry('mic_acquire', { phase: 'waiting', perm: permState, ms: self._promptWaitHintMs });
                self._showPromptWaitHint();
            }, self._promptWaitHintMs);
            var modalTimer = setTimeout(function() {
                self._telemetry('mic_acquire', { phase: 'waiting_long', perm: permState, ms: self._promptWaitModalMs });
                if (self._autoHelp && !_modalState.open) self.showRecoveryModal('prompt_unanswered');
            }, self._promptWaitModalMs);
            var done = function() { clearTimeout(hintTimer); clearTimeout(modalTimer); };
            gumPromise.then(function(stream) {
                done();
                self._telemetry('mic_acquire', { phase: 'ok', perm: permState, ms: Math.round(nowFn() - t0) });
                resolve(stream);
            }, function(err) { done(); reject(err); });
        });
    }

    var ms = this._acquireTimeoutMs || ((permState === 'unknown') ? 20000 : 12000);
    return new Promise(function(resolve, reject) {
        var settled = false;
        var timer = setTimeout(function() {
            if (settled) return;
            settled = true;
            self._telemetry('mic_acquire', { phase: 'timeout', perm: permState, ms: ms });
            // getUserMedia is still pending and uncancellable. The orphaned
            // stream, if it resolves later, is stopped by the single
            // cleanup path in the gumPromise.then() handler below (its
            // `if (settled)` branch) — not duplicated here.
            var e = new Error('getUserMedia did not settle within ' + ms + 'ms');
            e.name = 'TimeoutError';
            reject(e);
        }, ms);
        gumPromise.then(function(stream) {
            if (settled) {
                // Resolved after the watchdog already fired — stop the
                // orphaned tracks so the mic is not left open (and no
                // zombie "recording" indicator) behind a recovery modal
                // the user has already seen.
                try { stream.getTracks().forEach(function(t) { t.stop(); }); } catch (_) {}
                return;
            }
            settled = true;
            clearTimeout(timer);
            self._telemetry('mic_acquire', { phase: 'ok', perm: permState, ms: Math.round(nowFn() - t0) });
            resolve(stream);
        }, function(err) {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            reject(err);
        });
    });
};

/**
 * Stop microphone and release all resources.
 */
MicManager.prototype.stop = function() {
    this._signalReported = false;
    this.stopSilenceWatch();
    this.stopDeadInputWatch();
    this._teardownCtxNotice();
    if (this._levelRaf) { cancelAnimationFrame(this._levelRaf); this._levelRaf = null; }
    if (this._analyser) { try { this._analyser.disconnect(); } catch(e) {} this._analyser = null; }
    if (this._audioCtx) { try { this._audioCtx.close(); } catch(e) {} this._audioCtx = null; }
    if (this._stream) {
        this._stream.getTracks().forEach(function(t) { t.stop(); });
        this._stream = null;
    }
};

/**
 * Active "we can't hear you" watchdog on the CURRENT stream — opt-in, call
 * after start()/startRaw() resolves. Wraps the shared mic-silence-watch.js
 * (dictaphone-proven: digital flatline = byte-time-domain peak<=1) with a
 * localized banner so tools need no lang keys and no UI of their own. When a
 * device picker is attached, the banner offers PICKER.change right inside it —
 * the Screaming Bee case: a dead VIRTUAL default must be escapable at the
 * exact moment the user learns the mic is silent.
 *
 * WHERE IT MUST NOT GO: measuring tools (tuner, sound-meter, hearing-test,
 * instrument-identifier, snoring-monitor, mic-noise-test) — recording silence
 * is their job; a warning there is a false alarm by design.
 *
 * @param {Object} opts
 * @param {HTMLElement} opts.anchorEl — banner is inserted after this element
 *   (pass position:'before' to render above it)
 * @param {number} [opts.ms=15000] — continuous silence before the banner
 *   shows. 15 s is dictaphone's production value for open-ended recordings
 *   (its comment warns 5 s caused rage-quit false alarms); short read-aloud
 *   takes (a liveness reference) can pass 4000 like voice-cover does.
 *   The window runs only while nothing has EVER come from the input (a pause
 *   after speech is not a dead microphone — mic-silence-watch.js), so the banner
 *   says «no sound yet» then, and «we can't hear you» only for the facts that
 *   can follow speech: the system muted the track or the device went away.
 * @param {AnalyserNode} [opts.analyser] — the tool's own analyser on the RAW
 *   microphone (before any effect, gate or volume). It has to exist when this is
 *   called: a null here makes the watch open a second reader of the stream.
 * @returns {{stop:function}|null} null when the stream/module is missing
 */
MicManager.prototype.watchSilence = function(opts) {
    opts = opts || {};
    this.stopSilenceWatch();
    if (!this._stream || !opts.anchorEl || !window.MicSilenceWatch) return null;

    ensureSilenceStyles();
    var L = SILENCE[this._locale] || SILENCE.en;
    var P = PICKER[this._locale] || PICKER.en;
    var self = this;

    var banner = document.createElement('div');
    banner.className = 'mm-silence';
    banner.setAttribute('role', 'alert');
    banner.hidden = true;
    var txt = document.createElement('span');
    txt.textContent = '🔇 ' + (L.yet || L.live);
    banner.appendChild(txt);
    if (this._picker) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'mm-silence-change';
        btn.textContent = P.change;
        btn.addEventListener('click', function() { self._togglePicker(); });
        banner.appendChild(btn);
    }
    opts.anchorEl.insertAdjacentElement(opts.position === 'before' ? 'beforebegin' : 'afterend', banner);

    /* Prefer the tool's OWN analyser over opening a second reader of the
       microphone. Two AudioContexts consuming one MediaStream do not share it
       evenly in Chromium: delivery to the second one comes in bursts with gaps
       long enough to cross this watchdog's silence window, so it announces a
       broken microphone while the tool's level meter is visibly moving.
       Measured on /voice-changer with the level meter fixed: 4 false alarms in
       6 runs, and the watchdog's own maxPeak had reached 127 in the very runs
       where it fired — it HEARD the mic and still called it dead.
       A tool that passes its analyser is measuring the same samples it is
       already processing, so there is nothing to starve. */
    var watch = window.MicSilenceWatch.attach({
        analyser: opts.analyser || null,
        stream: this._stream,   // kept even with an analyser: track_state rides the telemetry
        slug: this._slug,
        silenceMs: opts.ms || 15000,
        onSilence: function(info) {
            txt.textContent = '🔇 ' + ((info && info.alive) ? L.live : (L.yet || L.live));
            banner.hidden = false;
        },
        onSignal: function() { banner.hidden = true; },
    });
    this._silence = {
        stop: function() {
            try { watch.stop(); } catch (_) {}
            if (banner.parentNode) banner.parentNode.removeChild(banner);
        },
    };
    return this._silence;
};

/**
 * watchDeadInput — "the input is not quiet, it is NOT THERE".
 *
 * ── Why this is not watchSilence() ────────────────────────────────────────
 * MEASURING tools (sound-meter, guitar-tuner, hearing-test, tachometer,
 * instrument-identifier…) must not use watchSilence(): a quiet room is a
 * legitimate reading there, and "we can't hear you" over one is a false alarm
 * by design — mic-silence-watch.js says so in its own header. But a reading of
 * literally NOTHING is not a quiet room. No ADC produces it.
 *
 * Measured 2026-08-31 on four Windows inputs, 6 s each, raw analyser:
 *   Intel array (default)   peak −13.2 dBFS   rms −34.7   first non-zero frame 17
 *   Intel array (comms)     peak −14.5        rms −31.6   first non-zero frame  5
 *   Intel array (direct)    peak −18.5        rms −33.5   first non-zero frame  4
 *   Realtek line-in, NOTHING PLUGGED IN
 *                           peak −42.7        rms −56.4   first non-zero frame  4
 * The quietest working input this project could produce still sits 57 dB above
 * the −100 dBFS floor below, so the floor cannot accuse a live microphone. And
 * every device opened with a burst of 3–17 all-zero frames — which is exactly
 * why the verdict is time-based (deadMs) and not instantaneous.
 *
 * ── Why the TOOL supplies the samples ────────────────────────────────────
 * A generic reader was tried in this file and abandoned (see the note above
 * _ownsStream): a second consumer of a Chromium MediaStream reads flat zero on
 * some tools, and a watchdog that calls working microphones dead is worse than
 * none. The tool's OWN analyser — the one whose number is already on screen —
 * has no such contention, and it is the number the user is staring at, so it
 * is the one that has to be explained.
 *
 * ── What the manager adds: the CAUSE ─────────────────────────────────────
 * The sample function knows the level and nothing else. These four need four
 * different actions from the user, and one generic line sends three quarters
 * of them to the wrong setting:
 *   ended   track.readyState === 'ended'   device unplugged / taken away
 *   muted   track.muted === true           the source is there and delivering
 *                                          nothing: Windows mixer mute, an
 *                                          exclusive-mode grab, a hardware
 *                                          mute switch
 *   paused  our AudioContext is not running (the iOS permission-sheet case
 *                                          bindContext() exists for)
 *   silent  device alive, unmuted, level below the floor: input level at 0,
 *                                          a virtual/broken driver, a covered
 *                                          mic
 *
 * @param {Object}   opts
 * @param {function} opts.sample      — returns the CURRENT level in dBFS
 *                                      (-Infinity / null when there is none).
 * @param {number}  [opts.floorDbfs=-100]
 * @param {number}  [opts.deadMs=3000]  continuous time below the floor
 * @param {number}  [opts.checkMs=250]
 * @param {string}  [opts.purpose]     'record' — a recorder: the cause lines say
 *                                      the TAKE is silent (DEAD_REC) instead of
 *                                      the meter being blank (DEAD), and the
 *                                      level is judged the recorder's way (below)
 * @param {number}  [opts.graceMs]     window while the input has NEVER been
 *                                      alive (record: 8000, else = deadMs)
 * @param {boolean} [opts.levelAfterAlive] does silence still count once the
 *                                      input has spoken? (record: false)
 * @param {function} opts.onDead      — (cause, message) once per episode
 * @param {function} [opts.onAlive]   — signal came back
 * @returns {{stop:function, cause:function, alive:function, rebind:function}|null}
 *
 * ── A recorder is judged differently from a meter ─────────────────────────
 * Measured on the first two hours of /dictaphone in production (2026-09-23,
 * 243 takes) after it moved here with the meter's rule:
 *   · inputs that hand the page EXACT zeros between phrases are common — a
 *     driver or headset noise gate. Seven sessions got «no sound» in the pauses
 *     of takes that were perfectly audible (peaks −2…−17 dBFS), one of them four
 *     times in six minutes; the «live inputs never read below −100» premise
 *     above held for the four Windows inputs it was measured on, not for them;
 *   · a device can take ~3 s to deliver its first sample: two 2.6–2.9 s takes
 *     came back entirely empty on a microphone that worked in the takes around
 *     them, and its 69 s take was declared dead at 3 s and came alive after.
 * So for a recorder the level is evidence only while the input has NEVER been
 * alive in this watch (≥ 2 checks above the floor in a row — a lone click in a
 * dead line is not life), and only after a warm-up grace. Once it has spoken,
 * silence is a pause; only facts count — the track ended, the OS muted it, our
 * context stopped rendering (the recording is not running then). A meter keeps
 * its rule: a zero reading is exactly what it has to explain.
 */
MicManager.prototype.watchDeadInput = function(opts) {
    opts = opts || {};
    this.stopDeadInputWatch();
    if (typeof opts.sample !== 'function') return null;

    var self = this;
    var record = opts.purpose === 'record';
    var floor = (typeof opts.floorDbfs === 'number') ? opts.floorDbfs : -100;
    var deadMs = opts.deadMs || 3000;
    var checkMs = opts.checkMs || 250;
    var graceMs = opts.graceMs || (record ? 8000 : deadMs);
    var levelAfterAlive = (typeof opts.levelAfterAlive === 'boolean') ? opts.levelAfterAlive : !record;
    var sustain = record ? 2 : 1;
    var belowMs = 0;
    var aboveRun = 0;
    var everAlive = false;
    var dead = false;
    var deadSince = 0;
    var cause = null;
    var stopped = false;
    var startedAt = Date.now();
    var lastTick = startedAt;

    function track() {
        var s = self._stream;
        if (!s) return null;
        var t = s.getAudioTracks ? s.getAudioTracks()[0] : null;
        return t || null;
    }

    /* Ordered most-specific first: a track that ENDED is also reported muted by
       some builds, and "reconnect it" is the useful half of that pair. */
    function classify() {
        var t = track();
        if (!t || t.readyState === 'ended') return 'ended';
        if (t.muted) return 'muted';
        var ctxs = self._ctxs();
        if (ctxs.length && !ctxs.some(_ctxIsRunning)) return 'paused';
        return 'silent';
    }

    function goDead() {
        if (dead || stopped) return;
        dead = true;
        deadSince = Date.now();
        cause = classify();
        /* A paused context is the one cause we can act on ourselves — try
           before telling the user to do anything. bindContext()'s healer runs
           on gestures, and there is no gesture here. */
        if (cause === 'paused') { try { self.ensureContextRunning('dead_input', 2000); } catch (_) {} }
        self._telemetry('mic_dead_input', { cause: cause, floor: floor, after_ms: everAlive ? deadMs : graceMs,
            alive: everAlive ? 1 : 0, t_ms: Date.now() - startedAt });
        try { opts.onDead && opts.onDead(cause, self.getDeadInputMessage(cause, opts.purpose)); } catch (_) {}
    }

    function goAlive() {
        if (!dead || stopped) return;
        dead = false;
        var was = cause;
        cause = null;
        self._telemetry('mic_dead_input_recovered', { cause: was, dead_ms: Date.now() - deadSince });
        try { opts.onAlive && opts.onAlive(); } catch (_) {}
    }

    var timer = setInterval(function() {
        if (stopped) return;
        /* Count TIME, not ticks: a background tab runs this at 1 Hz, and at a
           fixed +checkMs per tick the 8-s grace took 32 s there — exactly where
           #1735 was (she switched tabs 6 s into the take). Capped so that a
           tick after a frozen stretch does not spend minutes at once. */
        var nowT = Date.now();
        var step = Math.min(Math.max(nowT - lastTick, 0), Math.max(checkMs, 1500));
        lastTick = nowT;
        var v = opts.sample();
        var below = !(typeof v === 'number') || !isFinite(v) || v <= floor;
        /* A context that is not rendering does not hand the tool silence - its
           analyser keeps returning the LAST buffer it filled, so the tool's
           sample stays comfortably above the floor for ever and this watchdog
           never fired for the one cause it names `paused`. Measured on
           /sound-meter 2026-09-18: 100 dBA frozen on screen for as long as the
           context stayed suspended, while the microphone was hearing 50. A
           sample taken while nothing is rendering is not a sample. */
        var cx = self._ctxs();
        var ctxPaused = !!(cx.length && !cx.some(_ctxIsRunning));
        if (ctxPaused) below = true;
        if (!below) {
            aboveRun++;
            if (aboveRun >= sustain) {
                everAlive = true;
                belowMs = 0;
                goAlive();
            }
            return;
        }
        aboveRun = 0;
        // A recorder's silence after the input has spoken is a pause, not evidence.
        if (everAlive && !levelAfterAlive && !ctxPaused) { belowMs = 0; return; }
        belowMs += step;
        /* A track that STARTS muted (the OS mute switched on before the take —
           measured: a muted Windows endpoint hands Chrome zeros with
           track.muted === true and never fires `mute`) is a fact, not a
           warm-up: no grace for it. */
        if (record && !everAlive) { var tm = track(); if (tm && tm.muted) belowMs = Math.max(belowMs, graceMs); }
        if (belowMs >= (everAlive ? deadMs : graceMs)) goDead();
        /* One dead episode can change its cause: the context was paused,
           came back, and the input turned out to be silent as well. The
           first verdict said "tap to resume"; without a second one the tool
           sat on "—" with nothing on screen explaining why (seen 2026-09-18
           when a test file ran out during the pause). */
        if (dead && !stopped) {
            var now2 = classify();
            if (now2 !== cause) {
                cause = now2;
                self._telemetry('mic_dead_input', { cause: cause, floor: floor, after_ms: everAlive ? deadMs : graceMs,
                    alive: everAlive ? 1 : 0, t_ms: Date.now() - startedAt, recause: 1 });
                try { opts.onDead && opts.onDead(cause, self.getDeadInputMessage(cause, opts.purpose)); } catch (_) {}
            }
        }
    }, checkMs);

    /* The track's own events are instant and need no sampling window at all —
       an unplug or an OS mute is a fact, not an inference. They only ever
       promote to dead; recovery still goes through the level, because `unmute`
       fires before the first sample arrives. Bound to whatever stream the
       manager holds NOW: adoptStream() rebinds them. */
    var t0 = null;
    var onEnded = function() { belowMs = Math.max(deadMs, graceMs); goDead(); };
    var onMuted = function() { belowMs = Math.max(deadMs, graceMs); goDead(); };
    function unbind() {
        if (!t0) return;
        try { t0.removeEventListener('ended', onEnded); } catch (_) {}
        try { t0.removeEventListener('mute', onMuted); } catch (_) {}
        t0 = null;
    }
    function bind() {
        unbind();
        t0 = track();
        if (!t0) return;
        try { t0.addEventListener('ended', onEnded); } catch (_) {}
        try { t0.addEventListener('mute', onMuted); } catch (_) {}
    }
    bind();

    this._deadWatch = {
        cause: function() { return cause; },
        alive: function() { return everAlive; },
        rebind: bind,
        stop: function() {
            stopped = true;
            clearInterval(timer);
            unbind();
        },
    };
    return this._deadWatch;
};

/**
 * adoptStream — the tool re-opened the SAME device itself with other
 * constraints and records from that stream now (/dictaphone re-opens with the
 * browser's processing on for drivers that hand the raw path nothing). Causes
 * and track events must follow the stream that is actually recording: without
 * this the stopped original made every later verdict read «the microphone
 * disconnected» (production 2026-09-23, a MIUI phone: a successful re-open,
 * then five `ended` verdicts on a live take). The previous stream's tracks are
 * the caller's to stop.
 */
MicManager.prototype.adoptStream = function(stream) {
    if (!stream || !stream.getAudioTracks || !stream.getAudioTracks().length) return;
    this._stream = stream;
    if (this._deadWatch && this._deadWatch.rebind) this._deadWatch.rebind();
};

/** Tear down the dead-input watchdog (also runs from stop()). */
MicManager.prototype.stopDeadInputWatch = function() {
    if (this._deadWatch) { try { this._deadWatch.stop(); } catch (_) {} this._deadWatch = null; }
};

/** Localised one-liner for a dead-input cause. Same contract as
 *  getErrorMessage(): the tool renders it wherever its own errors live. */
MicManager.prototype.getDeadInputMessage = function(cause, purpose) {
    var table = purpose === 'record' ? DEAD_REC : DEAD;
    var d = table[this._locale] || table.en;
    return d[cause] || d.silent;
};

/** Tear down the silence watchdog + its banner (also runs from stop()). */
MicManager.prototype.stopSilenceWatch = function() {
    if (this._silence) { try { this._silence.stop(); } catch (_) {} this._silence = null; }
};

function ensureSilenceStyles() {
    if (document.getElementById('mmSilenceStyle')) return;
    var s = document.createElement('style');
    s.id = 'mmSilenceStyle';
    s.textContent =
        '.mm-silence{display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin:8px 0 0;padding:8px 12px;' +
            'border-radius:8px;font-size:13.5px;font-weight:600;color:#fff;background:var(--danger,#dc2626);' +
            'animation:mmSilencePulse 1.2s ease-in-out infinite}' +
        '.mm-silence[hidden]{display:none}' +
        '.mm-silence-change{border:1px solid rgba(255,255,255,.7);background:transparent;color:#fff;' +
            'border-radius:6px;padding:3px 10px;font:inherit;font-size:12.5px;cursor:pointer}' +
        '.mm-silence-change:hover{background:rgba(255,255,255,.15)}' +
        '@keyframes mmSilencePulse{0%,100%{opacity:1}50%{opacity:.72}}';
    document.head.appendChild(s);
}

/**
 * Get current stream (null if not started).
 * @returns {MediaStream|null}
 */
MicManager.prototype.getStream = function() {
    return this._stream;
};

/**
 * Get or create AudioContext + AnalyserNode for the current stream.
 * @param {Object} [opts]
 * @param {number} [opts.fftSize=2048]
 * @param {number} [opts.smoothing=0.3]
 * @returns {{audioCtx: AudioContext, analyser: AnalyserNode, source: MediaStreamAudioSourceNode}}
 */
MicManager.prototype.getAnalyser = function(opts) {
    opts = opts || {};
    if (!this._stream) throw new Error('MicManager: call start() first');
    if (!this._audioCtx || this._audioCtx.state === 'closed') {
        this._audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (!this._analyser) {
        this._analyser = this._audioCtx.createAnalyser();
        this._analyser.fftSize = opts.fftSize || 2048;
        this._analyser.smoothingTimeConstant = opts.smoothing !== undefined ? opts.smoothing : 0.3;
        var source = this._audioCtx.createMediaStreamSource(this._stream);
        source.connect(this._analyser);
        this._source = source;
    }
    return { audioCtx: this._audioCtx, analyser: this._analyser, source: this._source };
};

/**
 * Monitor audio level (RMS). Calls cb(rms) on each animation frame.
 * @param {function} cb — callback receiving RMS value (0-1)
 */
MicManager.prototype.onLevel = function(cb) {
    this._levelCb = cb;
    if (!this._analyser) this.getAnalyser();
    var analyser = this._analyser;
    var buf = new Float32Array(analyser.fftSize);
    var self = this;

    function tick() {
        if (!self._stream || !self._levelCb) return;
        analyser.getFloatTimeDomainData(buf);
        var sum = 0;
        for (var i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
        var rms = Math.sqrt(sum / buf.length);
        self._levelCb(rms);
        self._levelRaf = requestAnimationFrame(tick);
    }
    tick();
};

/**
 * Stop level monitoring.
 */
MicManager.prototype.offLevel = function() {
    this._levelCb = null;
    if (this._levelRaf) { cancelAnimationFrame(this._levelRaf); this._levelRaf = null; }
};

/**
 * Enumerate available audio input devices.
 * @returns {Promise<Array<{deviceId:string, label:string}>>}
 */
MicManager.prototype.enumerate = async function() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return [];
    var devices = await navigator.mediaDevices.enumerateDevices();
    return devices
        .filter(function(d) { return d.kind === 'audioinput'; })
        .map(function(d, i) { return { deviceId: d.deviceId, label: d.label || ('Microphone ' + (i + 1)) }; });
};

/**
 * Listen for device changes (mic plugged/unplugged).
 * @param {function} cb — called with updated device list
 * @returns {function} unsubscribe function
 */
MicManager.prototype.onDeviceChange = function(cb) {
    if (!navigator.mediaDevices) return function() {};
    var handler = function() {
        navigator.mediaDevices.enumerateDevices().then(function(devices) {
            cb(devices.filter(function(d) { return d.kind === 'audioinput'; })
                .map(function(d, i) { return { deviceId: d.deviceId, label: d.label || ('Microphone ' + (i + 1)) }; }));
        });
    };
    /* Guarded: mediaDevices exists but is not an EventTarget on some WebKit
       builds, and the raw call throws (see __onDeviceChange in timbrica-core). */
    if (window.__onDeviceChange) return window.__onDeviceChange(handler);
    if (typeof navigator.mediaDevices.addEventListener !== 'function') return function () {};
    navigator.mediaDevices.addEventListener('devicechange', handler);
    return function() { navigator.mediaDevices.removeEventListener('devicechange', handler); };
};

/**
 * Get track settings (sampleRate, channelCount, etc).
 * @returns {Object|null}
 */
MicManager.prototype.getSettings = function() {
    if (!this._stream) return null;
    var tracks = this._stream.getAudioTracks();
    return tracks.length ? tracks[0].getSettings() : null;
};

/**
 * Destroy MicManager — stop stream, remove event listeners.
 * Call this when the tool is done (e.g. component unmount).
 */
MicManager.prototype.destroy = function() {
    this.stop();
    this.detachDevicePicker();
    var at = _instances.indexOf(this);
    if (at !== -1) _instances.splice(at, 1);
    if (this._ctxWatchTimer) { clearInterval(this._ctxWatchTimer); this._ctxWatchTimer = null; }
    if (this._ctxPauseEl && this._ctxPauseEl.parentNode) {
        this._ctxPauseEl.parentNode.removeChild(this._ctxPauseEl);
        this._ctxPauseEl = null;
    }
    window.removeEventListener('beforeunload', this._cleanupFn);
};

/* ── Device picker ──────────────────────────────────────────────────────────
   Etalon pattern for the 40 mic tools. attachDevicePicker(anchorEl) mounts a
   small pill button (🎙️ name ▾) next to the tool's mic button. The pill is
   hidden until 2+ audio inputs are detected; clicking it opens a popover with
   a radio list of mics. Selection is persisted to localStorage under one
   site-wide key so the user only has to pick once across all mic tools, and
   is auto-injected into the next start()/startRaw() constraints. */

function _mmEsc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function(c) {
        return c === '&' ? '&amp;'
             : c === '<' ? '&lt;'
             : c === '>' ? '&gt;'
             : c === '"' ? '&quot;'
             : '&#39;';
    });
}

/**
 * Attach a microphone-source picker.
 * @param {Object} opts
 * @param {HTMLElement} opts.anchorEl — picker pill is inserted as a sibling
 *   of this element. By default `position:'after'` (afterend); pass
 *   `position:'before'` to render above.
 * @param {string} [opts.position='after'] — 'before' | 'after'
 * @param {function} [opts.onChange] — fired as (deviceId, {wasActive}) when
 *   the user picks a different device. If omitted and the stream is currently
 *   live, MicManager auto-replays the last start/startRaw with the new
 *   deviceId. Tools with a custom audio graph (voice-changer etc.) should
 *   pass an onChange that tears down their graph and re-invokes their own
 *   start function so the new stream is wired in correctly.
 * @param {string} [opts.storageKey] — override the default site-wide
 *   localStorage key. Almost never needed.
 * @returns {{refresh:function, getDeviceId:function, destroy:function}|null}
 *   null if anchorEl is missing or the browser has no enumerateDevices.
 */
MicManager.prototype.attachDevicePicker = function(opts) {
    opts = opts || {};
    var anchorEl = opts.anchorEl;
    if (!anchorEl || !navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return null;
    if (this._picker) this.detachDevicePicker();

    ensurePickerStyles();
    var L = PICKER[this._locale] || PICKER.en;
    var storageKey = opts.storageKey || PICKER_STORAGE_KEY;

    // Restore prior selection (cross-tool — one preference for the whole site).
    try {
        var stored = localStorage.getItem(storageKey);
        if (stored) this._pickedDeviceId = stored;
    } catch (_) {}

    var pill = document.createElement('button');
    pill.type = 'button';
    pill.className = 'mm-pick';
    pill.setAttribute('aria-haspopup', 'listbox');
    pill.setAttribute('aria-expanded', 'false');
    pill.innerHTML =
        '<span class="mm-pick-ico" aria-hidden="true">🎙️</span>' +
        '<span class="mm-pick-lbl">' + _mmEsc(L.defaultLabel) + '</span>' +
        '<span class="mm-pick-chev" aria-hidden="true">▾</span>';
    anchorEl.insertAdjacentElement(opts.position === 'before' ? 'beforebegin' : 'afterend', pill);

    var self = this;
    this._picker = {
        pill: pill,
        lblEl: pill.querySelector('.mm-pick-lbl'),
        devices: [],
        storageKey: storageKey,
        onChange: typeof opts.onChange === 'function' ? opts.onChange : null,
        popover: null,
        outsideHandler: null,
        escHandler: null,
        repositionHandler: null,
        deviceChangeHandler: null
    };

    pill.addEventListener('click', function() { self._togglePicker(); });

    // Subscribe to plug/unplug. Re-enumerate also covers the case where the
    // OS swaps the default device (e.g. user changes default in Windows Sound
    // settings — Chrome fires devicechange).
    if (navigator.mediaDevices.addEventListener) {
        this._picker.deviceChangeHandler = function() { self._refreshPickerDevices(); };
        navigator.mediaDevices.addEventListener('devicechange', this._picker.deviceChangeHandler);
    }

    // Initial enumerate. Labels are empty before the first permission grant,
    // so the pill will render as "Microphone N" until the user clicks the
    // main mic button at least once; after that, _refreshPickerDevices is
    // called from start() success path and real labels appear.
    this._refreshPickerDevices();

    return {
        refresh: function() { return self._refreshPickerDevices(); },
        getDeviceId: function() { return self._pickedDeviceId; },
        destroy: function() { self.detachDevicePicker(); }
    };
};

/** Tear down the picker (remove DOM, unsubscribe listeners). */
MicManager.prototype.detachDevicePicker = function() {
    var p = this._picker;
    if (!p) return;
    this._closePicker();
    if (p.deviceChangeHandler && navigator.mediaDevices && navigator.mediaDevices.removeEventListener) {
        navigator.mediaDevices.removeEventListener('devicechange', p.deviceChangeHandler);
    }
    if (p.pill && p.pill.parentNode) p.pill.parentNode.removeChild(p.pill);
    this._picker = null;
};

/** Re-read enumerateDevices() into the picker cache, update visibility + label. */
MicManager.prototype._refreshPickerDevices = async function() {
    var p = this._picker;
    if (!p) return;
    try {
        var raw = await navigator.mediaDevices.enumerateDevices();
        p.devices = raw.filter(function(d) { return d.kind === 'audioinput'; })
                       .map(function(d) { return { deviceId: d.deviceId, label: d.label || '' }; });
    } catch (_) {
        p.devices = [];
    }
    // If the stored deviceId is no longer present (device unplugged or site
    // data wiped after our save), silently fall back to system default —
    // we do NOT remove it from localStorage in case the device reappears.
    this._updatePickerVisibility();
    this._updatePickerLabel();
    // Repaint open popover if it's currently visible.
    if (p.popover) { this._closePicker(); this._openPicker(); }
};

MicManager.prototype._updatePickerVisibility = function() {
    var p = this._picker;
    if (!p) return;
    if (p.devices.length >= 2) p.pill.classList.add('mm-pick-show');
    else p.pill.classList.remove('mm-pick-show');
};

MicManager.prototype._updatePickerLabel = function() {
    var p = this._picker;
    if (!p) return;
    var L = PICKER[this._locale] || PICKER.en;
    var label = L.defaultLabel;
    if (this._pickedDeviceId) {
        for (var i = 0; i < p.devices.length; i++) {
            if (p.devices[i].deviceId === this._pickedDeviceId) {
                label = p.devices[i].label || L.device_n.replace('{n}', i + 1);
                break;
            }
        }
    }
    p.lblEl.textContent = label;
    p.pill.setAttribute('aria-label', L.pill_aria.replace('{name}', label));
};

MicManager.prototype._togglePicker = function() {
    if (!this._picker) return;
    if (this._picker.popover) this._closePicker();
    else this._openPicker();
};

MicManager.prototype._openPicker = function() {
    var p = this._picker;
    if (!p || p.popover) return;
    var self = this;
    var L = PICKER[this._locale] || PICKER.en;

    var pop = document.createElement('div');
    pop.className = 'mm-pop';
    pop.setAttribute('role', 'listbox');
    pop.setAttribute('aria-label', L.title);

    var html = '<div class="mm-pop-hd">' + _mmEsc(L.title) + '</div><ul>';
    var rows = [{ id: '', label: L.defaultLabel }];
    for (var i = 0; i < p.devices.length; i++) {
        rows.push({
            id: p.devices[i].deviceId,
            label: p.devices[i].label || L.device_n.replace('{n}', i + 1)
        });
    }
    for (var j = 0; j < rows.length; j++) {
        var checked = (rows[j].id || null) === (this._pickedDeviceId || null);
        html +=
            '<li><button type="button" class="mm-pop-opt" role="option"' +
            ' data-id="' + _mmEsc(rows[j].id) + '"' +
            ' aria-checked="' + (checked ? 'true' : 'false') + '">' +
            '<span class="mm-pop-dot" aria-hidden="true"></span>' +
            '<span class="mm-pop-lbl">' + _mmEsc(rows[j].label) + '</span>' +
            '</button></li>';
    }
    html += '</ul>';
    pop.innerHTML = html;
    document.body.appendChild(pop);
    p.popover = pop;
    p.pill.setAttribute('aria-expanded', 'true');
    this._positionPicker();

    pop.addEventListener('click', function(ev) {
        var btn = ev.target.closest('.mm-pop-opt');
        if (!btn) return;
        self._selectDevice(btn.getAttribute('data-id') || '');
        self._closePicker();
    });

    p.outsideHandler = function(ev) {
        if (!pop.contains(ev.target) && !p.pill.contains(ev.target)) self._closePicker();
    };
    p.escHandler = function(ev) {
        if (ev.key === 'Escape') { self._closePicker(); p.pill.focus(); }
    };
    p.repositionHandler = function() { self._positionPicker(); };
    // Defer outside-click subscription one frame so the click that opened the
    // popover doesn't immediately close it.
    setTimeout(function() { document.addEventListener('click', p.outsideHandler); }, 0);
    document.addEventListener('keydown', p.escHandler);
    window.addEventListener('resize', p.repositionHandler);
    window.addEventListener('scroll', p.repositionHandler, true);

    var checkedBtn = pop.querySelector('.mm-pop-opt[aria-checked="true"]');
    if (checkedBtn) checkedBtn.focus();
};

MicManager.prototype._positionPicker = function() {
    var p = this._picker;
    if (!p || !p.popover) return;
    var pillR = p.pill.getBoundingClientRect();
    var popR = p.popover.getBoundingClientRect();
    var vw = window.innerWidth;
    var vh = window.innerHeight;
    var top = pillR.bottom + 6;
    var left = pillR.left + pillR.width / 2 - popR.width / 2;
    if (left < 8) left = 8;
    if (left + popR.width > vw - 8) left = vw - 8 - popR.width;
    // Flip upward if there's not enough room below but enough above.
    if (top + popR.height > vh - 8 && pillR.top > popR.height + 12) {
        top = pillR.top - popR.height - 6;
    }
    p.popover.style.top = top + 'px';
    p.popover.style.left = left + 'px';
};

MicManager.prototype._closePicker = function() {
    var p = this._picker;
    if (!p || !p.popover) return;
    if (p.outsideHandler) document.removeEventListener('click', p.outsideHandler);
    if (p.escHandler) document.removeEventListener('keydown', p.escHandler);
    if (p.repositionHandler) {
        window.removeEventListener('resize', p.repositionHandler);
        window.removeEventListener('scroll', p.repositionHandler, true);
    }
    if (p.popover.parentNode) p.popover.parentNode.removeChild(p.popover);
    p.popover = null;
    p.outsideHandler = p.escHandler = p.repositionHandler = null;
    p.pill.setAttribute('aria-expanded', 'false');
};

MicManager.prototype._selectDevice = function(deviceId) {
    var p = this._picker;
    if (!p) return;
    var newId = deviceId || null;
    var prevId = this._pickedDeviceId || null;
    this._pickedDeviceId = newId;
    try {
        if (newId) localStorage.setItem(p.storageKey, newId);
        else localStorage.removeItem(p.storageKey);
    } catch (_) {}
    this._updatePickerLabel();
    if (prevId === newId) return;

    var wasActive = !!this._stream;
    this._telemetry('mic_device_picked', { dev: newId ? 'specific' : 'default', active: wasActive ? 1 : 0 });

    if (p.onChange) {
        try { p.onChange(newId, { wasActive: wasActive }); }
        catch (e) { if (window.__handleError) window.__handleError(e, 'mic-manager:picker-onchange'); }
    } else if (wasActive && this._lastStart) {
        // Default: replay the last start/startRaw — picked device auto-injects
        // via _applyPickedDevice inside start()/startRaw().
        var ls = this._lastStart;
        try {
            var promise = this[ls.kind](ls.args);
            if (promise && typeof promise.catch === 'function') promise.catch(function(){});
        } catch (_) {}
    }
};

/* Whether the user's picked deviceId is in the current enumerate cache. The
   cache is populated by the picker; without a picker we trust the stored ID
   (caller knows what they're doing). Returning false here is what stops us
   from forcing { deviceId: { exact: 'stale-hash' } } and triggering
   OverconstrainedError when the picked device has been unplugged. */
MicManager.prototype._pickedDeviceAvailable = function() {
    if (!this._pickedDeviceId) return false;
    if (!this._picker || !this._picker.devices || !this._picker.devices.length) return true;
    for (var i = 0; i < this._picker.devices.length; i++) {
        if (this._picker.devices[i].deviceId === this._pickedDeviceId) return true;
    }
    return false;
};

/* Return a constraints object with the user's picked deviceId stamped in if
   the caller did not already specify one. Never mutates the caller's args —
   _lastStart caches the original reference so replays always re-evaluate
   _pickedDeviceId against the latest selection. */
MicManager.prototype._applyPickedDevice = function(constraints) {
    if (!this._pickedDeviceAvailable()) return constraints;
    if (!constraints || typeof constraints !== 'object') return constraints;
    var audio = constraints.audio;
    if (audio === false) return constraints;
    var out = { audio: {} };
    // Copy other top-level keys (e.g. video, peerIdentity) by reference.
    for (var k in constraints) if (k !== 'audio' && Object.prototype.hasOwnProperty.call(constraints, k)) out[k] = constraints[k];
    if (audio === true || audio == null) {
        out.audio = { deviceId: { exact: this._pickedDeviceId } };
    } else {
        for (var ak in audio) if (Object.prototype.hasOwnProperty.call(audio, ak)) out.audio[ak] = audio[ak];
        if (!out.audio.deviceId) out.audio.deviceId = { exact: this._pickedDeviceId };
    }
    return out;
};

/* ── Internal ── */

/* Sync classifier — used inline at the throw site. NotAllowedError can be
   either "permission denied" (user clicked Block) or "permission dismissed"
   (user closed the prompt without choosing). We can't tell from err.name
   alone, so we mark NotAllowedError as 'pending_perm' here and let the
   async kicker upgrade it via the Permissions API once we know. */
MicManager.prototype._classifyError = function(err) {
    if (!err) return 'generic';
    switch (err.name) {
        case 'NotAllowedError': return 'pending_perm';
        case 'NotFoundError': return 'not_found';
        case 'NotReadableError': return 'busy';
        case 'AbortError': return 'busy';
        case 'OverconstrainedError': return 'not_found';
        case 'SecurityError': return 'denied';
        // _acquireStream's watchdog throws TimeoutError when getUserMedia
        // never settles; some browsers also reject getUserMedia itself
        // with TimeoutError when a found device can't deliver frames.
        // Same user-facing situation either way.
        case 'TimeoutError': return 'unresponsive';
        // WebKit: the page's audio session type refuses capture («AudioSession
        // category is not compatible with audio capture»). ios-audio-unmute.js
        // already asked again; this is what is left. A later retry works once
        // the type it wrote has landed.
        case 'InvalidStateError': return /AudioSession/i.test(String(err.message || '')) ? 'session_mode' : 'generic';
        default: return 'generic';
    }
};

/* Render the localized error message + an inline fallback CTA (button)
   inside the tool-supplied status element. MicManager owns the entire
   visible state of statusEl — tools must NOT also do
   `statusEl.textContent = err.message` because that destroys the CTA we
   just rendered. Idempotent: re-rendering with the same code is a no-op
   for the CTA, only the message is refreshed.

   DOM shape inside statusEl after this call:
       <span class="mm-status-text">…locale error message…</span>
       <button class="mm-inline-fallback">…tool's fallback label…</button>  ← optional
*/
MicManager.prototype._renderInlineFallback = function(code) {
    var fb = this._fallback;
    if (!fb || !fb.statusEl) return;

    // Refresh / create the message text node.
    var msgs = ERRORS[this._locale] || ERRORS.en;
    var msgText = msgs[code] || msgs.generic;
    var span = fb.statusEl.querySelector('.mm-status-text');
    if (!span) {
        // Move any existing plain text into the span so we don't lose it
        // (some tools pre-populate statusEl with a hint).
        span = document.createElement('span');
        span.className = 'mm-status-text';
        var oldText = fb.statusEl.textContent;
        fb.statusEl.textContent = '';
        if (oldText && oldText.trim()) span.textContent = oldText.trim();
        fb.statusEl.appendChild(span);
    }
    span.textContent = msgText;

    // CTA — append once, re-use across subsequent failures of the same instance.
    if (fb.statusEl.querySelector('.mm-inline-fallback')) return;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'mm-inline-fallback';
    btn.style.cssText = 'display:block;margin:10px auto 0;padding:8px 14px;border:1px solid var(--accent,#38bdf8);background:rgba(56,189,248,.12);color:var(--accent,#38bdf8);border-radius:8px;font-size:13px;font-weight:600;cursor:pointer';
    btn.textContent = fb.label;
    var self = this;
    btn.onclick = function(ev) {
        ev.preventDefault();
        ev.stopPropagation();
        self._telemetry('mic_fallback_action_clicked', { br: detectBrowser(), code: code, src: 'inline' });
        try { fb.handler(); } catch (e) {
            if (window.__handleError) window.__handleError(e, 'mic-manager:fallback');
        }
    };
    fb.statusEl.appendChild(btn);
    self._telemetry('mic_fallback_action_shown', { br: detectBrowser(), code: code, src: 'inline' });
};
MicManager.prototype._clearInlineFallback = function() {
    var fb = this._fallback;
    if (!fb || !fb.statusEl) return;
    var existing = fb.statusEl.querySelector('.mm-inline-fallback');
    if (existing) existing.remove();
    // Don't wipe .mm-status-text — successful start is signaled by tools
    // setting their own "Listening…" text into statusEl directly.
};

/* Async refinement: ask the Permissions API for ground truth.
   - state='denied'  → user clicked Block; persistent denial.
   - state='prompt'  → not decided. Could be a genuine dismissal OR an
                       environment that blocks mic at policy level (see below).
   - state='granted' → impossible after a NotAllowedError, but treat as denied
                       for safety (something else went wrong).
   Browsers without Permissions API for 'microphone' (Safari < 16, some
   embedded browsers) fall back to 'denied' — same behaviour as before, no
   regression. We deliberately don't read err.message because that's
   localized in some browsers (Yandex Browser RU writes "Запрос отклонён",
   Chrome ZH-CN writes "权限被拒绝", etc.) and a regex misses them all.

   Ground-truth timing probe (etalon, same principle as the vibration-test
   DeviceMotion probe and the document-reader speech-engine-probe — measure
   the real outcome, do not trust the API's verbal classification):
   a 'prompt'-state rejection has two very different causes that the
   Permissions API reports identically —
     (a) the user saw the native prompt and dismissed it, or
     (b) the environment auto-blocked mic and the prompt never appeared
         (AmazonKidsBrowser, managed/MDM devices, in-app webviews).
   They are told apart by LATENCY. A human who sees a prompt, decides, and
   taps cannot do it in under ~500 ms even when expecting it; an environment
   auto-block rejects in tens of ms. A sub-500 ms 'prompt' rejection is
   therefore case (b) — classify as 'blocked_env' so the user gets honest
   "switch browsers" guidance instead of the wrong "click the lock icon"
   instructions (kids browsers have no lock icon). Bug 2026-05-15 sid
   658383ae: Fire HD Kids tablet, 3× dismissed, gave up → feedback.

   state='granted' + NotAllowedError is NOT a contradiction: it is the
   signature of a SYSTEM-level block — the site permission is fine but the OS
   (Windows Settings → Privacy → Microphone, macOS System Settings → Privacy)
   or an antivirus mic-shield denies the app. Telling that user to "allow in
   browser settings" is wrong advice — the browser already allows it. We
   classify it 'os_blocked' so the recovery modal points at the OS toggle
   instead. Bug 2026-05-17 sid 236a70f6: Windows / Edge user, "ошибка б нет
   доступа" — got the browser-settings advice for an OS-level block. */
/**
 * Отличить «среда запретила» от «человек когда-то запретил, и браузер помнит».
 *
 * ЗАМЕР 2026-08-06 (30 дней, RU): `mic_blocked_env` — 759 сессий, и из них
 * выбираются лишь 11.5 %, вдвое-вчетверо хуже любого другого отказа. Причина
 * не в среде: 402 из 759 несут текст «NotAllowedError: Permission denied», а
 * не «…not allowed by the user agent or the platform in the current context»
 * (371). То есть это обычный ЗАПОМНЕННЫЙ отказ.
 *
 * Прежняя эвристика смотрела ТОЛЬКО на время: отказ быстрее 500 мс при
 * состоянии `prompt` = среда. Но запомненный отказ отвергается так же мгновенно
 * и так же оставляет состояние `prompt` в части браузеров. В итоге 402 человека
 * читали «этот браузер блокирует микрофон, откройте страницу в Chrome» — сидя
 * в Chrome. Совет, который невозможно выполнить, хуже отсутствия совета: он
 * уводит от единственного работающего действия (разрешить в настройках сайта).
 *
 * Различаем по тексту самой ошибки — он у этих случаев РАЗНЫЙ и не зависит от
 * языка интерфейса (Chrome и Firefox шлют англоязычные строки всегда).
 * Время остаётся вторым признаком: без совпадения по тексту быстрый отказ — это
 * всё-таки скорее закрытый диалог, а не запрет платформы.
 */
var PLATFORM_BLOCK_RE = /not allowed by the user agent|platform in the current context|permissions policy|not supported in this context/i;

/* Детские/управляемые браузеры, где микрофон запрещён политикой ПРОФИЛЯ, а не
   пользователем: ни промпта, ни замочка, ни настройки у родителя — и другого
   браузера внутри профиля нет (Silk в детском профиле Fire OS отсутствует,
   Chrome на Fire OS не существует). Официальная позиция Amazon по камере в
   Kids Browser: заблокировано «even with parental permission»; для микрофона
   то же по всем родительским тредам 2021-2025. Единственный реальный выход —
   родитель открывает страницу в Silk из ВЗРОСЛОГО профиля, поэтому такой UA
   получает отдельный текст be_kids_body, а не общий «откройте в Chrome»
   (Chrome тут — мёртвый совет). Кейс: фидбек 2026-07-31, Fire HD 8 KFONWI,
   «It won't let me use the Mic like at all I've tried so many things» —
   generic-текст ошибки уводил его в ветку denied с советом про замочек,
   которого в этом браузере нет. Идентичность браузера — прямо в UA, поэтому
   классифицируем детерминированно, без текстовых/временных эвристик. */
var KIDS_BROWSER_RE = /AmazonKidsBrowser/i;

MicManager.prototype._looksLikePlatformBlock = function(err) {
    var msg = (err && (err.message || err.name)) ? String(err.message || err.name) : '';
    if (PLATFORM_BLOCK_RE.test(msg)) return true;
    // Встроенные браузеры соцсетей и мессенджеров getUserMedia не отдают вовсе.
    // Здесь это ПРАВДА «откройте в обычном браузере» — единственный случай,
    // когда прежний совет был уместен.
    var ua = navigator.userAgent || '';
    if (KIDS_BROWSER_RE.test(ua)) return true;
    return /FBAN|FBAV|Instagram|VKClient|VKAndroidApp|OKApp|Telegram|Line\/|MicroMessenger/i.test(ua);
};

MicManager.prototype._refinePermissionError = async function(elapsedMs, originalErr) {
    /* Remembered for _promptStillPossible(): the modal decides whether a
       retry can be honest from what the browser said HERE, not from its name. */
    this._lastRejectMs = (typeof elapsedMs === 'number') ? elapsedMs : null;
    this._lastRejectState = 'unknown';
    try {
        if (!navigator.permissions || !navigator.permissions.query) {
            return this._looksLikePlatformBlock(originalErr) ? 'blocked_env' : 'denied';
        }
        var result = await navigator.permissions.query({ name: 'microphone' });
        this._lastRejectState = result.state;
        if (result.state === 'prompt') {
            if (this._looksLikePlatformBlock(originalErr)) return 'blocked_env';
            /* Мгновенный отказ без признаков платформы — это запомненный отказ:
               диалог не показывали, потому что ответ уже известен. Совет тот же,
               что и при обычном «запрещено»: разрешить в настройках сайта. */
            if (typeof elapsedMs === 'number' && elapsedMs >= 0 && elapsedMs < 500) return 'denied';
            return 'dismissed';
        }
        // Site permission granted but getUserMedia still threw → OS / system block.
        if (result.state === 'granted') return 'os_blocked';
        return 'denied';
    } catch (_) {
        // Safari < 16 throws on { name: 'microphone' } — treat as denied.
        return 'denied';
    }
};

/** Build MicError, log telemetry (deduped), optionally show recovery modal. */
MicManager.prototype._fail = function(code, originalErr) {
    var locale = this._locale;
    var msgs = ERRORS[locale] || ERRORS.en;
    var err = new Error(msgs[code] || msgs.generic);
    err.code = code;
    err.originalError = originalErr || null;
    // User-condition codes (denied/dismissed/not_found/busy) are already
    // surfaced via recovery modal + inline CTA + tool_blocked telemetry.
    // Tag as expected so unhandledrejection guard + Sentry beforeSend drop
    // them when a caller forgets to .catch(). TIMBRICA-FRONTEND-56.
    if (code === 'denied' || code === 'dismissed' || code === 'not_found' || code === 'busy' || code === 'blocked_env' || code === 'unresponsive' || code === 'os_blocked') {
        err.expected = true;
    }

    // Consecutive-dismissal tracking. A genuine "user closed the prompt"
    // happens once — the next click resolves to grant or deny. Repeated
    // 'dismissed' means the native prompt never reaches the user: kids-mode
    // browsers (AmazonKidsBrowser) and some embedded webviews block the mic
    // at the parental-control / policy level and silently leave the
    // permission stuck in the 'prompt' state forever. Bug 2026-05-15 sid
    // 658383ae: Fire HD Kids tablet — 3× dismissed, 3× silent "Start
    // Recording" clicks, user gave up and filed "there's no mic button".
    // Escalate on the 2nd dismissal so the user sees the recovery modal
    // instead of an invisible status-text loop.
    if (code === 'dismissed') this._dismissCount = (this._dismissCount || 0) + 1;
    else this._dismissCount = 0;
    var dismissStuck = (code === 'dismissed' && this._dismissCount >= 2);

    // Dedup: one tool_error per (slug, code) per session — was ~6.6 duplicate
    // events per session from users re-clicking Record after denial.
    var key = (this._slug || 'unknown') + ':' + code;
    var last = _errDedup[key] || 0;
    var now = Date.now();
    if (!last) {
        _errDedup[key] = now;
        // User-action codes (denied, dismissed, not_found, busy) are not bugs.
        // Route to tool_blocked so tool_error only counts real bugs.
        // 'unresponsive' is environmental (a wedged OS audio service / a
        // conflicting app), not a bug in our code — route it to
        // tool_blocked alongside the other non-bug conditions so
        // tool_error stays a clean bug signal.
        var isBlocked = (code === 'denied' || code === 'dismissed' || code === 'not_found' || code === 'busy' || code === 'blocked_env' || code === 'unresponsive' || code === 'os_blocked');
        try {
            var payload = {
                error_type: 'mic_' + code,
                msg: (originalErr && (originalErr.name + ': ' + (originalErr.message || '')) || '').substring(0, 200)
            };
            // iPhone session refusal: which road the request took (29.10). The
            // module's own `ios_capture_refused` never reached the field while
            // this row did, so the refusal carries the trail itself —
            // ios-audio-unmute.js request(), docs/why/primitives/live-audio-ctx.md.
            if (code === 'session_mode') {
                try {
                    var _u = window.__iosAudioUnmute, _md = navigator.mediaDevices;
                    var _t = (_u && _u.trail) ? _u.trail() : null;
                    payload.um = !_u ? 'none' : (_u.request ? (_u.trail ? 'v4' : 'v3') : 'old');
                    payload.gw = (_md && _md.getUserMedia && _md.getUserMedia.__tmbNet) ? 1 : 0;
                    payload.st = (navigator.audioSession && navigator.audioSession.type) || '';
                    payload.byp = window.__tmbGumBypass || 0;
                    payload.mds = (_md && window.__tmbGumMd) ? (_md === window.__tmbGumMd ? 1 : 0) : -1;
                    payload.gsrc = (_md && _md.getUserMedia) ? String(_md.getUserMedia).replace(/\s+/g, ' ').slice(0, 70) : '';
                    if (_t) { payload.tr = _t.tr; payload.tr_age = _t.age; }
                } catch (_) {}
            }
            if (isBlocked) {
                payload.reason = 'mic_' + code;
                if (window.__aeBlocked) window.__aeBlocked(payload);
                else if (window.__ae) window.__ae('tool_blocked', Object.assign({ slug: this._slug || 'unknown' }, payload));
            } else if (window.__aeToolError) {
                window.__aeToolError(payload);
            }
        } catch (_) {}
    }

    // Show recovery modal for terminal states (denied / not_found / busy),
    // for an environment block (blocked_env — the timing probe proved the
    // prompt never reached the user), and for a stuck-dismissal loop
    // (dismissStuck, see _dismissCount above — the slow-rejection fallback
    // for cases the timing probe missed).
    // A SINGLE 'dismissed' still shows nothing — the next click re-prompts
    // cleanly, and the scary "open browser settings" modal would train
    // dismiss-then-give-up (Yandex Browser session 1ecb409a). But a 2nd
    // dismissal means the prompt is not reaching the user — they need the
    // help modal to find a way forward.
    if (this._autoHelp && (code === 'denied' || code === 'not_found' || code === 'busy' || code === 'blocked_env' || code === 'unresponsive' || code === 'os_blocked' || code === 'generic' || code === 'session_mode' || dismissStuck)) {
        var withinSpamGuard = Date.now() - _modalState.closedAt < MODAL_SPAM_GUARD_MS;
        if (!_modalState.open && !withinSpamGuard) {
            // blocked_env → its own honest modal; dismissStuck → 'denied'
            // variant (browser-specific unblock instructions + retry).
            this.showRecoveryModal(code === 'blocked_env' ? 'blocked_env' : (dismissStuck ? 'denied' : code));
        }
    }

    // Inline fallback CTA — render under the tool's mic-status element on ANY
    // failure. Survives modal close so the user always sees a path forward,
    // even if they dismiss the modal in 1 second like real-RU-user-1ecb409a.
    if (code === 'denied' || code === 'dismissed' || code === 'not_found' || code === 'busy' || code === 'blocked_env' || code === 'unresponsive' || code === 'os_blocked' || code === 'generic' || code === 'session_mode') {
        // 'generic' added 2026-06-02: any unknown err.name (Yandex/Edge quirks,
        // antivirus mic-shield mediation, MDM webviews, broken USB drivers) used
        // to dead-end with no UI — empty vcMicStatus, no modal, no inline CTA.
        // Now the user gets the same localised text + "Or upload a file" CTA
        // as the seven enumerated failure codes.
        this._renderInlineFallback(code);
    }

    // Once the user denies access, hook the Permissions API so a later "Allow"
    // through the address-bar/lock icon resumes the last start() automatically —
    // browsers don't expose any way to re-trigger their native prompt from JS.
    if (code === 'denied') this._watchPermissionGrant();

    return err;
};

/**
 * Listen for `microphone` permission state changes. When the state flips to
 * 'granted' (user clicked the address-bar lock → Allow), auto-replay the last
 * start/startRaw call so the tool resumes without another button press.
 * Idempotent — only attaches the listener once per MicManager instance.
 */
MicManager.prototype._watchPermissionGrant = function() {
    if (this._permWatched) return;
    var self = this;
    try {
        if (!navigator.permissions || !navigator.permissions.query) return;
        navigator.permissions.query({ name: 'microphone' }).then(function(result) {
            self._permWatched = true;
            result.onchange = function() {
                if (result.state !== 'granted') return;
                // Prefer the tool-supplied onRetry: replaying _lastStart re-runs
                // only startRaw()/start() — it re-acquires the MediaStream but
                // NOT the tool's own setup chain (AudioContext, analysers,
                // visualisers). A tool that built its pipeline inside a
                // `.then()` after start() would get a live stream that nothing
                // consumes → permission granted, tool visibly dead. onRetry
                // re-enters the tool's real start path. (mic-test bug
                // 2026-05-17 sid 236a70f6.)
                if (typeof self._onRetry === 'function') {
                    try { self._onRetry(); } catch (_) {}
                    return;
                }
                if (!self._lastStart) return;
                var ls = self._lastStart;
                var p;
                try { p = self[ls.kind](ls.args); } catch (_) { return; }
                if (p && typeof p.catch === 'function') p.catch(function(){});
            };
        }).catch(function() { /* unsupported (Safari <16, etc.) — silent fallback */ });
    } catch (_) {}
};

MicManager.prototype._telemetry = function(action, extra) {
    try {
        if (window.__aeFeature) window.__aeFeature(action, extra || {});
        else if (window.__ae) window.__ae('feature_use', Object.assign({ slug: this._slug, action: action }, extra || {}));
    } catch (_) {}
};

/**
 * Get localized error message by code.
 * @param {string} code — 'denied', 'not_found', 'busy', 'not_supported', 'generic'
 * @returns {string}
 */
MicManager.prototype.getErrorMessage = function(code) {
    var msgs = ERRORS[this._locale] || ERRORS.en;
    return msgs[code] || msgs.generic;
};

// Export
window.MicManager = MicManager;

})();
